import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import FooterNav from "../components/FooterNav";
import {
    ArrowLeft,
    Archive,
    PhoneCall,
    VideoIcon,
    MoreVertical,
    Send,
    Paperclip,
    Smile,
    Trash2,
    BellOff,
    Pin,
    Ban,
    X,
    Unlock,
    Check,
    MessageCircle,
    Mic,
    Lock,
    UnlockIcon,
    Heart,
    Download,
    EyeOff,
    FileText,
    Search,
    ShieldCheck,
    Plus,
    Loader,
    Share2,
    UsersRound,
    Inbox,
    Crown,
    UserPlus,
    UserMinus,
    Shield,
    Pencil,
    Save,
    LogOut,
    UserCog,
    Info,
    Copy,
    RotateCcw,
    Skull,
} from "lucide-react";
import {
    fetchConversations,
    fetchMessages,
    sendMessage as sendMessageApi,
    createConversation,
    createGroupConversation,
    fetchMessageRequests,
    fetchGroupInvites,
    respondMessageRequest,
    markConversationRead,
    setMessageReaction,
    createSafetyReport,
    editMessage as editMessageApi,
    deleteMessage as deleteMessageApi,
    searchUsers,
    deleteConversation as deleteConversationApi,
    archiveConversation as archiveConversationApi,
    recycleConversation as recycleConversationApi,
    restoreConversation as restoreConversationApi,
    oblivionConversation as oblivionConversationApi,
    muteConversation as muteConversationApi,
    pinConversation as pinConversationApi,
    blockConversation as blockConversationApi,
    encryptConversation as encryptConversationApi,
    fetchBlockedConversations,
    storeIncognitoClear,
    fetchIncognitoPending,
    fetchGroupDetails,
    updateGroupDetails,
    addGroupMembers,
    removeGroupMember as removeGroupMemberApi,
    updateGroupMemberRole,
    transferGroupOwner,
    leaveGroupConversation,
    updateGroupPermissions,
    regenerateGroupInviteCode,
    joinGroupByCode,
    respondGroupInvite,
    reviewGroupJoinRequest,
    restrictGroupMember,
    updateGroupBan,
    updateGroupAuthority,
    type ConversationItem,
    type GroupDetails,
    type GroupMember,
    type SearchUserResult,
} from "../api/messages";
import { connectSocket, disconnectSocket, getSocket } from "../api/socket";
import { getProfile, toggleBlock as toggleProfileBlock } from "../api/profile";
import ChatCell from "./messages/ChatCell";
import SupportInbox from "../components/SupportInbox";
import { ChatPresenceBadge } from "./messages/Presence";
import type { Blocked, Chat, ChatMessageItem, FeatureKey, FriendRequest, OnlineUser, PresenceMap } from "./messages/types";
import {
    addToIconOrder,
    getIconOrder,
    presenceFromStatus,
    removeFromIconOrder,
    restorePreBlockState,
    savePreBlockState,
} from "./messages/utils";
import { formatBlackspaceFullDateTime } from "../utils/time";

const NOTIFICATIONS_UPDATED_EVENT = "blackspace:notifications-updated";
const MESSAGE_REQUESTS_UPDATED_EVENT = "blackspace:message-requests-updated";
const ENABLE_CALLS = false;
const ENABLE_VIDEO_CALLS = false;
const ENABLE_E2E = false;
const ENABLE_ATTACHMENTS = false;
const ENABLE_VOICE_NOTES = false;

function publishNotificationUpdate(unreadCount?: number) {
    if (typeof unreadCount !== "number") return;
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, { detail: { unreadCount } }));
}

function publishMessageRequestUpdate() {
    window.dispatchEvent(new CustomEvent(MESSAGE_REQUESTS_UPDATED_EVENT));
}

const initialChats: Chat[] = [];

const initialOnline: OnlineUser[] = [];

const initialRequests: FriendRequest[] = [];

const initialBlocked: Blocked[] = [];

const REACTION_OPTIONS = [
    { id: "heart", label: "Love", value: "\u2764\uFE0F" },
    { id: "laugh", label: "Laugh", value: "\u{1F602}" },
    { id: "wow", label: "Wow", value: "\u{1F62E}" },
    { id: "sad", label: "Sad", value: "\u{1F622}" },
    { id: "fire", label: "Fire", value: "\u{1F525}" },
] as const;

function parseSharedPostText(text: string) {
    const lines = String(text || "").split(/\r?\n/);
    const markerIndex = lines.findIndex((line) => line.trim() === "Shared a Blackspace post");
    if (markerIndex < 0) return null;

    const referenceIndex = lines.findIndex((line) => line.startsWith("Post reference:"));
    const meta = lines[markerIndex + 1] || "Blackspace post";
    const note = lines.slice(0, markerIndex).join("\n").trim();
    const bodyStart = markerIndex + 2;
    const bodyEnd = referenceIndex >= 0 ? referenceIndex : lines.length;
    const body = lines
        .slice(bodyStart, bodyEnd)
        .join("\n")
        .trim();
    const href = referenceIndex >= 0 ? lines[referenceIndex].replace("Post reference:", "").trim() : "";

    return { note, meta, body, href };
}

function SharedPostMessage({ text }: { text: string }) {
    const shared = parseSharedPostText(text);
    if (!shared) return <div className="whitespace-pre-wrap break-words">{text}</div>;

    return (
        <div className="min-w-[15rem] max-w-sm">
            {shared.note && <p className="mb-3 whitespace-pre-wrap break-words text-sm leading-6">{shared.note}</p>}
            <div className="overflow-hidden rounded-lg border border-cyan-300/25 bg-black/25 shadow-[0_0_24px_rgba(34,211,238,0.10)]">
                <div className="flex items-center gap-2 border-b border-cyan-300/15 bg-cyan-300/10 px-3 py-2">
                    <Share2 size={14} className="text-cyan-200" />
                    <span className="text-xs font-black uppercase tracking-[0.16em] text-cyan-100">Shared Post</span>
                </div>
                <div className="p-3">
                    <p className="text-xs font-semibold text-neutral-300">{shared.meta}</p>
                    {shared.body && <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-white/95">{shared.body}</p>}
                    {shared.href && (
                        <a
                            href={shared.href}
                            className="mt-3 inline-flex rounded-md border border-cyan-300/30 bg-cyan-300/10 px-3 py-1.5 text-xs font-black text-cyan-100 transition hover:border-cyan-200/60 hover:bg-cyan-300/15"
                        >
                            Open reference
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

const EMOJI_OPTIONS = [
    "\u{1F600}", "\u{1F602}", "\u{1F60D}", "\u{1F914}", "\u{1F44D}", "\u{1F64C}",
    "\u2764\uFE0F", "\u{1F525}", "\u2728", "\u{1F44F}", "\u{1F622}", "\u{1F62E}",
    "\u{1F680}", "\u{1F4AF}", "\u{1F389}", "\u{1F60E}", "\u{1F642}", "\u{1F917}",
];

const SEND_ACK_TIMEOUT_MS = 8000;
const MESSAGE_PAGE_SIZE = 50;
const MESSAGE_SNAPSHOT_KEY = "blackspace.messages.snapshot.v1";
const MESSAGE_SNAPSHOT_TTL_MS = 15 * 60 * 1000;

type SocketMessagePayload = {
    id: string;
    clientMessageId?: string;
    conversationId: string;
    sender: { id: string; username: string; displayName: string; avatarUrl?: string };
    content: string;
    createdAt: string;
    editedAt?: string | null;
    deletedAt?: string | null;
    isIncognito?: boolean;
    replyTo?: {
        id: string | number;
        senderName: string;
        content: string;
        unavailable?: boolean;
    } | null;
    forwarded?: boolean;
    reactions?: { user: string; reaction: string; reactedAt?: string }[];
    readBy?: { user: string; readAt: string }[];
};

type SocketSendAck = {
    success: boolean;
    message?: SocketMessagePayload;
    rateLimited?: boolean;
    error?: string;
    clientMessageId?: string;
    requestStatus?: "accepted" | "draft" | "pending" | "declined";
    requestedBy?: string | null;
};

type ReactionId = (typeof REACTION_OPTIONS)[number]["id"];
type InboxFilter = "all" | "active" | "unread" | "pinned" | "private" | "archive" | "recycle";
type MessageSnapshot = {
    chats: Chat[];
    requests: FriendRequest[];
    blocked: Blocked[];
    activeTab: "online" | "chats" | "requests" | "blocked";
    inboxFilter: InboxFilter;
    searchQuery: string;
    selectedChatId: string | number | null;
    messagePageByChat: Record<string, number>;
    messageHasMoreByChat: Record<string, boolean>;
    savedAt: number;
};
type ForwardTarget = {
    kind: "chat" | "user";
    id: string;
    chatId?: string | number;
    name: string;
    avatar: string;
    isGroup?: boolean;
    disabledReason?: string;
};
type MessageActionMenuPosition = {
    top: number;
    left: number;
    placement: "above" | "below";
};

type ReactionPalettePosition = MessageActionMenuPosition;

function readMessageSnapshot(): MessageSnapshot | null {
    try {
        const raw = sessionStorage.getItem(MESSAGE_SNAPSHOT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as MessageSnapshot;
        if (!Array.isArray(parsed.chats)) return null;
        if (Date.now() - Number(parsed.savedAt || 0) > MESSAGE_SNAPSHOT_TTL_MS) {
            sessionStorage.removeItem(MESSAGE_SNAPSHOT_KEY);
            return null;
        }
        return snapshotWithoutIncognito(parsed);
    } catch {
        return null;
    }
}

function chatsWithoutIncognito(chats: Chat[]): Chat[] {
    return chats.map((chat) => {
        const incognitoIds = new Set(
            chat.messages.filter((message) => message.isIncognito).map((message) => String(message.id))
        );
        if (!incognitoIds.size) return chat;
        return {
            ...chat,
            messages: chat.messages
                .filter((message) => !message.isIncognito)
                .map((message) => message.replyTo && incognitoIds.has(String(message.replyTo.id))
                    ? { ...message, replyTo: { ...message.replyTo, senderName: "", content: "", unavailable: true } }
                    : message),
            pinnedMessages: chat.pinnedMessages.filter((messageId) => !incognitoIds.has(String(messageId))),
        };
    });
}

function snapshotWithoutIncognito(snapshot: MessageSnapshot): MessageSnapshot {
    return { ...snapshot, chats: chatsWithoutIncognito(snapshot.chats) };
}

function writeMessageSnapshot(snapshot: MessageSnapshot) {
    try {
        sessionStorage.setItem(MESSAGE_SNAPSHOT_KEY, JSON.stringify(snapshotWithoutIncognito(snapshot)));
    } catch {
        // The snapshot is a UX optimization; messaging should keep working without it.
    }
}

export default function Message() {
    const location = useLocation();
    const locationState = location.state as { conversationId?: string; notificationType?: string } | null;
    const requestedConversationId = locationState?.conversationId;
    const requestedNotificationType = locationState?.notificationType;
    const requestedSupportCaseId = new URLSearchParams(location.search).get("support");
    const requestedBlockedAccounts = new URLSearchParams(location.search).get("tab") === "blocked";
    const restoredSnapshotRef = useRef<MessageSnapshot | null>(readMessageSnapshot());
    const restoredSnapshot = restoredSnapshotRef.current;
    const currentUser = (() => {
        try {
            const raw = localStorage.getItem("user");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    })();
    const [currentUserIsPresence, setCurrentUserIsPresence] = useState(() => Boolean(
        currentUser?.isPresence || currentUser?.realm?.isPresence || currentUser?.realmRole === "presence"
    ));

    // Tabs
    const [activeTab, setActiveTab] = useState<"online" | "chats" | "requests" | "blocked">(() => requestedBlockedAccounts ? "blocked" : restoredSnapshot?.activeTab || "chats");
    const [inboxFilter, setInboxFilter] = useState<InboxFilter>(() => restoredSnapshot?.inboxFilter || "all");
    useEffect(() => {
        if (requestedSupportCaseId) { setActiveTab("chats"); setInboxFilter("all"); }
    }, [requestedSupportCaseId]);
    const [supportCounts, setSupportCounts] = useState({ all: 0, unread: 0 });
    const updateSupportCounts = useCallback((counts: { all: number; unread: number }) => setSupportCounts(counts), []);
    const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set());
    const [bulkDeleteChats, setBulkDeleteChats] = useState<Chat[]>([]);
    const [chats, setChats] = useState<Chat[]>(() => restoredSnapshot?.chats || initialChats);
    const chatsRef = useRef<Chat[]>(restoredSnapshot?.chats || initialChats);
    const [lastSeenTick, setLastSeenTick] = useState(0);
    const presenceAllowedUserIdsRef = useRef<Set<string>>(new Set());
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>(initialOnline);

    // ✅ New States
    const [typingUsers, setTypingUsers] = useState<Set<string | number>>(new Set());
    const [messageReactions, setMessageReactions] = useState<Record<string, ReactionId>>({});
    const [searchInChat, setSearchInChat] = useState("");
    const [isRecording, setIsRecording] = useState(false);
    const [incognitoChatIds, setIncognitoChatIds] = useState<Set<string | number>>(() => {
        try {
            const saved = localStorage.getItem('incognitoChatIds');
            return saved ? new Set(JSON.parse(saved)) : new Set();
        } catch { return new Set(); }
    });
    const [globalIncognitoEnabled, setGlobalIncognitoEnabled] = useState(false);
    const [vanishingMessages, setVanishingMessages] = useState<Set<string | number>>(new Set());
    const [showExportMenu, setShowExportMenu] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selfDestructTimer, setSelfDestructTimer] = useState(0);

    const [requests, setRequests] = useState<FriendRequest[]>(() => restoredSnapshot?.requests || initialRequests);
    const requestConversationByIdRef = useRef<Map<string, ConversationItem>>(new Map());
    const [blocked, setBlocked] = useState<Blocked[]>(() => restoredSnapshot?.blocked || initialBlocked);

    // Persist incognito chat IDs to localStorage
    useEffect(() => {
        const arr: (string | number)[] = [];
        incognitoChatIds.forEach(id => arr.push(id));
        localStorage.setItem('incognitoChatIds', JSON.stringify(arr));
    }, [incognitoChatIds]);

    const isChatIncognito = (chatId: string | number) =>
        globalIncognitoEnabled || incognitoChatIds.has(chatId);

    // Sync global incognito from Settings page without overwriting per-chat choices.
    useEffect(() => {
        const checkGlobal = () => {
            const flag = localStorage.getItem('globalIncognito');
            if (!flag) return;

            if (flag === 'true') {
                setGlobalIncognitoEnabled(true);
            } else if (flag === 'false') {
                setGlobalIncognitoEnabled(false);
            }
            localStorage.removeItem('globalIncognito');
        };
        checkGlobal();
        // Listen for cross-tab changes from Settings page
        const onStorage = (e: StorageEvent) => {
            if (e.key === 'globalIncognito') checkGlobal();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // ✅ ADDED: Global search state
    const [searchQuery, setSearchQuery] = useState(() => requestedBlockedAccounts ? "" : restoredSnapshot?.searchQuery || "");
    useEffect(() => {
        if (!requestedBlockedAccounts) return;
        setActiveTab("blocked");
        setSearchQuery("");
    }, [location.key, requestedBlockedAccounts]);

    // Presence
    const [presenceMap, setPresenceMap] = useState<PresenceMap>(() => {
        const map: PresenceMap = {};
        for (const c of (restoredSnapshot?.chats || initialChats)) map[c.id] = presenceFromStatus(c.status);
        for (const u of initialOnline)
            map[u.id] = { online: true, lastSeen: Date.now() };
        return map;
    });

    // Chat state
    const [selectedChatId, setSelectedChatId] = useState<string | number | null>(() => requestedConversationId || restoredSnapshot?.selectedChatId || null);
    const selectedChatIdRef = useRef<string | number | null>(null);
    const liveChatSignalAllowedRef = useRef<Set<string>>(new Set());
    const confirmedClientMessageIdsRef = useRef<Set<string>>(new Set());
    useEffect(() => { selectedChatIdRef.current = selectedChatId; }, [selectedChatId]);
    useEffect(() => { chatsRef.current = chats; }, [chats]);
    const isCurrentChatIncognito = selectedChatId ? isChatIncognito(selectedChatId) : false;
    const acknowledgeConversationRead = (conversationId: string | number) => {
        markConversationRead(String(conversationId))
            .then((result) => publishNotificationUpdate(result.unreadCount))
            .catch(() => {});
    };
    const makeClientMessageId = () => {
        if (window.crypto?.randomUUID) return window.crypto.randomUUID();
        return `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };
    const canUseLiveChatSignals = (chat: Chat | null) =>
        Boolean(chat && !chat.isBlocked && (chat.isGroup || (chat.requestStatus || "accepted") === "accepted"));

    const closeMessageActionMenu = useCallback(() => {
        setOpenMessageMenuId(null);
        setMessageActionMenuPosition(null);
    }, []);

    const closeMessageOverlays = useCallback(() => {
        if (reactionPressTimerRef.current) {
            window.clearTimeout(reactionPressTimerRef.current);
            reactionPressTimerRef.current = null;
        }
        closeMessageActionMenu();
        setOpenReactionMessageId(null);
        setReactionPalettePosition(null);
        setActiveMessageControlsId(null);
    }, [closeMessageActionMenu]);

    const getReactionPalettePosition = (
        rect: DOMRect,
        align: "left" | "right"
    ): ReactionPalettePosition => {
        const margin = 6;
        const gap = 3;
        const paletteWidth = Math.min(188, Math.max(158, window.innerWidth - (margin * 2)));
        const paletteHeight = 36;
        const viewport = window.visualViewport;
        const viewportLeft = viewport?.offsetLeft ?? 0;
        const viewportTop = viewport?.offsetTop ?? 0;
        const viewportWidth = viewport?.width ?? window.innerWidth ?? document.documentElement.clientWidth;
        const viewportHeight = viewport?.height ?? window.innerHeight ?? document.documentElement.clientHeight;
        const minLeft = viewportLeft + margin;
        const maxLeft = Math.max(minLeft, viewportLeft + viewportWidth - paletteWidth - margin);
        const minTop = viewportTop + margin;
        const maxTop = Math.max(minTop, viewportTop + viewportHeight - paletteHeight - margin);
        const preferredLeft = align === "right" ? rect.right - paletteWidth : rect.left;
        const left = Math.min(maxLeft, Math.max(minLeft, preferredLeft));
        const hasSpaceAbove = rect.top - gap - paletteHeight >= minTop;
        const hasSpaceBelow = rect.bottom + gap + paletteHeight <= viewportTop + viewportHeight - margin;
        const placement: ReactionPalettePosition["placement"] = hasSpaceAbove || !hasSpaceBelow ? "above" : "below";
        const preferredTop = placement === "above"
            ? rect.top - paletteHeight - gap
            : rect.bottom + gap;
        const top = Math.min(maxTop, Math.max(minTop, preferredTop));

        return { top, left, placement };
    };

    const openMessageActionMenuFromRect = (
        rect: DOMRect,
        messageId: string | number,
        align: "left" | "right",
        canManage: boolean
    ) => {
        const menuWidth = 168;
        const menuHeight = canManage ? 292 : 216;
        const margin = 8;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const hasSpaceBelow = rect.bottom + margin + menuHeight <= viewportHeight - margin;
        const placement: MessageActionMenuPosition["placement"] = hasSpaceBelow ? "below" : "above";
        const preferredLeft = align === "right" ? rect.right - menuWidth : rect.left;
        const left = Math.min(
            viewportWidth - menuWidth - margin,
            Math.max(margin, preferredLeft)
        );
        const top = placement === "below"
            ? rect.bottom + margin
            : Math.max(margin, rect.top - menuHeight - margin);

        const isSameMenu = String(openMessageMenuId) === String(messageId);
        if (isSameMenu) {
            closeMessageActionMenu();
            return;
        }
        setOpenMessageMenuId(messageId);
        setActiveMessageControlsId(messageId);
        setOpenReactionMessageId(null);
        setReactionPalettePosition(null);
        setMessageActionMenuPosition({ top, left, placement });
    };

    const openMessageActionMenu = (
        event: React.MouseEvent<HTMLButtonElement>,
        messageId: string | number,
        align: "left" | "right",
        canManage: boolean
    ) => {
        event.stopPropagation();
        openMessageActionMenuFromRect(event.currentTarget.getBoundingClientRect(), messageId, align, canManage);
    };

    const toggleChatIncognito = (chatId: string | number) => {
        if (globalIncognitoEnabled) return;
        setIncognitoChatIds(prev => {
            const next = new Set(prev);
            if (next.has(chatId)) {
                // Turning OFF — vanish incognito messages
                removeFromIconOrder(chatId, 'incognito');
                const chat = chats.find(c => c.id === chatId);
                if (chat) {
                    const incognitoMsgIds = chat.messages
                        .filter(m => m.isIncognito && m.from === "me")
                        .map(m => m.id);
                    if (incognitoMsgIds.length > 0) {
                        setVanishingMessages(new Set(incognitoMsgIds));
                        const socket = getSocket();
                        if (socket) {
                            socket.emit('incognito:clear', {
                                conversationId: chatId,
                                messageIds: incognitoMsgIds.map(String),
                            });
                        }
                        if (typeof chatId === 'string') {
                            storeIncognitoClear(chatId, incognitoMsgIds.map(String)).catch(() => {});
                        }
                        setTimeout(() => {
                            setChats(prev => prev.map(c =>
                                c.id === chatId
                                    ? { ...c, messages: c.messages.filter(m => !m.isIncognito || m.from !== "me") }
                                    : c
                            ));
                            setVanishingMessages(new Set());
                        }, 800);
                    }
                }
                next.delete(chatId);
            } else {
                addToIconOrder(chatId, 'incognito');
                next.add(chatId);
            }
            return next;
        });
    };

    const [newMessage, setNewMessage] = useState("");
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showChatSearch, setShowChatSearch] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showInboxMenu, setShowInboxMenu] = useState(false);
    const [groupActionExpanded, setGroupActionExpanded] = useState(false);
    const [messagePageByChat, setMessagePageByChat] = useState<Record<string, number>>(() => restoredSnapshot?.messagePageByChat || {});
    const [messageHasMoreByChat, setMessageHasMoreByChat] = useState<Record<string, boolean>>(() => restoredSnapshot?.messageHasMoreByChat || {});
    const [loadingOlderChatId, setLoadingOlderChatId] = useState<string | null>(null);
    const [retryingMessageIds, setRetryingMessageIds] = useState<Set<string | number>>(new Set());
    const [messageActionError, setMessageActionError] = useState("");
    const [editingMessageId, setEditingMessageId] = useState<string | number | null>(null);
    const [editingMessageText, setEditingMessageText] = useState("");
    const [openMessageMenuId, setOpenMessageMenuId] = useState<string | number | null>(null);
    const [messageActionMenuPosition, setMessageActionMenuPosition] = useState<MessageActionMenuPosition | null>(null);
    const [openReactionMessageId, setOpenReactionMessageId] = useState<string | number | null>(null);
    const [reactionPalettePosition, setReactionPalettePosition] = useState<ReactionPalettePosition | null>(null);
    const [activeMessageControlsId, setActiveMessageControlsId] = useState<string | number | null>(null);
    const [replyDraft, setReplyDraft] = useState<ChatMessageItem | null>(null);
    const [copiedMessageId, setCopiedMessageId] = useState<string | number | null>(null);
    const [messageToast, setMessageToast] = useState("");
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | number | null>(null);
    const [forwardMessage, setForwardMessage] = useState<ChatMessageItem | null>(null);
    const [forwardTargets, setForwardTargets] = useState<ForwardTarget[]>([]);
    const [forwardSearch, setForwardSearch] = useState("");
    const [forwardUserResults, setForwardUserResults] = useState<SearchUserResult[]>([]);
    const [forwardSearching, setForwardSearching] = useState(false);
    const [forwardSending, setForwardSending] = useState(false);
    const reactionPressTimerRef = useRef<number | null>(null);
    const pressStartRef = useRef<{ x: number; y: number } | null>(null);
    const highlightTimerRef = useRef<number | null>(null);
    const toastTimerRef = useRef<number | null>(null);
    const typingExpiryTimersRef = useRef<Record<string, number>>({});
    const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
    useEffect(() => {
        const snapshot: MessageSnapshot = {
            chats,
            requests,
            blocked,
            activeTab,
            inboxFilter,
            searchQuery,
            selectedChatId,
            messagePageByChat,
            messageHasMoreByChat,
            savedAt: Date.now(),
        };
        writeMessageSnapshot(snapshot);
    }, [activeTab, blocked, chats, inboxFilter, messageHasMoreByChat, messagePageByChat, requests, searchQuery, selectedChatId]);

    useEffect(() => {
        const persistNow = () => {
            writeMessageSnapshot({
                chats: chatsRef.current,
                requests,
                blocked,
                activeTab,
                inboxFilter,
                searchQuery,
                selectedChatId: selectedChatIdRef.current,
                messagePageByChat,
                messageHasMoreByChat,
                savedAt: Date.now(),
            });
        };
        window.addEventListener("pagehide", persistNow);
        document.addEventListener("visibilitychange", persistNow);
        return () => {
            window.removeEventListener("pagehide", persistNow);
            document.removeEventListener("visibilitychange", persistNow);
        };
    }, [activeTab, blocked, inboxFilter, messageHasMoreByChat, messagePageByChat, requests, searchQuery]);

    useEffect(() => {
        setReplyDraft(null);
        closeMessageOverlays();
    }, [selectedChatId, closeMessageOverlays]);

    useEffect(() => {
        if (!showInboxMenu) setGroupActionExpanded(false);
    }, [showInboxMenu]);

    useEffect(() => {
        setGroupActionExpanded(false);
    }, [activeTab]);

    useEffect(() => {
        setSelectedChatIds(new Set());
    }, [activeTab, inboxFilter]);

    useEffect(() => {
        const timer = window.setInterval(() => setLastSeenTick((tick) => tick + 1), 30000);
        return () => window.clearInterval(timer);
    }, []);

    // Delete confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [chatToDelete, setChatToDelete] = useState<Chat | null>(null);
    const [showOblivionModal, setShowOblivionModal] = useState(false);
    const [chatToOblivion, setChatToOblivion] = useState<Chat | null>(null);

    // Block confirmation modal
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [chatToBlock, setChatToBlock] = useState<Chat | null>(null);

    // Theme
    const [chatTheme, setChatTheme] = useState<string>(() => localStorage.getItem('theme') || 'Auto');

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === 'theme' && e.newValue) setChatTheme(e.newValue);
        };
        window.addEventListener('storage', onStorage);
        const interval = setInterval(() => {
            const t = localStorage.getItem('theme') || 'Auto';
            setChatTheme(prev => prev !== t ? t : prev);
        }, 2000);
        return () => { window.removeEventListener('storage', onStorage); clearInterval(interval); };
    }, []);

    const bubbleColors = (() => {
        switch (chatTheme) {
            case 'AMOLED': return { me: 'bg-neutral-800 text-white', them: 'bg-neutral-900 text-white' };
            case 'Light': return { me: 'bg-blue-500 text-white', them: 'bg-gray-200 text-gray-900' };
            case 'Solarized': return { me: 'bg-cyan-700 text-white', them: 'bg-stone-700 text-amber-100' };
            case 'Zima Blue': return { me: 'bg-sky-600 text-white', them: 'bg-slate-700 text-sky-100' };
            case 'Sea': return { me: 'bg-teal-600 text-white', them: 'bg-slate-700 text-teal-100' };
            case 'Space': return { me: 'bg-indigo-700 text-white', them: 'bg-slate-800 text-indigo-100' };
            case 'Dark': return { me: 'bg-violet-700 text-white', them: 'bg-neutral-800 text-white' };
            default: return { me: 'bg-purple-700 text-white', them: 'bg-neutral-800 text-white' };
        }
    })();

    // Tab-switch blur for incognito chats
    const [tabBlurred, setTabBlurred] = useState(false);
    useEffect(() => {
        if (!isCurrentChatIncognito) { setTabBlurred(false); return; }
        const onVisChange = () => {
            setTabBlurred(document.hidden);
        };
        document.addEventListener('visibilitychange', onVisChange);
        return () => document.removeEventListener('visibilitychange', onVisChange);
    }, [isCurrentChatIncognito]);

    // New Chat modal
    const [showNewChatModal, setShowNewChatModal] = useState(false);
    const [newChatSearch, setNewChatSearch] = useState("");
    const [newChatResults, setNewChatResults] = useState<SearchUserResult[]>([]);
    const [newChatLoading, setNewChatLoading] = useState(false);
    const [newChatCreating, setNewChatCreating] = useState<string | null>(null);
    const [newChatActiveIndex, setNewChatActiveIndex] = useState(0);
    const [newChatMode, setNewChatMode] = useState<"direct" | "group">("direct");
    const [newGroupName, setNewGroupName] = useState("");
    const [newGroupMembers, setNewGroupMembers] = useState<SearchUserResult[]>([]);
    const [newGroupCreating, setNewGroupCreating] = useState(false);
    const [newGroupError, setNewGroupError] = useState("");
    const [groupCodeInput, setGroupCodeInput] = useState("");
    const [groupCodeStatus, setGroupCodeStatus] = useState("");
    const [showGroupPanel, setShowGroupPanel] = useState(false);
    const [groupDetails, setGroupDetails] = useState<GroupDetails | null>(null);
    const [groupLoading, setGroupLoading] = useState(false);
    const [groupAction, setGroupAction] = useState("");
    const [groupError, setGroupError] = useState("");
    const [groupToast, setGroupToast] = useState("");
    const [groupInfoReturnTarget, setGroupInfoReturnTarget] = useState<"chat" | "menu">("chat");
    const [groupNameDraft, setGroupNameDraft] = useState("");
    const [groupDescriptionDraft, setGroupDescriptionDraft] = useState("");
    const [groupMemberSearch, setGroupMemberSearch] = useState("");
    const [groupMemberResults, setGroupMemberResults] = useState<SearchUserResult[]>([]);
    const [groupMemberSearching, setGroupMemberSearching] = useState(false);
    const [groupMemberListSearchOpen, setGroupMemberListSearchOpen] = useState(false);
    const [groupMemberListSearch, setGroupMemberListSearch] = useState("");

    const closeNewChatModal = useCallback(() => {
        setShowNewChatModal(false);
        setNewChatSearch("");
        setNewChatResults([]);
        setNewChatCreating(null);
        setNewChatActiveIndex(0);
        setNewChatMode("direct");
        setNewGroupName("");
        setNewGroupMembers([]);
        setNewGroupCreating(false);
        setNewGroupError("");
    }, []);

    const conversationToChat = (c: ConversationItem): Chat => {
        const displayName = c.isGroup
            ? (c.groupName || "Group conversation")
            : (c.otherUser?.displayName || c.otherUser?.username || "Conversation");
        const avatar = c.isGroup
            ? (c.groupAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=14313b&color=67e8f9`)
            : (c.otherUser?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=222&color=fff`);
        const fromMe = c.lastMessage?.sender && c.lastMessage.sender === currentUser?.username;
        const unreadCount = c.isRecycled ? 0 : (c.unreadCount || 0);

        const visibleMemberCount = Math.max(0, c.memberCount ?? c.participants?.length ?? 0);

        return {
            id: c.id,
            otherUserId: !c.isGroup && c.otherUser?.id ? String(c.otherUser.id) : undefined,
            participantIds: c.participants?.map((participant) => String(participant.id)) || [],
            isGroup: c.isGroup,
            groupDescription: c.groupDescription || "",
            groupOwnerId: c.groupOwner ? String(c.groupOwner) : undefined,
            groupAdminIds: c.groupAdmins?.map(String) || [],
            myGroupRole: c.myGroupRole || null,
            presenceOwnedByPeer: Boolean(c.presenceOwnedByPeer),
            hasLeftGroup: Boolean(c.hasLeftGroup),
            leftAt: c.leftAt || null,
            groupSendPermission: c.groupPermissions?.sendMessages || "everyone",
            name: displayName,
            avatar,
            status: c.requestStatus === "pending"
                ? "Request pending"
                : c.hasLeftGroup
                    ? "You left this group"
                : c.isGroup
                    ? `${visibleMemberCount} ${visibleMemberCount === 1 ? "member" : "members"}`
                    : (unreadCount > 0 ? `${unreadCount} unread` : ""),
            isEncrypted: c.isEncrypted,
            pinnedMessages: [],
            unreadCount,
            lastMessageAt: c.lastMessage?.timestamp
                ? new Date(c.lastMessage.timestamp).getTime()
                : c.updatedAt
                    ? new Date(c.updatedAt).getTime()
                    : undefined,
            updatedAt: c.updatedAt ? new Date(c.updatedAt).getTime() : undefined,
            isMuted: c.isMuted ?? false,
            isPinned: c.isPinned ?? false,
            isArchived: Boolean(c.isArchived),
            isRecycled: Boolean(c.isRecycled),
            isBlocked: c.isBlocked ?? false,
            isBlockedByMe: Boolean(c.isBlockedByMe),
            isPresenceConversation: Boolean(c.isPresenceConversation),
            requestStatus: c.requestStatus || "accepted",
            requestedBy: c.requestedBy || null,
            messages: c.lastMessage
                ? [{
                    id: `${c.id}-last`,
                    from: fromMe ? "me" : displayName,
                    text: c.lastMessage.content,
                    time: new Date(c.lastMessage.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }),
                    status: fromMe ? "sent" as const : "delivered" as const,
                }]
                : [],
        };
    };

    const loadRequests = async () => {
        try {
            const [messageList, groupInviteList] = await Promise.all([
                fetchMessageRequests(),
                fetchGroupInvites(),
            ]);
            requestConversationByIdRef.current = new Map(
                (messageList || []).map((request) => [String(request.id), request])
            );

            const messageRequests: FriendRequest[] = (messageList || []).map((request) => {
                const displayName = request.otherUser?.displayName || request.otherUser?.username || "New conversation";
                return {
                    id: request.id,
                    kind: "message",
                    conversationId: request.id,
                    name: displayName,
                    avatar: request.otherUser?.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=222&color=fff`,
                    note: request.preview?.content || "Wants to start a conversation.",
                };
            });

            const groupRequests: FriendRequest[] = (groupInviteList || []).map((entry) => {
                const groupName = entry.conversation.groupName || "Group conversation";
                return {
                    id: `group-${entry.conversation.id}`,
                    kind: "group",
                    conversationId: entry.conversation.id,
                    name: groupName,
                    avatar: entry.conversation.groupAvatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(groupName)}&background=14313b&color=67e8f9`,
                    note: "Group invitation waiting for your response.",
                };
            });

            setRequests([...messageRequests, ...groupRequests]);
            publishMessageRequestUpdate();
            if (requestedConversationId) {
                const targetRequest = messageRequests.find((request) => String(request.conversationId) === String(requestedConversationId));
                if (targetRequest) {
                    const conversation = requestConversationByIdRef.current.get(String(targetRequest.conversationId));
                    if (conversation) syncConversation(conversation);
                    setSelectedChatId(String(targetRequest.conversationId));
                    setActiveTab(requestedNotificationType === "message_request" ? "chats" : "requests");
                }
            }
        } catch (err) {
            console.error("Failed to load requests:", err);
        }
    };

    const loadBlocked = async () => {
        try {
            const blockedList = await fetchBlockedConversations();
            setBlocked(blockedList.map(b => ({
                id: b.id,
                userId: b.userId || null,
                name: b.name,
                avatar: b.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.name)}&background=222&color=fff`,
                reason: b.reason,
                conversationId: b.conversationId || null,
                requestStatus: b.requestStatus,
                requestedBy: b.requestedBy || null,
            })));
        } catch (err) {
            console.error("Failed to load blocked:", err);
        }
    };

    useEffect(() => {
        const loadConversations = async () => {
            try {
                const conversations = await fetchConversations();
                const mapped: Chat[] = conversations.map(conversationToChat);

                setChats(prev => {
                    const previousById = new Map(prev.map(chat => [String(chat.id), chat]));
                    return mapped.map(chat => {
                        const previous = previousById.get(String(chat.id));
                        return previous
                            ? { ...chat, messages: previous.messages.length ? previous.messages : chat.messages }
                            : chat;
                    });
                });
                if (requestedConversationId && mapped.some(chat => String(chat.id) === requestedConversationId)) {
                    setSelectedChatId(requestedConversationId);
                    setActiveTab("chats");
                }
                // Seed icon order for chats with active features but no saved order
                mapped.forEach(chat => {
                    const existing = getIconOrder(chat.id);
                    if (existing.length === 0) {
                        const order: FeatureKey[] = [];
                        if (chat.isEncrypted) order.push('lock');
                        if (chat.isMuted) order.push('muted');
                        if (chat.isPinned) order.push('pinned');
                        if (order.length > 0) {
                            localStorage.setItem(`iconOrder_${chat.id}`, JSON.stringify(order));
                        }
                    }
                });
                return mapped; // ← return so kill switch loader can receive them
            } catch (err) {
                console.error("Failed to load conversations:", err);
                return [];
            }
        };

        const loadKillSwitchState = async (loadedChats: Chat[]) => {
            try {
                const res = await getProfile();
                if (!res.success) return;
                const killSwitchOn = Boolean(res.user?.settings?.killSwitch);
                const realm = (res as any)?.realm || (res.user as any)?.realm;

                setCurrentUserIsPresence(Boolean(
                    realm?.isPresence || (res.user as any)?.isPresence || (res.user as any)?.realmRole === "presence"
                ));
                setGlobalIncognitoEnabled(killSwitchOn);
            } catch {
                // Non-critical — messaging still works without kill switch state
            }
        };

        loadConversations().then(loaded => {
            if (loaded) loadKillSwitchState(loaded);
        });
        loadBlocked();
        loadRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedConversationId]);

    // ── Socket.IO connection ──
    useEffect(() => {
        let socket: ReturnType<typeof connectSocket> | null = null;
        try {
            socket = connectSocket();
        } catch {
            return;
        }

        const resyncAfterReconnect = async () => {
            try {
                const conversations = await fetchConversations();
                setChats(prev => {
                    const previousById = new Map(prev.map(chat => [String(chat.id), chat]));
                    return conversations.map(conversationToChat).map(chat => {
                        const previous = previousById.get(String(chat.id));
                        return previous
                            ? { ...chat, messages: previous.messages.length ? previous.messages : chat.messages }
                            : chat;
                    });
                });
                const activeId = selectedChatIdRef.current;
                if (activeId && typeof activeId === "string") {
                    void loadConversationMessages(activeId, 1, false);
                }
                const allowedIds = Array.from(presenceAllowedUserIdsRef.current);
                if (allowedIds.length) socket?.emit('presence:subscribe', { userIds: allowedIds });
            } catch {
                // Reconnect resync is best-effort; normal message handlers continue to work.
            }
        };

        socket.on('connect', () => {
            socket?.emit('messages:enter');
            void resyncAfterReconnect();
        });
        socket.emit('messages:enter');

        socket.on('message:request:new', (payload: { conversationId?: string }) => {
            void loadRequests();
            if (payload?.conversationId && !selectedChatIdRef.current) {
                setActiveTab("requests");
            }
        });

        socket.on('message:new', (payload: SocketMessagePayload) => {
            const fromMe = payload.sender.id === currentUser?.id;
            const activeConversation = String(selectedChatIdRef.current) === String(payload.conversationId);
            const targetChatBeforeUpdate = chatsRef.current.find(chat => String(chat.id) === String(payload.conversationId));
            if (payload.clientMessageId) {
                confirmedClientMessageIdsRef.current.add(payload.clientMessageId);
            }

            // If this is our own incognito message echoed back, skip entirely
            // (we already added it locally in handleSend)
            if (fromMe && payload.isIncognito) return;

            const msg: ChatMessageItem = {
                id: String(payload.id),
                clientMessageId: payload.clientMessageId,
                from: fromMe ? "me" : (payload.sender.displayName || payload.sender.username),
                text: payload.content,
                time: new Date(payload.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
                status: (fromMe ? "sent" : "delivered") as 'sent' | 'delivered' | 'read' | 'failed',
                replyTo: payload.replyTo || null,
                forwarded: Boolean(payload.forwarded),
                reactions: payload.reactions || [],
                createdAt: payload.createdAt,
                editedAt: payload.editedAt || null,
                ...(payload.isIncognito ? { isIncognito: true } : {}),
            };
            setChats(prev => {
                let foundConversation = false;
                const updatedChats = prev.map(c => {
                    if (String(c.id) !== String(payload.conversationId)) return c;
                    foundConversation = true;

                    // If this is our own message echoed back, replace the optimistic temp message
                    if (fromMe) {
                        const tempIdx = c.messages.findIndex(m => (
                            (payload.clientMessageId && m.clientMessageId === payload.clientMessageId) ||
                            (typeof m.id === 'string' && m.id.startsWith('temp-') && m.text === payload.content)
                        ));
                        if (tempIdx !== -1) {
                            const updated = [...c.messages];
                            updated[tempIdx] = msg;
                            return {
                                ...c,
                                messages: updated,
                                lastMessageAt: new Date(payload.createdAt).getTime(),
                            };
                        }
                    }

                    // Otherwise add the message (dedup by server id)
                    const withoutDuplicates = c.messages.filter(m =>
                        String(m.id) !== String(payload.id) &&
                        (!payload.clientMessageId || m.clientMessageId !== payload.clientMessageId)
                    );
                    const unreadCount = c.isRecycled
                        ? 0
                        : activeConversation
                            ? c.unreadCount
                            : c.unreadCount + (fromMe ? 0 : 1);
                    return {
                        ...c,
                        messages: [...withoutDuplicates, msg],
                        lastMessageAt: new Date(payload.createdAt).getTime(),
                        unreadCount,
                    };
                });
                if (!foundConversation && !fromMe) {
                    fetchConversations()
                        .then(conversations => {
                            const revived = conversations.find(conversation => String(conversation.id) === String(payload.conversationId));
                            if (!revived) return;
                            const mapped = conversationToChat(revived);
                            setChats(current => {
                                const exists = current.some(chat => String(chat.id) === String(mapped.id));
                                if (exists) return current;
                                return [{ ...mapped, messages: [msg], unreadCount: activeConversation ? 0 : Math.max(mapped.unreadCount || 0, 1) }, ...current];
                            });
                        })
                        .catch(() => {});
                }
                return updatedChats;
            });

            const liveSignalsAllowed = targetChatBeforeUpdate
                ? canUseLiveChatSignals(targetChatBeforeUpdate)
                : liveChatSignalAllowedRef.current.has(String(payload.conversationId));

            if (!fromMe && activeConversation && !payload.isIncognito && liveSignalsAllowed) {
                socket?.emit('conversation:read', { conversationId: payload.conversationId });
                acknowledgeConversationRead(payload.conversationId);
                window.setTimeout(() => acknowledgeConversationRead(payload.conversationId), 350);
            }
        });

        socket.on('message:reaction:update', (data: { conversationId: string; messageId: string; reactions: { user: string; reaction: string; reactedAt?: string }[] }) => {
            setChats(prev => prev.map(chat =>
                String(chat.id) === String(data.conversationId)
                    ? {
                        ...chat,
                        messages: chat.messages.map(message =>
                            String(message.id) === String(data.messageId)
                                ? { ...message, reactions: data.reactions }
                                : message
                        ),
                    }
                    : chat
            ));
        });

        socket.on('message:edited', (data: { conversationId: string; message: SocketMessagePayload }) => {
            setChats(prev => prev.map(chat =>
                String(chat.id) === String(data.conversationId)
                    ? {
                        ...chat,
                        messages: chat.messages.map(message =>
                            String(message.id) === String(data.message.id)
                                ? {
                                    ...message,
                                    text: data.message.content,
                                    editedAt: data.message.editedAt || new Date().toISOString(),
                                }
                                : message
                        ),
                    }
                    : chat
            ));
        });

        socket.on('message:deleted', (data: { conversationId: string; messageId: string }) => {
            setChats(prev => prev.map(chat =>
                String(chat.id) === String(data.conversationId)
                    ? { ...chat, messages: chat.messages.filter(message => String(message.id) !== String(data.messageId)) }
                    : chat
            ));
        });

        socket.on('conversation:read:update', (data: { conversationId: string; userId: string }) => {
            setChats(prev => prev.map(chat => {
                if (String(chat.id) !== String(data.conversationId)) return chat;

                if (String(data.userId) === String(currentUser?.id)) {
                    return { ...chat, unreadCount: 0 };
                }

                return {
                    ...chat,
                    messages: chat.messages.map(message =>
                        message.from === "me" && message.status !== "failed"
                            ? { ...message, status: "read" as const }
                            : message
                    ),
                };
            }));
        });

        socket.on('conversation:blocked:update', (data: { conversationId: string; isBlocked: boolean; isBlockedByMe?: boolean; blockedBy?: string[] }) => {
            setChats(prev => prev.map(chat => {
                if (String(chat.id) !== String(data.conversationId)) return chat;
                return {
                    ...chat,
                    isBlocked: data.isBlocked,
                    isBlockedByMe: Boolean(data.isBlockedByMe),
                    status: data.isBlocked ? "Messaging unavailable" : chat.status,
                };
            }));
            if (data.isBlocked) {
                setTypingUsers(prev => {
                    const next = new Set(prev);
                    next.delete(data.conversationId);
                    return next;
                });
                setPresenceMap(prev => {
                    const chat = chats.find(item => String(item.id) === String(data.conversationId));
                    const key = chat?.otherUserId;
                    if (!key) return prev;
                    return {
                        ...prev,
                        [key]: { online: false, lastSeen: undefined, incognito: true, hidden: true },
                    };
                });
            }
        });

        socket.on('conversation:oblivion', (data: { conversationId: string }) => {
            setChats(prev => prev.filter(chat => String(chat.id) !== String(data.conversationId)));
            setTypingUsers(prev => {
                const next = new Set(prev);
                next.delete(data.conversationId);
                return next;
            });
            if (String(selectedChatIdRef.current) === String(data.conversationId)) {
                setSelectedChatId(null);
                setNewMessage("");
                setReplyDraft(null);
                setShowGroupPanel(false);
            }
        });

        socket.on('typing:update', (data: { conversationId: string; userId: string; typing: boolean }) => {
            if (data.userId === currentUser?.id) return;
            const key = String(data.conversationId);
            if (typingExpiryTimersRef.current[key]) {
                window.clearTimeout(typingExpiryTimersRef.current[key]);
                delete typingExpiryTimersRef.current[key];
            }
            setTypingUsers(prev => {
                const next = new Set(prev);
                if (data.typing) next.add(data.conversationId);
                else next.delete(data.conversationId);
                return next;
            });
            if (data.typing) {
                typingExpiryTimersRef.current[key] = window.setTimeout(() => {
                    setTypingUsers(prev => {
                        const next = new Set(prev);
                        next.delete(data.conversationId);
                        return next;
                    });
                    delete typingExpiryTimersRef.current[key];
                }, 6000);
            }
        });

        socket.on('presence:update', (data: { userId: string; online: boolean; lastSeen?: number; incognito?: boolean; hidden?: boolean }) => {
            if (!presenceAllowedUserIdsRef.current.has(String(data.userId))) {
                setPresenceMap(prev => ({
                    ...prev,
                    [data.userId]: {
                        online: false,
                        lastSeen: undefined,
                        incognito: true,
                        hidden: true,
                    },
                }));
                return;
            }
            setPresenceMap(prev => ({
                ...prev,
                [data.userId]: {
                    online: data.online,
                    lastSeen: data.online ? Date.now() : data.lastSeen,
                    incognito: data.incognito,
                    hidden: data.hidden,
                },
            }));
        });

        socket.on('presence:state', (data: { states?: { userId: string; online: boolean; lastSeen?: number; incognito?: boolean; hidden?: boolean }[] }) => {
            if (!Array.isArray(data.states)) return;
            setPresenceMap(prev => {
                const next = { ...prev };
                data.states!.forEach(state => {
                    next[state.userId] = {
                        online: state.online,
                        lastSeen: state.online ? Date.now() : state.lastSeen,
                        incognito: state.incognito,
                        hidden: state.hidden,
                    };
                });
                return next;
            });
        });

        socket.on('incognito:clear', (data: { conversationId: string; messageIds: string[] }) => {
            if (!data.messageIds?.length) return;
            const ids = new Set(data.messageIds);
            // Animate then remove
            setVanishingMessages(new Set(data.messageIds));
            setTimeout(() => {
                setChats(prev => prev.map(c =>
                    String(c.id) === String(data.conversationId)
                        ? { ...c, messages: c.messages.filter(m => !ids.has(String(m.id))) }
                        : c
                ));
                setVanishingMessages(new Set());
            }, 800);
        });

        socket.on('group:updated', (data: {
            conversationId: string;
            event: string;
            conversation?: ConversationItem | null;
            removedUserId?: string;
            userId?: string;
        }) => {
            const removedMe = String(data.removedUserId || data.userId || '') === String(currentUser?.id);
            if (!data.conversation || removedMe || data.event === 'group_deleted') {
                setChats(prev => prev.filter(chat => String(chat.id) !== String(data.conversationId)));
                if (String(selectedChatIdRef.current) === String(data.conversationId)) {
                    setSelectedChatId(null);
                    setShowGroupPanel(false);
                }
                return;
            }

            const mapped = conversationToChat(data.conversation);
            setChats(prev => {
                const exists = prev.some(chat => String(chat.id) === String(mapped.id));
                if (!exists) return [mapped, ...prev];
                return prev.map(chat => String(chat.id) === String(mapped.id)
                    ? { ...chat, ...mapped, messages: chat.messages }
                    : chat
                );
            });

            if (String(selectedChatIdRef.current) === String(data.conversationId)) {
                fetchGroupDetails(data.conversationId)
                    .then(details => {
                        setGroupDetails(details);
                        setGroupNameDraft(details.groupName || "");
                        setGroupDescriptionDraft(details.groupDescription || "");
                    })
                    .catch(() => {});
            }
        });

        return () => {
            socket?.emit('messages:leave');
            Object.values(typingExpiryTimersRef.current).forEach(timer => window.clearTimeout(timer));
            typingExpiryTimersRef.current = {};
            disconnectSocket();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const tick = () => {
            const socket = getSocket();
            if (socket?.connected) socket.emit('presence:heartbeat');
        };
        tick();
        const timer = window.setInterval(tick, 45000);
        return () => window.clearInterval(timer);
    }, []);

    const presenceSubscriptionKey = chats
        .filter(chat => !chat.isBlocked && !chat.isGroup && (chat.requestStatus || "accepted") === "accepted")
        .map(chat => chat.otherUserId)
        .filter(Boolean)
        .map(String)
        .sort()
        .join("|");

    useEffect(() => {
        if (!presenceSubscriptionKey) return;
        const socket = getSocket();
        if (!socket) return;
        const userIds = presenceSubscriptionKey.split("|").filter(Boolean);
        socket.emit('presence:subscribe', { userIds });
    }, [presenceSubscriptionKey]);

    // ── Join/leave conversation rooms ──
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;
        if (prevChatRef.current && prevChatRef.current !== selectedChatId) {
            socket.emit('conversation:leave', { conversationId: prevChatRef.current });
        }
        if (selectedChatId && typeof selectedChatId === 'string') {
            const chatForSignals = chats.find(chat => String(chat.id) === String(selectedChatId)) || null;
            const canLoadLiveConversation = Boolean(
                chatForSignals &&
                !chatForSignals.isBlocked &&
                (chatForSignals.isGroup || (chatForSignals.requestStatus || "accepted") === "accepted")
            );
            if (canLoadLiveConversation) {
                socket.emit('conversation:join', { conversationId: selectedChatId });
                loadConversationMessages(selectedChatId, 1, false);
            } else {
                setMessageActionError("");
            }
            if (canLoadLiveConversation && !isChatIncognito(selectedChatId) && canUseLiveChatSignals(chatForSignals)) {
                socket.emit('conversation:read', { conversationId: selectedChatId });
                acknowledgeConversationRead(selectedChatId);
            }
            // Check pending incognito clears from other user
            if (canLoadLiveConversation) fetchIncognitoPending(selectedChatId).then(ids => {
                if (ids.length > 0) {
                    const idSet = new Set(ids);
                    setVanishingMessages(new Set(ids));
                    setTimeout(() => {
                        setChats(prev => prev.map(c =>
                            c.id === selectedChatId
                                ? { ...c, messages: c.messages.filter(m => !idSet.has(String(m.id))) }
                                : c
                        ));
                        setVanishingMessages(new Set());
                    }, 800);
                }
            }).catch(() => {});
        }
        prevChatRef.current = selectedChatId;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChatId]);

    const loadConversationMessages = async (conversationId: string, page = 1, appendOlder = false) => {
        if (appendOlder) setLoadingOlderChatId(conversationId);
        try {
            const data = await fetchMessages(conversationId, { page, limit: MESSAGE_PAGE_SIZE });
            const mapped = data.messages.map((m) => {
                const isMe = m.sender.id === currentUser?.id;
                // Determine status from readBy: if someone other than sender read it → read, otherwise → sent
                let status: 'sent' | 'delivered' | 'read' | 'failed' = 'sent';
                if (isMe && m.readBy?.length) {
                    const othersRead = m.readBy.some(r => r.user !== m.sender.id);
                    status = othersRead ? 'read' : 'sent';
                } else if (!isMe) {
                    status = 'delivered';
                }
                return {
                    id: m.id,
                    clientMessageId: m.clientMessageId,
                    from: isMe ? "me" : (m.sender.displayName || m.sender.username),
                    text: m.content,
                    time: new Date(m.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }),
                    status,
                    replyTo: m.replyTo || null,
                    forwarded: Boolean(m.forwarded),
                    reactions: m.reactions || [],
                    createdAt: m.createdAt,
                    editedAt: m.editedAt || null,
                };
            });

            if (appendOlder) loadingOlderRef.current = true;
            setChats((prev) => prev.map((chat) => (
                String(chat.id) === String(conversationId)
                    ? (() => {
                        const mappedIds = new Set(mapped.map((message) => String(message.id)));
                        const newestMappedTime = mapped.reduce((latest, message) => {
                            const value = message.createdAt ? new Date(message.createdAt).getTime() : 0;
                            return Number.isFinite(value) && value > latest ? value : latest;
                        }, 0);
                        const localMessagesToKeep = chat.messages.filter((existing) => {
                            if (mappedIds.has(String(existing.id))) return false;
                            if (existing.clientMessageId && mapped.some((incoming) => incoming.clientMessageId === existing.clientMessageId)) return false;
                            if (existing.status === "sending" || existing.status === "failed") return true;
                            const existingTime = existing.createdAt ? new Date(existing.createdAt).getTime() : 0;
                            return Number.isFinite(existingTime) && existingTime > newestMappedTime;
                        });

                        return {
                            ...chat,
                            messages: appendOlder
                                ? [
                                    ...mapped.filter((incoming) => !chat.messages.some((existing) => String(existing.id) === String(incoming.id))),
                                    ...chat.messages,
                                ]
                                : [...mapped, ...localMessagesToKeep].sort((a, b) => {
                                    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                                    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                                    return aTime - bTime;
                                }),
                        };
                    })()
                    : chat
            )));
            setMessagePageByChat(prev => ({ ...prev, [conversationId]: page }));
            setMessageHasMoreByChat(prev => ({ ...prev, [conversationId]: data.hasMore }));
            return data.hasMore;
        } catch (err) {
            console.error("Failed to load messages:", err);
            const status = (err as { status?: number } | null)?.status;
            if (status === 403 || status === 404) {
                setChats(prev => prev.filter(chat => String(chat.id) !== String(conversationId)));
                if (String(selectedChatIdRef.current) === String(conversationId)) {
                    setSelectedChatId(null);
                    setNewMessage("");
                    setReplyDraft(null);
                }
                setMessageActionError("");
                return false;
            }
            setMessageActionError(err instanceof Error ? err.message : "Failed to load messages.");
            return false;
        } finally {
            if (appendOlder) setLoadingOlderChatId(null);
        }
    };

    const loadOlderMessages = () => {
        if (!selectedChatId || typeof selectedChatId !== "string") return;
        const key = String(selectedChatId);
        if (!messageHasMoreByChat[key] || loadingOlderChatId === key) return;
        void loadConversationMessages(key, (messagePageByChat[key] || 1) + 1, true);
    };

    // Dropdown/menu ref
    const inboxMenuRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const emojiPickerRef = useRef<HTMLDivElement | null>(null);
    const messageInputRef = useRef<HTMLInputElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const typingLastEmitRef = useRef(0);
    const prevChatRef = useRef<string | number | null>(null);
    const loadingOlderRef = useRef(false);

    // === Resizable sidebar ===
    const initialSidebar = 320;
    const [sidebarWidth, setSidebarWidth] = useState<number>(initialSidebar);
    const dragRef = useRef<{
        dragging: boolean;
        startX: number;
        startW: number;
        minW: number;
        maxW: number;
    } | null>(null);

    useEffect(() => {
        dragRef.current = {
            dragging: false,
            startX: 0,
            startW: sidebarWidth,
            minW: sidebarWidth,
            maxW: Math.floor(window.innerWidth * 0.6),
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onResizerMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        dragRef.current = {
            dragging: true,
            startX: e.clientX,
            startW: sidebarWidth,
            minW: Math.max(sidebarWidth, 240),
            maxW: Math.floor(window.innerWidth * 0.6),
        };
        document.addEventListener("mousemove", onResizerMouseMove);
        document.addEventListener("mouseup", onResizerMouseUp);
    };

    const onResizerMouseMove = (e: MouseEvent) => {
        if (!dragRef.current?.dragging) return;
        const delta = e.clientX - dragRef.current.startX;
        let next = dragRef.current.startW + delta;
        if (next < dragRef.current.minW) next = dragRef.current.minW;
        if (next > dragRef.current.maxW) next = dragRef.current.maxW;
        setSidebarWidth(next);
    };

    const onResizerMouseUp = () => {
        if (dragRef.current) dragRef.current.dragging = false;
        document.removeEventListener("mousemove", onResizerMouseMove);
        document.removeEventListener("mouseup", onResizerMouseUp);
    };

    const onResizerDoubleClick = () => {
        setSidebarWidth(initialSidebar);
        if (dragRef.current) dragRef.current.minW = initialSidebar;
    };

    // Close menu on outside click OR ESC; ESC also closes selected chat
    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-message-actions="true"]')) {
                closeMessageActionMenu();
            }
            if (!target.closest('[data-message-reactions="true"]')) {
                setOpenReactionMessageId(null);
                setReactionPalettePosition(null);
                setActiveMessageControlsId(null);
            }
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
                setShowExportMenu(false);
            }
            if (inboxMenuRef.current && !inboxMenuRef.current.contains(e.target as Node)) {
                setShowInboxMenu(false);
            }
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
                setShowEmojiPicker(false);
            }
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (showBlockModal) {
                setShowBlockModal(false);
                setChatToBlock(null);
                return;
            }
            if (showDeleteModal) {
                setShowDeleteModal(false);
                setChatToDelete(null);
                return;
            }
            if (showOblivionModal) {
                setShowOblivionModal(false);
                setChatToOblivion(null);
                return;
            }
            if (showNewChatModal) {
                closeNewChatModal();
                return;
            }
            if (showGroupPanel) {
                const returnToMenu = groupInfoReturnTarget === "menu";
                setShowGroupPanel(false);
                setGroupError("");
                setGroupToast("");
                setGroupAction("");
                setGroupMemberSearch("");
                setGroupMemberResults([]);
                setGroupMemberListSearch("");
                setGroupMemberListSearchOpen(false);
                setGroupInfoReturnTarget("chat");
                if (returnToMenu) setMenuOpen(true);
                return;
            }
            if (menuOpen) {
                setMenuOpen(false);
                return;
            }
            if (showInboxMenu) {
                setShowInboxMenu(false);
                return;
            }
            if (showExportMenu) {
                setShowExportMenu(false);
                return;
            }
            if (selectedChatIds.size > 0) {
                setSelectedChatIds(new Set());
                return;
            }
            if (openMessageMenuId !== null) {
                closeMessageActionMenu();
                return;
            }
            if (openReactionMessageId !== null) {
                setOpenReactionMessageId(null);
                setReactionPalettePosition(null);
                setActiveMessageControlsId(null);
                return;
            }
            if (showEmojiPicker) {
                setShowEmojiPicker(false);
                return;
            }
            if (showChatSearch) {
                setShowChatSearch(false);
                setSearchInChat("");
                return;
            }
            if (selectedChatId !== null) {
                setSelectedChatId(null);
                setNewMessage("");
            }
        };
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("keydown", handleEsc);
        document.addEventListener("scroll", closeMessageOverlays, true);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("keydown", handleEsc);
            document.removeEventListener("scroll", closeMessageOverlays, true);
        };
    }, [menuOpen, selectedChatId, selectedChatIds, showNewChatModal, showGroupPanel, groupInfoReturnTarget, showDeleteModal, showOblivionModal, showBlockModal, showExportMenu, showEmojiPicker, showInboxMenu, showChatSearch, openMessageMenuId, openReactionMessageId, activeMessageControlsId, closeMessageActionMenu, closeMessageOverlays, closeNewChatModal]);

    const selectedChat = chats.find((c) => c.id === selectedChatId) || null;
    const getChatPresenceKey = (chat: Chat) => chat.otherUserId || String(chat.id);
    const canShowChatPresence = (chat: Chat) =>
        !chat.isBlocked && !chat.isGroup && (chat.requestStatus || "accepted") === "accepted";
    const getChatPresence = (chat: Chat) => {
        void lastSeenTick;
        return canShowChatPresence(chat) ? presenceMap[getChatPresenceKey(chat)] : undefined;
    };
    useEffect(() => {
        setPresenceMap(prev => {
            let changed = false;
            const next = { ...prev };
            chats.forEach(chat => {
                const presenceAllowed = !chat.isBlocked && !chat.isGroup && (chat.requestStatus || "accepted") === "accepted";
                if (chat.isGroup || !chat.otherUserId || presenceAllowed) return;
                const key = String(chat.otherUserId);
                if (!prev[key] || prev[key].hidden) return;
                next[key] = { online: false, lastSeen: undefined, incognito: true, hidden: true };
                changed = true;
            });
            return changed ? next : prev;
        });
    }, [chats]);
    const getChatStatus = (chat: Chat) =>
        onlineUsers.find((user) => String(user.id) === getChatPresenceKey(chat))?.status;
    const selectedChatPresence = selectedChat ? getChatPresence(selectedChat) : undefined;
    const selectedChatStatus = selectedChat ? getChatStatus(selectedChat) : undefined;
    const selectedRequestPendingByMe = Boolean(
        selectedChat &&
        !selectedChat.isGroup &&
        selectedChat.requestStatus === "pending" &&
        String(selectedChat.requestedBy) === String(currentUser?.id)
    );
    const selectedRequestNeedsReview = Boolean(
        selectedChat &&
        !selectedChat.isGroup &&
        selectedChat.requestStatus === "pending" &&
        String(selectedChat.requestedBy) !== String(currentUser?.id)
    );
    const selectedBlockedForViewer = Boolean(selectedChat?.isBlocked);
    const canBlockSelectedChat = Boolean(
        selectedChat &&
        !selectedChat.isGroup &&
        (!selectedChat.isPresenceConversation || currentUserIsPresence)
    );
    const canUnblockSelectedChat = Boolean(selectedChat?.isBlockedByMe && canBlockSelectedChat);
    const canReportSelectedChat = Boolean(
        selectedChat &&
        !selectedChat.isGroup &&
        (!selectedChat.isPresenceConversation || currentUserIsPresence)
    );
    const canUseOblivionSelectedChat = Boolean(
        selectedChat &&
        !selectedChat.isGroup &&
        (!selectedChat.isPresenceConversation || currentUserIsPresence) &&
        (!selectedChat.isBlocked || selectedChat.isBlockedByMe)
    );
    const selectedMessagingDisabled = selectedRequestPendingByMe || selectedRequestNeedsReview || selectedChat?.requestStatus === "declined" || selectedBlockedForViewer || Boolean(selectedChat?.hasLeftGroup);
    const groupMemberIdsKey = (groupDetails?.members || []).map(member => String(member.id)).sort().join("|");
    const groupOwnedByPeerPresence = Boolean(groupDetails?.presenceOwnedByPeer || selectedChat?.presenceOwnedByPeer);
    const isGroupManager = !groupOwnedByPeerPresence && (groupDetails?.myGroupRole === "owner" || groupDetails?.myGroupRole === "admin" || groupDetails?.myGroupRole === "presence");
    const isGroupOwner = !groupOwnedByPeerPresence && (groupDetails?.myGroupRole === "owner" || groupDetails?.myGroupRole === "presence");
    const visibleGroupMembers = (groupDetails?.members || []).filter((member) => (
        member.role !== "presence" && String(member.id) !== "anonymous"
    ));
    const normalizedGroupMemberListSearch = groupMemberListSearch.trim().toLowerCase();
    const filteredGroupMembers = normalizedGroupMemberListSearch
        ? visibleGroupMembers.filter((member) => {
            const name = `${member.displayName || ""} ${member.username || ""}`.toLowerCase();
            return name.includes(normalizedGroupMemberListSearch);
        })
        : visibleGroupMembers;
    const visibleGroupMemberCount = groupDetails
        ? Math.min(groupDetails.memberCount || visibleGroupMembers.length, visibleGroupMembers.length)
        : selectedChat?.participantIds?.length || 0;
    const isPresenceAuthority = !groupOwnedByPeerPresence && groupDetails?.myGroupRole === "presence";
    const isNormalGroupOwner = groupDetails?.myGroupRole === "owner";
    const groupSettingRowClass = "bs-group-setting-row flex min-w-0 flex-col gap-1.5 rounded-md border border-neutral-900 bg-neutral-950/75 p-3 text-xs font-semibold text-neutral-300";
    const groupSettingTitleClass = "bs-group-setting-title text-xs font-bold text-neutral-200";
    const groupSettingHintClass = "bs-group-setting-hint text-[11px] font-medium leading-4 text-neutral-500";
    const groupControlClass = "bs-group-control bs-group-native-control h-9 w-full rounded-md border border-neutral-800 bg-black/80 px-2 text-sm text-white outline-none transition focus:border-violet-300/45";
    const groupRulesSectionClass = "bs-group-rule-section rounded-lg border border-neutral-900 bg-neutral-950/55";
    const groupRulesSummaryClass = "bs-group-rule-summary flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-3 text-sm font-bold text-white [&::-webkit-details-marker]:hidden";
    const renderGroupChoiceControl = (
        value: string,
        options: Array<{ value: string; label: string }>,
        onChange: (value: string) => void,
        ariaLabel: string
    ) => (
        <>
            <select
                value={value}
                onChange={(event) => onChange(event.target.value)}
                aria-label={ariaLabel}
                className={groupControlClass}
            >
                {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <div className="bs-group-choice-control" role="radiogroup" aria-label={ariaLabel}>
                {options.map(option => {
                    const selected = option.value === value;
                    return (
                        <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            className={selected ? "is-selected" : ""}
                            onClick={() => {
                                if (!selected) onChange(option.value);
                            }}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </>
    );

    const syncConversation = (conversation?: ConversationItem | null) => {
        if (!conversation) return;
        const mapped = conversationToChat(conversation);
        setChats(prev => {
            const exists = prev.some(chat => String(chat.id) === String(mapped.id));
            if (!exists) return [mapped, ...prev];
            return prev.map(chat => String(chat.id) === String(mapped.id)
                ? { ...chat, ...mapped, messages: chat.messages.length ? chat.messages : mapped.messages }
                : chat
            );
        });
    };

    const showGroupMessage = (message: string) => {
        setGroupToast(message);
        window.setTimeout(() => setGroupToast(""), 2200);
    };

    const applyGroupResponse = (group: GroupDetails, conversation?: ConversationItem | null, message = "Group updated") => {
        setGroupDetails(group);
        setGroupNameDraft(group.groupName || "");
        setGroupDescriptionDraft(group.groupDescription || "");
        syncConversation(conversation);
        showGroupMessage(message);
    };

    const loadGroupPanel = async (conversationId: string, silent = false) => {
        if (!silent) {
            setGroupLoading(true);
            setGroupError("");
        }
        try {
            const details = await fetchGroupDetails(conversationId);
            setGroupDetails(details);
            setGroupNameDraft(details.groupName || "");
            setGroupDescriptionDraft(details.groupDescription || "");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not load this group.");
        } finally {
            if (!silent) setGroupLoading(false);
        }
    };

    const openGroupPanel = (returnTarget: "chat" | "menu" = "chat") => {
        if (!selectedChat?.isGroup || typeof selectedChat.id !== "string") return;
        setGroupInfoReturnTarget(returnTarget);
        setShowGroupPanel(true);
        setMenuOpen(false);
        setShowExportMenu(false);
        setGroupMemberSearch("");
        setGroupMemberListSearch("");
        setGroupMemberListSearchOpen(false);
        loadGroupPanel(selectedChat.id);
    };

    const closeGroupPanel = () => {
        const returnToMenu = groupInfoReturnTarget === "menu";
        setShowGroupPanel(false);
        setGroupError("");
        setGroupToast("");
        setGroupAction("");
        setGroupMemberSearch("");
        setGroupMemberResults([]);
        setGroupMemberListSearch("");
        setGroupMemberListSearchOpen(false);
        setGroupInfoReturnTarget("chat");
        if (returnToMenu) setMenuOpen(true);
    };

    useEffect(() => {
        if (!showGroupPanel || !groupMemberSearch.trim() || groupMemberSearch.trim().length < 2) {
            setGroupMemberResults([]);
            setGroupMemberSearching(false);
            return;
        }

        let alive = true;
        setGroupMemberSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const results = await searchUsers(groupMemberSearch);
                if (!alive) return;
                const existing = new Set(groupMemberIdsKey.split("|").filter(Boolean));
                setGroupMemberResults(results.filter(user => !existing.has(String(user.id))));
            } catch (err) {
                if (alive) {
                    setGroupMemberResults([]);
                    setGroupError(betaSafeErrorMessage(err, "Could not search users."));
                }
            } finally {
                if (alive) setGroupMemberSearching(false);
            }
        }, 250);

        return () => {
            alive = false;
            window.clearTimeout(timer);
        };
    }, [groupMemberSearch, groupMemberIdsKey, showGroupPanel]);

    const handleSaveGroupInfo = async () => {
        if (!groupDetails || groupAction) return;
        setGroupAction("save-info");
        setGroupError("");
        try {
            const result = await updateGroupDetails(groupDetails.id, {
                groupName: groupNameDraft,
                groupDescription: groupDescriptionDraft,
                groupAvatar: groupDetails.groupAvatar,
            });
            applyGroupResponse(result.group, result.conversation, "Group profile saved.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not save group.");
        } finally {
            setGroupAction("");
        }
    };

    const handleAddMemberToGroup = async (user: SearchUserResult) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`add-${user.id}`);
        setGroupError("");
        try {
            const result = await addGroupMembers(groupDetails.id, [user.id]);
            applyGroupResponse(result.group, result.conversation, result.invited ? "Invite sent." : "Member added.");
            setGroupMemberSearch("");
            setGroupMemberResults([]);
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not add member.");
        } finally {
            setGroupAction("");
        }
    };

    const handleRemoveGroupMember = async (member: GroupMember) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`remove-${member.id}`);
        setGroupError("");
        try {
            const result = await removeGroupMemberApi(groupDetails.id, member.id);
            applyGroupResponse(result.group, result.conversation, "Member removed.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not remove member.");
        } finally {
            setGroupAction("");
        }
    };

    const handleGroupRoleChange = async (member: GroupMember, role: "admin" | "member") => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`role-${member.id}`);
        setGroupError("");
        try {
            const result = await updateGroupMemberRole(groupDetails.id, member.id, role);
            applyGroupResponse(result.group, result.conversation, role === "admin" ? "Admin granted." : "Admin removed.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not update role.");
        } finally {
            setGroupAction("");
        }
    };

    const handleTransferGroupOwner = async (member: GroupMember) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`owner-${member.id}`);
        setGroupError("");
        try {
            const result = await transferGroupOwner(groupDetails.id, member.id);
            applyGroupResponse(result.group, result.conversation, "Owner role transferred.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not transfer owner role.");
        } finally {
            setGroupAction("");
        }
    };

    const handleLeaveGroup = async () => {
        if (!selectedChat?.isGroup || typeof selectedChat.id !== "string" || groupAction) return;
        setGroupAction("leave");
        setGroupError("");
        try {
            const result = await leaveGroupConversation(selectedChat.id);
            if (result.conversation) {
                const mapped = conversationToChat(result.conversation);
                setChats(prev => prev.map(chat => String(chat.id) === String(selectedChat.id)
                    ? { ...chat, ...mapped, messages: chat.messages }
                    : chat
                ));
            } else {
                setChats(prev => prev.map(chat => String(chat.id) === String(selectedChat.id)
                    ? { ...chat, hasLeftGroup: true, status: "You left this group" }
                    : chat
                ));
            }
            closeGroupPanel();
        } catch (err) {
            const rawMessage = err instanceof Error ? err.message : "Could not leave group.";
            const message = /transfer.*owner|owner.*before.*leav/i.test(rawMessage)
                ? "Transfer the Ownership before exiting."
                : rawMessage;
            setGroupError(message);
            window.setTimeout(() => {
                setGroupError(current => current === message ? "" : current);
            }, 4200);
        } finally {
            setGroupAction("");
        }
    };

    const handleGroupPermissionChange = async (key: "addMembers" | "editInfo" | "sendMessages", value: string) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`permission-${key}`);
        setGroupError("");
        try {
            const result = await updateGroupPermissions(groupDetails.id, { [key]: value } as Partial<GroupDetails["permissions"]>);
            applyGroupResponse(result.group, result.conversation, "Group permissions updated.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not update permissions.");
        } finally {
            setGroupAction("");
        }
    };

    const handleGroupSettingChange = async (key: keyof GroupDetails["settings"], value: string | boolean | number | null) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`setting-${String(key)}`);
        setGroupError("");
        try {
            const result = await updateGroupPermissions(groupDetails.id, { [key]: value } as Partial<GroupDetails["permissions"] & GroupDetails["settings"]>);
            applyGroupResponse(result.group, result.conversation, "Group settings updated.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not update group settings.");
        } finally {
            setGroupAction("");
        }
    };

    const handleRegenerateGroupCode = async () => {
        if (!groupDetails || groupAction) return;
        setGroupAction("invite-code");
        setGroupError("");
        try {
            const result = await regenerateGroupInviteCode(groupDetails.id, true);
            applyGroupResponse(result.group, result.conversation, "Invite code regenerated.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not regenerate code.");
        } finally {
            setGroupAction("");
        }
    };

    const handleToggleGroupCode = async () => {
        if (!groupDetails || groupAction) return;
        const nextEnabled = !groupDetails.invite?.enabled;
        setGroupAction("invite-code-toggle");
        setGroupError("");
        try {
            const result = await regenerateGroupInviteCode(groupDetails.id, nextEnabled);
            applyGroupResponse(result.group, result.conversation, nextEnabled ? "Invite code enabled." : "Invite code disabled.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not update invite code.");
        } finally {
            setGroupAction("");
        }
    };

    const handleReviewJoinRequest = async (member: { id: string }, action: "approve" | "decline") => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`join-${action}-${member.id}`);
        setGroupError("");
        try {
            const result = await reviewGroupJoinRequest(groupDetails.id, member.id, action);
            applyGroupResponse(result.group, result.conversation, action === "approve" ? "Join request approved." : "Join request declined.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not review request.");
        } finally {
            setGroupAction("");
        }
    };

    const handleToggleRestriction = async (member: GroupMember, muted: boolean) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`mute-${member.id}`);
        setGroupError("");
        try {
            const result = await restrictGroupMember(groupDetails.id, member.id, {
                muted,
                restricted: muted,
                reason: muted ? "Group messaging restricted." : "",
            });
            applyGroupResponse(result.group, result.conversation, muted ? "Member muted." : "Member restored.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not update restriction.");
        } finally {
            setGroupAction("");
        }
    };

    const handleBanGroupMember = async (member: GroupMember) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`ban-${member.id}`);
        setGroupError("");
        try {
            const result = await updateGroupBan(groupDetails.id, member.id, { action: "ban", reason: "Removed by group moderator" });
            applyGroupResponse(result.group, result.conversation, "Member banned.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not ban member.");
        } finally {
            setGroupAction("");
        }
    };

    const handleUnbanGroupUser = async (userId: string) => {
        if (!groupDetails || groupAction) return;
        setGroupAction(`unban-${userId}`);
        setGroupError("");
        try {
            const result = await updateGroupBan(groupDetails.id, userId, { action: "unban" });
            applyGroupResponse(result.group, result.conversation, "User unbanned.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not unban user.");
        } finally {
            setGroupAction("");
        }
    };

    const handleAuthorityControl = async (controlled: boolean, sendMessages?: "everyone" | "admins" | "owner" | "locked") => {
        if (!groupDetails || groupAction) return;
        setGroupAction("authority");
        setGroupError("");
        try {
            const result = await updateGroupAuthority(groupDetails.id, {
                controlled,
                protected: controlled,
                sendMessages,
                reason: controlled ? "Group controls enabled." : "",
            });
            applyGroupResponse(result.group, result.conversation, controlled ? "Group locked." : "Group reopened.");
        } catch (err) {
            setGroupError(err instanceof Error ? err.message : "Could not update group controls.");
        } finally {
            setGroupAction("");
        }
    };

    const handleJoinByCode = async () => {
        const code = groupCodeInput.trim();
        if (!code || groupAction) return;
        setGroupAction("join-code");
        setGroupCodeStatus("");
        try {
            const result = await joinGroupByCode(code);
            if (result.conversation) {
                syncConversation(result.conversation);
                setSelectedChatId(result.conversation.id);
                setActiveTab("chats");
            }
            setGroupCodeInput("");
            setGroupCodeStatus(
                result.status === "joined"
                    ? "Group joined."
                    : result.status === "already_member"
                        ? "You are already in that group."
                        : "Join request sent."
            );
            loadRequests();
        } catch (err) {
            setGroupCodeStatus(err instanceof Error ? err.message : "Could not use that code.");
        } finally {
            setGroupAction("");
        }
    };

    // ── Auto-scroll on new messages ──
    useEffect(() => {
        if (loadingOlderRef.current) {
            loadingOlderRef.current = false;
            return;
        }
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [selectedChat?.messages?.length]);

    // ✅ ADDED: Filter chats and users based on search (pinned first)
    const getChatActivityTime = (chat: Chat) => {
        const latestMessageTime = chat.messages.reduce((latest, message) => {
            const value = message.createdAt ? new Date(message.createdAt).getTime() : 0;
            return Number.isFinite(value) && value > latest ? value : latest;
        }, 0);
        return chat.lastMessageAt || latestMessageTime || chat.updatedAt || 0;
    };

    const sortChatsByInboxActivity = (items: Chat[]) =>
        [...items].sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return getChatActivityTime(b) - getChatActivityTime(a);
        });

    const normalizedSearch = searchQuery.toLowerCase();
    const chatMatchesSearch = (chat: Chat) => (
        chat.name.toLowerCase().includes(normalizedSearch) ||
        chat.messages.some(msg => msg.text.toLowerCase().includes(normalizedSearch))
    );
    const isInboxConversation = (chat: Chat) => {
        if (chat.isGroup) return true;
        const requestStatus = chat.requestStatus || "accepted";
        if (requestStatus === "accepted") return true;
        if ((requestStatus === "pending" || requestStatus === "draft") && String(chat.requestedBy || "") === String(currentUser?.id || "")) {
            return true;
        }
        return false;
    };
    const searchableChats = chats.filter(chat => !chat.isBlockedByMe && isInboxConversation(chat) && chatMatchesSearch(chat));
    const normalInboxChats = sortChatsByInboxActivity(searchableChats.filter(chat => !chat.isArchived && !chat.isRecycled));
    const archivedChats = sortChatsByInboxActivity(searchableChats.filter(chat => Boolean(chat.isArchived) && !chat.isRecycled));
    const recycledChats = sortChatsByInboxActivity(searchableChats.filter(chat => Boolean(chat.isRecycled)));
    const filteredChats = normalInboxChats;

    useEffect(() => {
        presenceAllowedUserIdsRef.current = new Set(
            chats
                .filter(chat => !chat.isBlocked && !chat.isGroup && (chat.requestStatus || "accepted") === "accepted")
                .map(chat => chat.otherUserId)
                .filter(Boolean)
                .map(String)
        );
        liveChatSignalAllowedRef.current = new Set(
            chats
                .filter(chat => canUseLiveChatSignals(chat))
                .map(chat => String(chat.id))
        );
    }, [chats]);

    const unreadChats = filteredChats.filter(chat => chat.unreadCount > 0);
    const pinnedChats = filteredChats.filter(chat => chat.isPinned);
    const privateChats = filteredChats.filter(chat => isChatIncognito(chat.id));
    const activeNowChats = filteredChats.filter(chat => !isChatIncognito(chat.id) && canUseLiveChatSignals(chat) && getChatPresence(chat)?.online);
    const visibleChats = (() => {
        if (inboxFilter === "archive") return archivedChats;
        if (inboxFilter === "recycle") return recycledChats;
        return filteredChats.filter(chat => {
        if (inboxFilter === "active") return !isChatIncognito(chat.id) && canUseLiveChatSignals(chat) && Boolean(getChatPresence(chat)?.online);
        if (inboxFilter === "unread") return chat.unreadCount > 0;
        if (inboxFilter === "pinned") return chat.isPinned;
        if (inboxFilter === "private") return isChatIncognito(chat.id);
        return true;
        });
    })();
    const selectedBulkChats = chats.filter(chat => selectedChatIds.has(String(chat.id)));
    const chatSelectionMode = activeTab === "chats" && selectedChatIds.size > 0;
    const bulkPinShouldPin = selectedBulkChats.some(chat => !chat.isPinned);

    const inboxFilters: { id: InboxFilter; label: string; count: number }[] = [
        { id: "all", label: "All", count: filteredChats.length + supportCounts.all },
        { id: "active", label: "Active now", count: activeNowChats.length },
        { id: "unread", label: "Unread", count: unreadChats.length + supportCounts.unread },
        { id: "pinned", label: "Pinned", count: pinnedChats.length },
        { id: "private", label: "Private", count: privateChats.length },
        { id: "archive", label: "Archive", count: archivedChats.length },
        { id: "recycle", label: "Recycle Bin", count: recycledChats.length },
    ];

    const newChatRecentPeople = chats
        .filter(chat => !chat.isBlockedByMe && isInboxConversation(chat) && !chat.isArchived && !chat.isRecycled && chat.otherUserId)
        .sort((a, b) => getChatActivityTime(b) - getChatActivityTime(a))
        .slice(0, 5);

    const filteredRequests = requests.filter(req =>
        req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.note?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredBlocked = blocked.filter(blockedUser =>
        blockedUser.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        blockedUser.reason?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ✅ ADDED: Search handler
    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    // New Chat modal handlers
    useEffect(() => {
        setNewChatActiveIndex(0);
        if (!newChatSearch.trim() || newChatSearch.trim().length < 2) {
            setNewChatResults([]);
            setNewChatLoading(false);
            return;
        }
        const timer = setTimeout(async () => {
            setNewChatLoading(true);
            try {
                const results = await searchUsers(newChatSearch);
                setNewChatResults(results);
            } catch (err) {
                setNewChatResults([]);
                showMessageToast(betaSafeErrorMessage(err, "Could not search users."));
            } finally {
                setNewChatLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [newChatSearch]);

    useEffect(() => {
        if (!forwardMessage) return;
        if (!forwardSearch.trim() || forwardSearch.trim().length < 2) {
            setForwardUserResults([]);
            setForwardSearching(false);
            return;
        }
        const timer = window.setTimeout(async () => {
            setForwardSearching(true);
            try {
                const results = await searchUsers(forwardSearch);
                setForwardUserResults(results.filter(user => String(user.id) !== String(currentUser?.id)));
            } catch (err) {
                setForwardUserResults([]);
                showMessageToast(betaSafeErrorMessage(err, "Could not search users."));
            } finally {
                setForwardSearching(false);
            }
        }, 300);
        return () => window.clearTimeout(timer);
    }, [currentUser?.id, forwardMessage, forwardSearch]);

    const findExistingChatForUser = (userId: string) =>
        chats.find(chat => String(chat.otherUserId) === String(userId) && !chat.isBlocked);

    const findAnyExistingChatForUser = (userId: string) =>
        chats.find(chat => String(chat.otherUserId) === String(userId));

    const groupRoleCanSend = (chat: Chat) => {
        if (!chat.isGroup) return true;
        if (chat.myGroupRole === "presence") return true;
        if (!chat.myGroupRole) return false;
        const mode = chat.groupSendPermission || "everyone";
        if (mode === "locked") return false;
        if (mode === "owner") return chat.myGroupRole === "owner";
        if (mode === "admins") return chat.myGroupRole === "owner" || chat.myGroupRole === "admin";
        return true;
    };

    const getForwardBlockReason = (chat: Chat | null | undefined) => {
        if (!chat) return "No accepted chat";
        if (chat.isBlocked) return "Messaging unavailable";
        if (chat.hasLeftGroup) return "You left this group";
        const requestStatus = chat.requestStatus || "accepted";
        if (requestStatus === "pending" || requestStatus === "draft") return "Request pending";
        if (requestStatus === "declined") return "Request declined";
        if (chat.isGroup && !groupRoleCanSend(chat)) return "No send permission";
        if (!chat.isGroup && !chat.otherUserId) return "Messaging unavailable";
        return "";
    };

    const canForwardToChat = (chat: Chat | null | undefined) => !getForwardBlockReason(chat);

    const openExistingChat = (chatId: string | number) => {
        setSelectedChatId(chatId);
        setActiveTab("chats");
        setMenuOpen(false);
        setShowChatSearch(false);
        setSearchInChat("");
        setChats(prev => prev.map(chat =>
            String(chat.id) === String(chatId) ? { ...chat, unreadCount: 0 } : chat
        ));
        closeNewChatModal();
    };

    const handleStartChat = async (user: SearchUserResult) => {
        const existing = findExistingChatForUser(user.id);
        if (existing) {
            openExistingChat(existing.id);
            return;
        }

        setNewChatCreating(user.id);
        try {
            const conv = await createConversation(user.id);
            const displayName = user.displayName || user.username;
            const avatar = user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=222&color=fff`;

            // Add to chats if not already present
            setChats(prev => {
                const exists = prev.find(c => c.id === conv.id);
                if (exists) return prev;
                const newChat: Chat = {
                    id: conv.id,
                    otherUserId: user.id,
                    name: displayName,
                    avatar,
                    status: conv.requestStatus === "pending" ? "Request pending" : "",
                    requestStatus: conv.requestStatus || "accepted",
                    requestedBy: conv.requestedBy || null,
                    isEncrypted: true,
                    pinnedMessages: [],
                    unreadCount: 0,
                    lastMessageAt: Date.now(),
                    isMuted: false,
                    isPinned: false,
                    isBlocked: false,
                    isBlockedByMe: false,
                    messages: [],
                };
                return [newChat, ...prev];
            });

            setSelectedChatId(conv.id);
            setActiveTab("chats");
            closeNewChatModal();
        } catch (err) {
            console.error("Failed to create conversation:", err);
            setNewGroupError(err instanceof Error ? err.message : "Could not start this conversation.");
        } finally {
            setNewChatCreating(null);
        }
    };

    // ✅ New Functions
    const addGroupMember = (user: SearchUserResult) => {
        setNewGroupError("");
        setNewGroupMembers((current) => {
            if (current.some((member) => String(member.id) === String(user.id))) return current;
            return [...current, user].slice(0, 24);
        });
    };

    const removeGroupMember = (userId: string) => {
        setNewGroupMembers((current) => current.filter((member) => String(member.id) !== String(userId)));
    };

    const handleCreateGroup = async () => {
        if (newGroupMembers.length < 2) {
            setNewGroupError("Pick at least two people for a group.");
            return;
        }

        const fallbackName = newGroupMembers
            .slice(0, 3)
            .map((member) => member.displayName || member.username)
            .join(", ");
        const groupName = (newGroupName.trim() || fallbackName || "Group conversation").slice(0, 60);

        setNewGroupCreating(true);
        setNewGroupError("");
        try {
            const conv = await createGroupConversation(newGroupMembers.map((member) => member.id), groupName);
            const participantIds = conv.participants?.map((participant) => String(participant.id)) || [];
            const visibleMemberCount = Math.max(0, conv.memberCount ?? participantIds.length);
            const avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(groupName)}&background=14313b&color=67e8f9`;

            setChats((prev) => {
                const exists = prev.find((chat) => String(chat.id) === String(conv.id));
                if (exists) return prev;
                const newChat: Chat = {
                    id: conv.id,
                    participantIds,
                    isGroup: true,
                    groupOwnerId: conv.groupOwner || currentUser?.id,
                    groupAdminIds: conv.groupAdmins || (currentUser?.id ? [currentUser.id] : []),
                    myGroupRole: conv.myGroupRole || "owner",
                    groupSendPermission: "everyone",
                    name: conv.groupName || groupName,
                    avatar,
                    status: `${visibleMemberCount} ${visibleMemberCount === 1 ? "member" : "members"}`,
                    isEncrypted: true,
                    pinnedMessages: [],
                    unreadCount: 0,
                    lastMessageAt: Date.now(),
                    isMuted: false,
                    isPinned: false,
                    isBlocked: false,
                    isBlockedByMe: false,
                    messages: [],
                };
                return [newChat, ...prev];
            });

            setSelectedChatId(conv.id);
            setActiveTab("chats");
            closeNewChatModal();
        } catch (err: any) {
            setNewGroupError(err?.message || "Could not create group.");
        } finally {
            setNewGroupCreating(false);
        }
    };

    const formatMessageTime = (value: string | Date | number = new Date()) =>
        new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });

    const mapServerMessage = (payload: SocketMessagePayload, fallbackClientMessageId?: string): ChatMessageItem => {
        const fromMe = String(payload.sender.id) === String(currentUser?.id);
        const senderReadByOther = Boolean(payload.readBy?.some(entry => String(entry.user) !== String(payload.sender.id)));
        return {
            id: String(payload.id),
            clientMessageId: payload.clientMessageId || fallbackClientMessageId,
            from: fromMe ? "me" : (payload.sender.displayName || payload.sender.username),
            text: payload.content,
            time: formatMessageTime(payload.createdAt),
            status: fromMe ? (senderReadByOther ? "read" : "sent") : "delivered",
            replyTo: payload.replyTo || null,
            forwarded: Boolean(payload.forwarded),
            reactions: payload.reactions || [],
            createdAt: payload.createdAt,
            editedAt: payload.editedAt || null,
            ...(payload.isIncognito ? { isIncognito: true } : {}),
        };
    };

    const reconcileServerMessage = (
        conversationId: string | number,
        payload: SocketMessagePayload,
        fallbackClientMessageId?: string
    ) => {
        const clientMessageId = payload.clientMessageId || fallbackClientMessageId;
        if (clientMessageId) confirmedClientMessageIdsRef.current.add(clientMessageId);
        const mapped = mapServerMessage(payload, clientMessageId);
        const createdAt = new Date(payload.createdAt).getTime();

        setChats(prev => prev.map(chat => {
            if (String(chat.id) !== String(conversationId)) return chat;
            const messages = chat.messages.filter(message =>
                String(message.id) !== String(payload.id) &&
                (!clientMessageId || message.clientMessageId !== clientMessageId)
            );
            return {
                ...chat,
                messages: [...messages, mapped],
                lastMessageAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
            };
        }));
    };

    const upsertLocalMessage = (
        chatId: string | number,
        message: ChatMessageItem,
        existingId?: string | number
    ) => {
        setChats(prev => prev.map(chat => {
            if (String(chat.id) !== String(chatId)) return chat;
            const existingIndex = chat.messages.findIndex(existing =>
                (existingId !== undefined && String(existing.id) === String(existingId)) ||
                (message.clientMessageId && existing.clientMessageId === message.clientMessageId)
            );
            const nextMessages = [...chat.messages];
            if (existingIndex >= 0) nextMessages[existingIndex] = message;
            else nextMessages.push(message);
            return { ...chat, messages: nextMessages, lastMessageAt: Date.now() };
        }));
    };

    const markLocalMessageStatus = (
        chatId: string | number,
        clientMessageId: string,
        status: ChatMessageItem["status"]
    ) => {
        if (status === "failed" && confirmedClientMessageIdsRef.current.has(clientMessageId)) return;
        setChats(prev => prev.map(chat => (
            String(chat.id) === String(chatId)
                ? {
                    ...chat,
                    messages: chat.messages.map(message =>
                        message.clientMessageId === clientMessageId
                            ? { ...message, status }
                            : message
                    ),
                }
                : chat
        )));
    };

    const markConversationRequestState = (
        chatId: string | number,
        requestStatus?: "accepted" | "draft" | "pending" | "declined",
        requestedBy?: string | null
    ) => {
        if (!requestStatus) return;
        setChats(prev => prev.map(chat => (
            String(chat.id) === String(chatId)
                ? {
                    ...chat,
                    requestStatus,
                    requestedBy: requestedBy ?? chat.requestedBy ?? null,
                    status: requestStatus === "pending" && String(requestedBy || chat.requestedBy) === String(currentUser?.id)
                        ? "Request pending"
                        : requestStatus === "pending"
                            ? "Message request"
                            : chat.status,
                }
                : chat
        )));
    };

    const emitMessageWithAck = (
        socket: ReturnType<typeof getSocket>,
        payload: {
            conversationId: string;
            content: string;
            messageType: string;
            isIncognito: boolean;
            clientMessageId: string;
            replyToMessageId?: string | number | null;
            forwarded?: boolean;
        }
    ): Promise<SocketSendAck> =>
        new Promise(resolve => {
            if (!socket?.connected) {
                resolve({ success: false, clientMessageId: payload.clientMessageId });
                return;
            }

            let settled = false;
            const timer = window.setTimeout(() => {
                if (settled) return;
                settled = true;
                resolve({
                    success: confirmedClientMessageIdsRef.current.has(payload.clientMessageId),
                    clientMessageId: payload.clientMessageId,
                });
            }, SEND_ACK_TIMEOUT_MS);

            socket.emit('message:send', payload, (ack: SocketSendAck) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timer);
                resolve(ack || { success: false, clientMessageId: payload.clientMessageId });
            });
        });

    const replyMetadataFromMessage = (message: ChatMessageItem): ChatMessageItem["replyTo"] => ({
        id: message.id,
        senderName: message.from === "me" ? "You" : message.from,
        content: message.text,
        unavailable: false,
    });

    const sendTextMessage = async (
        chat: Chat,
        text: string,
        existingClientMessageId?: string,
        existingId?: string | number,
        replyTo: ChatMessageItem["replyTo"] | null = null,
        forwarded = false
    ) => {
        if (!text.trim() || typeof chat.id !== "string") return false;
        if (!chat.isGroup && chat.isBlocked) {
            setMessageActionError("You can not message this user.");
            return false;
        }
        if (!chat.isGroup && chat.requestStatus === "pending") {
            setMessageActionError(
                String(chat.requestedBy) === String(currentUser?.id)
                    ? "Message request pending. You can send more after they accept."
                    : "Accept the request before replying."
            );
            return false;
        }
        if (!chat.isGroup && chat.requestStatus === "declined") {
            setMessageActionError("This message request was declined.");
            return false;
        }

        const socket = getSocket();
        const chatIsIncognito = isChatIncognito(chat.id);
        const clientMessageId = existingClientMessageId || makeClientMessageId();
        const createdAt = new Date().toISOString();
        const localMessage: ChatMessageItem = {
            id: existingId || `${chatIsIncognito ? "incog" : "temp"}-${clientMessageId}`,
            clientMessageId,
            from: "me",
            text: text.trim(),
            time: formatMessageTime(createdAt),
            status: "sending",
            replyTo: replyTo || null,
            forwarded,
            createdAt,
            ...(chatIsIncognito ? { isIncognito: true } : {}),
        };

        upsertLocalMessage(chat.id, localMessage, existingId);

        if (socket?.connected) {
            const ack = await emitMessageWithAck(socket, {
                conversationId: chat.id,
                content: text.trim(),
                messageType: 'text',
                isIncognito: chatIsIncognito,
                clientMessageId,
                replyToMessageId: replyTo?.unavailable ? null : replyTo?.id ?? null,
                forwarded,
            });

            if (ack.success && ack.message) {
                reconcileServerMessage(chat.id, ack.message, clientMessageId);
                markConversationRequestState(chat.id, ack.requestStatus, ack.requestedBy);
                return true;
            }
            if (ack.success) {
                markLocalMessageStatus(chat.id, clientMessageId, "sent");
                markConversationRequestState(chat.id, ack.requestStatus, ack.requestedBy);
                return true;
            }
            if (ack.rateLimited) {
                markLocalMessageStatus(chat.id, clientMessageId, "failed");
                setMessageActionError("Too many attempts. Please wait a moment.");
                return false;
            }
        }

        if (chatIsIncognito) {
            markLocalMessageStatus(chat.id, clientMessageId, "failed");
            setMessageActionError("Incognito messages need an active socket connection. Try again when connected.");
            return false;
        }

        try {
            const sent = await sendMessageApi(chat.id, text.trim(), 'text', clientMessageId, replyTo?.unavailable ? null : replyTo?.id ?? null, forwarded);
            markConversationRequestState(chat.id, sent.requestStatus, sent.requestedBy);
            reconcileServerMessage(chat.id, {
                id: sent.id,
                clientMessageId: sent.clientMessageId || clientMessageId,
                conversationId: chat.id,
                sender: sent.sender,
                content: sent.content,
                createdAt: sent.createdAt,
                editedAt: sent.editedAt || null,
                replyTo: sent.replyTo || null,
                forwarded: Boolean(sent.forwarded),
                reactions: sent.reactions || [],
                readBy: sent.readBy || [],
            }, clientMessageId);
            return true;
        } catch (err) {
            console.error("Send message failed:", err);
            markLocalMessageStatus(chat.id, clientMessageId, "failed");
            setMessageActionError(betaSafeErrorMessage(err, "Message could not be delivered."));
            return false;
        }
    };

    const handleSend = () => {
        if (!newMessage.trim() || !selectedChat) return;

        const text = newMessage.trim();
        const socket = getSocket();
        const chatIsIncognito = isChatIncognito(selectedChat.id);

        if (!chatIsIncognito && socket && typeof selectedChat.id === 'string' && canUseLiveChatSignals(selectedChat)) {
            socket.emit('typing:stop', { conversationId: selectedChat.id });
        }
        if (typingTimerRef.current) {
            clearTimeout(typingTimerRef.current);
            typingTimerRef.current = null;
        }

        const replyTo = replyDraft ? replyMetadataFromMessage(replyDraft) : null;
        setNewMessage("");
        setReplyDraft(null);
        void sendTextMessage(selectedChat, text, undefined, undefined, replyTo);
    };

    const isPersistedMessageId = (messageId: string | number) => {
        const value = String(messageId);
        return Boolean(value) && !value.startsWith("temp-") && !value.startsWith("incog-");
    };

    const beginEditMessage = (message: ChatMessageItem) => {
        if (message.isIncognito || message.status === "failed" || !isPersistedMessageId(message.id)) return;
        setMessageActionError("");
        setEditingMessageId(message.id);
        setEditingMessageText(message.text);
    };

    const cancelEditMessage = () => {
        setEditingMessageId(null);
        setEditingMessageText("");
    };

    const saveEditedMessage = async (message: ChatMessageItem) => {
        if (!selectedChat || !isPersistedMessageId(message.id)) return;
        const nextText = editingMessageText.trim();
        if (!nextText) {
            setMessageActionError("Message cannot be empty.");
            return;
        }
        if (nextText === message.text) {
            cancelEditMessage();
            return;
        }

        setMessageActionError("");
        try {
            const updated = await editMessageApi(String(message.id), nextText);
            setChats(prev => prev.map(chat =>
                String(chat.id) === String(selectedChat.id)
                    ? {
                        ...chat,
                        messages: chat.messages.map(item =>
                            String(item.id) === String(message.id)
                                ? {
                                    ...item,
                                    text: updated.content,
                                    editedAt: updated.editedAt || new Date().toISOString(),
                                }
                                : item
                        ),
                    }
                    : chat
            ));
            cancelEditMessage();
        } catch (err) {
            setMessageActionError(err instanceof Error ? err.message : "Could not edit message.");
        }
    };

    const deleteChatMessage = async (message: ChatMessageItem) => {
        if (!selectedChat || !isPersistedMessageId(message.id)) return;
        setMessageActionError("");
        try {
            await deleteMessageApi(String(message.id));
            setChats(prev => prev.map(chat =>
                String(chat.id) === String(selectedChat.id)
                    ? { ...chat, messages: chat.messages.filter(item => String(item.id) !== String(message.id)) }
                    : chat
            ));
        } catch (err) {
            setMessageActionError(err instanceof Error ? err.message : "Could not delete message.");
        }
    };

    const shortMessageText = (text = "", max = 96) => {
        const clean = String(text || "").replace(/\s+/g, " ").trim();
        return clean.length > max ? `${clean.slice(0, max - 1)}...` : clean;
    };

    const showMessageToast = (message: string) => {
        setMessageToast(message);
        if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = window.setTimeout(() => {
            setMessageToast("");
            toastTimerRef.current = null;
        }, 1800);
    };

    const betaSafeErrorMessage = (err: unknown, fallback: string) => {
        const message = err instanceof Error ? err.message : "";
        if (/too many|429|rate/i.test(message)) return "Too many attempts. Please wait a moment.";
        return message || fallback;
    };

    const beginReplyToMessage = (message: ChatMessageItem) => {
        setReplyDraft(message);
        closeMessageOverlays();
        setTimeout(() => messageInputRef.current?.focus(), 0);
    };

    const copyChatMessage = async (message: ChatMessageItem) => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
            await navigator.clipboard?.writeText(message.text);
            setCopiedMessageId(message.id);
            showMessageToast("Copied");
            window.setTimeout(() => {
                setCopiedMessageId(current => String(current) === String(message.id) ? null : current);
            }, 1400);
        } catch {
            setMessageActionError("Could not copy message.");
        } finally {
            closeMessageActionMenu();
        }
    };

    const beginForwardMessage = (message: ChatMessageItem) => {
        setForwardMessage(message);
        setForwardTargets([]);
        setForwardSearch("");
        setForwardUserResults([]);
        closeMessageOverlays();
    };

    const closeForwardPicker = () => {
        setForwardMessage(null);
        setForwardTargets([]);
        setForwardSearch("");
        setForwardUserResults([]);
        setForwardSending(false);
    };

    const forwardTargetKey = (target: ForwardTarget) => `${target.kind}:${target.id}`;

    const chatToForwardTarget = (chat: Chat): ForwardTarget => ({
        kind: "chat",
        id: String(chat.id),
        chatId: chat.id,
        name: chat.name,
        avatar: chat.avatar,
        isGroup: Boolean(chat.isGroup),
        disabledReason: getForwardBlockReason(chat) || undefined,
    });

    const userToForwardTarget = (user: SearchUserResult): ForwardTarget => {
        const existing = findAnyExistingChatForUser(user.id);
        if (existing) return chatToForwardTarget(existing);
        const name = user.displayName || user.username;
        return {
            kind: "user",
            id: String(user.id),
            name,
            avatar: user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=222&color=fff`,
            disabledReason: "No accepted chat",
        };
    };

    const isForwardTargetSelected = (target: ForwardTarget) =>
        forwardTargets.some(item => forwardTargetKey(item) === forwardTargetKey(target));

    const toggleForwardTarget = (target: ForwardTarget) => {
        if (target.disabledReason) return;
        setForwardTargets(prev => {
            const key = forwardTargetKey(target);
            return prev.some(item => forwardTargetKey(item) === key)
                ? prev.filter(item => forwardTargetKey(item) !== key)
                : [...prev, target];
        });
    };

    const removeForwardTarget = (target: ForwardTarget) => {
        const key = forwardTargetKey(target);
        setForwardTargets(prev => prev.filter(item => forwardTargetKey(item) !== key));
    };

    const resolveForwardTargetChat = async (target: ForwardTarget): Promise<Chat | null> => {
        if (target.disabledReason) return null;
        if (target.kind === "chat") {
            const chat = chats.find(chat => String(chat.id) === String(target.chatId || target.id)) || null;
            return canForwardToChat(chat) ? chat : null;
        }

        const existing = findAnyExistingChatForUser(target.id);
        return canForwardToChat(existing) ? existing || null : null;
    };

    const sendForwardedMessage = async () => {
        if (!forwardMessage || !forwardTargets.length || forwardSending) return;
        setForwardSending(true);
        setMessageActionError("");
        try {
            let sentCount = 0;
            for (const target of forwardTargets) {
                const chat = await resolveForwardTargetChat(target);
                if (chat) {
                    const sent = await sendTextMessage(chat, forwardMessage.text, undefined, undefined, null, true);
                    if (sent) sentCount += 1;
                }
            }
            if (!sentCount) throw new Error("Could not forward message.");
            showMessageToast(`Forwarded to ${sentCount} ${sentCount === 1 ? "chat" : "chats"}`);
            closeForwardPicker();
        } catch (err) {
            showMessageToast("Could not forward message");
            setMessageActionError(betaSafeErrorMessage(err, "Could not forward message."));
            setForwardSending(false);
        }
    };

    const findRenderedMessageElement = (messageId: string | number) => {
        const key = String(messageId);
        return messageRefs.current[key] || document.getElementById(`message-${key}`);
    };

    const waitForMessageRender = () =>
        new Promise<void>(resolve => {
            window.requestAnimationFrame(() => window.requestAnimationFrame(() => resolve()));
        });

    const highlightMessageElement = (messageId: string | number, element: HTMLElement) => {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedMessageId(messageId);
        if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = window.setTimeout(() => {
            setHighlightedMessageId(current => String(current) === String(messageId) ? null : current);
            highlightTimerRef.current = null;
        }, 1700);
    };

    const scrollToMessage = async (messageId: string | number) => {
        let element = findRenderedMessageElement(messageId);
        if (!element && selectedChat?.messages.some(message => String(message.id) === String(messageId))) {
            setSearchInChat("");
            await waitForMessageRender();
            element = findRenderedMessageElement(messageId);
        }
        if (!element && selectedChatId && typeof selectedChatId === "string") {
            const chatKey = String(selectedChatId);
            let nextPage = (messagePageByChat[chatKey] || 1) + 1;
            let hasMore = Boolean(messageHasMoreByChat[chatKey]);
            let attempts = 0;
            while (!element && hasMore && attempts < 5) {
                hasMore = await loadConversationMessages(chatKey, nextPage, true);
                nextPage += 1;
                attempts += 1;
                await waitForMessageRender();
                element = findRenderedMessageElement(messageId);
            }
        }
        if (!element) {
            showMessageToast("Original message unavailable");
            return;
        }
        highlightMessageElement(messageId, element);
    };

    const retryMessage = async (message: ChatMessageItem) => {
        if (!selectedChat || message.from !== "me") return;
        const retryKey = message.clientMessageId || String(message.id);
        if (retryingMessageIds.has(retryKey)) return;
        setMessageActionError("");
        setRetryingMessageIds(prev => new Set(prev).add(retryKey));
        try {
            await sendTextMessage(
                selectedChat,
                message.text,
                message.clientMessageId || makeClientMessageId(),
                message.id,
                message.replyTo || null,
                Boolean(message.forwarded)
            );
        } catch (err) {
            setMessageActionError(err instanceof Error ? err.message : "Retry failed.");
        } finally {
            setRetryingMessageIds(prev => {
                const next = new Set(prev);
                next.delete(retryKey);
                return next;
            });
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const startChatWithOnlineUser = (user: OnlineUser) => {
        setChats((prev) => {
            const exists = prev.find((c) => c.id === user.id);
            if (exists) return prev;

            const time = new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            });
            const newChat: Chat = {
                id: user.id,
                otherUserId: String(user.id),
                name: user.name,
                avatar: user.avatar,
                status: "online",
                isEncrypted: true,
                pinnedMessages: [],
                unreadCount: 0,
                lastMessageAt: Date.now(),
                isMuted: false,
                isPinned: false,
                isBlocked: false,
                isBlockedByMe: false,
                messages: [{
                    from: "me",
                    text: `Hi ${user.name}! 👋`,
                    time,
                    status: 'sent',
                    id: Date.now()
                }],
            };
            return [newChat, ...prev];
        });

        setActiveTab("chats");
        setSelectedChatId(user.id);

        setPresenceMap((prev) => ({
            ...prev,
            [user.id]: { online: true, lastSeen: Date.now() },
        }));
    };

    const openMessageRequest = (req: FriendRequest) => {
        if (req.kind !== "message" || !req.conversationId) return;
        const conversation = requestConversationByIdRef.current.get(String(req.conversationId));
        if (conversation) {
            syncConversation(conversation);
        }
        setSelectedChatId(req.conversationId);
        setActiveTab("chats");
        setMenuOpen(false);
        setShowChatSearch(false);
        setSearchInChat("");
    };

    const acceptRequest = async (req: FriendRequest) => {
        try {
            if (req.kind === "message" && req.conversationId) {
                const result = await respondMessageRequest(req.conversationId, "accept");
                publishNotificationUpdate(result.unreadCount);
                const conversations = await fetchConversations();
                setChats(conversations.map(conversationToChat));
                setRequests((prev) => prev.filter((r) => r.id !== req.id));
                publishMessageRequestUpdate();
                setSelectedChatId(req.conversationId);
                setActiveTab("chats");
                return;
            }
            if (req.kind === "group" && req.conversationId) {
                const result = await respondGroupInvite(req.conversationId, "accept");
                if (result.conversation) {
                    syncConversation(result.conversation);
                    setSelectedChatId(result.conversation.id);
                    setActiveTab("chats");
                }
                setRequests((prev) => prev.filter((r) => r.id !== req.id));
                publishMessageRequestUpdate();
                return;
            }
        } catch (err) {
            console.error("Accept request failed:", err);
            return;
        }

        const newChat: Chat = {
            id: req.id,
            otherUserId: String(req.id),
            name: req.name,
            avatar: req.avatar,
            status: "new conversation",
            isEncrypted: true,
            pinnedMessages: [],
            unreadCount: 0,
            lastMessageAt: Date.now(),
            isMuted: false,
            isPinned: false,
            isBlocked: false,
            isBlockedByMe: false,
            messages: [
                {
                    from: req.name,
                    text: req.note || "👋",
                    time: new Date().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false,
                    }),
                    status: 'read',
                    id: Date.now()
                },
            ],
        };
        setChats((prev) => [newChat, ...prev]);
        setRequests((prev) => prev.filter((r) => r.id !== req.id));
        publishMessageRequestUpdate();
        setActiveTab("chats");
        setSelectedChatId(req.id);
        setPresenceMap((prev) => ({
            ...prev,
            [req.id]: { online: false, lastSeen: Date.now() },
        }));
    };

    const declineRequest = async (req: FriendRequest) => {
        try {
            if (req.kind === "message" && req.conversationId) {
                const result = await respondMessageRequest(req.conversationId, "decline");
                publishNotificationUpdate(result.unreadCount);
            } else if (req.kind === "group" && req.conversationId) {
                await respondGroupInvite(req.conversationId, "decline");
            }
            setRequests((prev) => prev.filter((r) => r.id !== req.id));
            publishMessageRequestUpdate();
        } catch (err) {
            console.error("Decline request failed:", err);
        }
    };

    const respondToSelectedMessageRequest = async (action: "accept" | "decline") => {
        if (!selectedChat || typeof selectedChat.id !== "string") return;
        try {
            const result = await respondMessageRequest(selectedChat.id, action);
            publishNotificationUpdate(result.unreadCount);
            setRequests(prev => prev.filter(request => request.conversationId !== String(selectedChat.id)));
            publishMessageRequestUpdate();
            requestConversationByIdRef.current.delete(String(selectedChat.id));
            if (action === "accept") {
                const conversations = await fetchConversations();
                setChats(conversations.map(conversationToChat));
                setSelectedChatId(selectedChat.id);
                setActiveTab("chats");
            } else {
                setChats(prev => prev.filter(chat => String(chat.id) !== String(selectedChat.id)));
                setSelectedChatId(null);
                setActiveTab("requests");
            }
        } catch (err) {
            setMessageActionError(err instanceof Error ? err.message : "Could not update message request.");
        }
    };

    const unblockUser = async (entry: Blocked) => {
        try {
            if (entry.conversationId && typeof entry.conversationId === 'string') {
                try {
                    await blockConversationApi(entry.conversationId);

                    // Restore pre-block state (icon order + incognito)
                    const snapshot = restorePreBlockState(entry.conversationId);
                    if (snapshot) {
                        // Restore icon order
                        localStorage.setItem(`iconOrder_${entry.conversationId}`, JSON.stringify(snapshot.order));
                        // Restore incognito if it was active before block
                        if (snapshot.hadIncognito) {
                            setIncognitoChatIds(prev => {
                                const next = new Set(prev);
                                next.add(entry.conversationId!);
                                return next;
                            });
                        }
                    }
                } catch (err) {
                    if (!(err instanceof Error) || !err.message.includes("404")) throw err;
                    if (!entry.userId) throw err;
                    await toggleProfileBlock(entry.userId);
                }
            } else if (entry.userId) {
                await toggleProfileBlock(entry.userId);
            } else {
                throw new Error("Blocked user is missing.");
            }

            const [conversations, messageRequests] = await Promise.all([
                fetchConversations(),
                fetchMessageRequests(),
            ]);
            const restoredRequest = messageRequests.some(request => (
                String(request.id) === String(entry.conversationId || '') ||
                (entry.userId && request.participants?.some(participant => String(participant.id) === String(entry.userId)))
            ));
            setChats(conversations.map(conversationToChat));
            await Promise.all([loadRequests(), loadBlocked()]);
            if (restoredRequest) {
                if (
                    String(selectedChatId) === String(entry.conversationId) ||
                    (entry.userId && selectedChat?.otherUserId && String(selectedChat.otherUserId) === String(entry.userId))
                ) {
                    setSelectedChatId(null);
                }
                setActiveTab("requests");
            } else {
                setActiveTab("chats");
            }
        } catch (err) {
            console.error('Unblock failed:', err);
            showMessageToast(betaSafeErrorMessage(err, "Could not unblock user."));
        }
    };

    const handleMuteChat = async (chatId: string | number, showToast = true) => {
        if (typeof chatId !== 'string') return;
        try {
            const result = await muteConversationApi(chatId);
            if (result.isMuted) {
                addToIconOrder(chatId, 'muted');
            } else {
                removeFromIconOrder(chatId, 'muted');
            }
            setChats(prev => prev.map(c =>
                c.id === chatId ? { ...c, isMuted: result.isMuted } : c
            ));
            if (showToast) showMessageToast(result.isMuted ? "Muted" : "Unmuted");
        } catch (err) {
            console.error('Mute failed:', err);
            if (showToast) showMessageToast(betaSafeErrorMessage(err, "Could not update mute."));
        }
        setMenuOpen(false);
    };

    const handlePinChat = async (chatId: string | number, desiredPinned?: boolean, showToast = true) => {
        if (typeof chatId !== 'string') return;
        const currentChat = chatsRef.current.find(chat => String(chat.id) === String(chatId));
        if (desiredPinned !== undefined && currentChat?.isPinned === desiredPinned) return;
        const optimisticPinned = desiredPinned ?? !currentChat?.isPinned;
        setChats(prev => prev.map(c =>
            String(c.id) === String(chatId) ? { ...c, isPinned: optimisticPinned } : c
        ));
        try {
            const result = await pinConversationApi(chatId);
            if (result.isPinned) {
                addToIconOrder(chatId, 'pinned');
            } else {
                removeFromIconOrder(chatId, 'pinned');
            }
            setChats(prev => prev.map(c =>
                String(c.id) === String(chatId) ? { ...c, isPinned: result.isPinned } : c
            ));
            if (showToast) showMessageToast(result.isPinned ? "Pinned" : "Unpinned");
        } catch (err) {
            console.error('Pin failed:', err);
            if (currentChat) {
                setChats(prev => prev.map(c =>
                    String(c.id) === String(chatId) ? { ...c, isPinned: currentChat.isPinned } : c
                ));
            }
            if (showToast) showMessageToast(betaSafeErrorMessage(err, "Could not update pin."));
            else throw err;
        }
        setMenuOpen(false);
    };

    const handleArchiveChat = async (chat: Chat, showToast = true) => {
        if (typeof chat.id !== "string") return;
        setChats(prev => prev.map(item => String(item.id) === String(chat.id) ? { ...item, isArchived: true, isRecycled: false } : item));
        if (String(selectedChatId) === String(chat.id)) setSelectedChatId(null);
        try {
            const result = await archiveConversationApi(chat.id);
            syncConversation(result.conversation);
            if (showToast) showMessageToast("Archived");
        } catch (err) {
            setChats(prev => prev.map(item => String(item.id) === String(chat.id) ? { ...item, isArchived: chat.isArchived, isRecycled: chat.isRecycled } : item));
            if (showToast) showMessageToast(betaSafeErrorMessage(err, "Could not archive chat."));
            else throw err;
        }
    };

    const handleRecycleChat = async (chat: Chat, showToast = true) => {
        if (typeof chat.id !== "string") return;
        setChats(prev => prev.map(item => String(item.id) === String(chat.id) ? { ...item, isArchived: false, isRecycled: true } : item));
        if (String(selectedChatId) === String(chat.id)) setSelectedChatId(null);
        try {
            const result = await recycleConversationApi(chat.id);
            syncConversation(result.conversation);
            if (showToast) showMessageToast("Moved to Recycle Bin");
        } catch (err) {
            setChats(prev => prev.map(item => String(item.id) === String(chat.id) ? { ...item, isArchived: chat.isArchived, isRecycled: chat.isRecycled } : item));
            if (showToast) showMessageToast(betaSafeErrorMessage(err, "Could not move chat to Recycle Bin."));
            else throw err;
        }
    };

    const handleRestoreChat = async (chat: Chat, showToast = true) => {
        if (typeof chat.id !== "string") return;
        setChats(prev => prev.map(item => String(item.id) === String(chat.id) ? { ...item, isArchived: false, isRecycled: false } : item));
        try {
            const result = await restoreConversationApi(chat.id);
            syncConversation(result.conversation);
            setInboxFilter("all");
            if (showToast) showMessageToast("Restored");
        } catch (err) {
            setChats(prev => prev.map(item => String(item.id) === String(chat.id) ? { ...item, isArchived: chat.isArchived, isRecycled: chat.isRecycled } : item));
            if (showToast) showMessageToast(betaSafeErrorMessage(err, "Could not restore chat."));
            else throw err;
        }
    };

    const clearChatSelection = () => setSelectedChatIds(new Set());

    const toggleChatSelection = (chat: Chat) => {
        setSelectedChatIds(prev => {
            const next = new Set(prev);
            const key = String(chat.id);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const enterChatSelection = (chat: Chat) => {
        setSelectedChatIds(new Set([String(chat.id)]));
    };

    const openChatFromList = (chat: Chat) => {
        if (chatSelectionMode) {
            toggleChatSelection(chat);
            return;
        }
        setSelectedChatId(chat.id);
        setMenuOpen(false);
        setShowChatSearch(false);
        setSearchInChat("");
        setChats(prev => prev.map(c =>
            c.id === chat.id ? { ...c, unreadCount: 0 } : c
        ));
    };

    const runBulkPin = async (desiredPinned: boolean) => {
        const targets = selectedBulkChats.filter(chat => !chat.isArchived && !chat.isRecycled);
        try {
            await Promise.all(targets.map(chat => handlePinChat(chat.id, desiredPinned, false)));
            clearChatSelection();
            showMessageToast(desiredPinned ? "Pinned" : "Unpinned");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not update selected chats."));
        }
    };

    const runBulkArchive = async () => {
        const targets = selectedBulkChats.filter(chat => !chat.isArchived && !chat.isRecycled);
        try {
            await Promise.all(targets.map(chat => handleArchiveChat(chat, false)));
            clearChatSelection();
            showMessageToast("Archived");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not archive selected chats."));
        }
    };

    const runBulkRecycle = async () => {
        const targets = selectedBulkChats.filter(chat => !chat.isRecycled);
        try {
            await Promise.all(targets.map(chat => handleRecycleChat(chat, false)));
            clearChatSelection();
            showMessageToast("Moved to Recycle Bin");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not move selected chats."));
        }
    };

    const runBulkRestore = async () => {
        const targets = selectedBulkChats.filter(chat => chat.isArchived || chat.isRecycled);
        try {
            await Promise.all(targets.map(chat => handleRestoreChat(chat, false)));
            clearChatSelection();
            showMessageToast("Restored");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not restore selected chats."));
        }
    };

    const beginBulkPermanentDelete = () => {
        const targets = selectedBulkChats.filter(chat => chat.isRecycled);
        if (targets.length) setBulkDeleteChats(targets);
    };

    const handleBulkPermanentDelete = async () => {
        const targets = bulkDeleteChats.filter(chat => typeof chat.id === "string");
        try {
            for (const chat of targets) {
                await deleteConversationApi(String(chat.id));
            }
            const deletedIds = new Set(targets.map(chat => String(chat.id)));
            setChats(prev => prev.filter(chat => !deletedIds.has(String(chat.id))));
            if (selectedChatId && deletedIds.has(String(selectedChatId))) {
                setSelectedChatId(null);
                setNewMessage("");
            }
            clearChatSelection();
            showMessageToast("Deleted permanently");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not delete chats."));
        } finally {
            setBulkDeleteChats([]);
        }
    };

    const handleDeleteChat = async () => {
        if (!chatToDelete || typeof chatToDelete.id !== 'string') {
            setShowDeleteModal(false);
            setChatToDelete(null);
            return;
        }
        try {
            if (chatToDelete.isGroup && chatToDelete.presenceOwnedByPeer) {
                showMessageToast("This action is not available for this identity.");
                setShowDeleteModal(false);
                setChatToDelete(null);
                setMenuOpen(false);
                return;
            }
            await deleteConversationApi(chatToDelete.id as string);
            setChats(prev => prev.filter(c => c.id !== chatToDelete.id));
            if (selectedChatId === chatToDelete.id) {
                setSelectedChatId(null);
                setNewMessage("");
            }
            showMessageToast(chatToDelete.isGroup ? "Group deleted" : "Deleted permanently");
        } catch (err) {
            console.error('Delete chat failed:', err);
            showMessageToast(betaSafeErrorMessage(err, "Could not delete chat."));
        }
        setShowDeleteModal(false);
        setChatToDelete(null);
        setMenuOpen(false);
    };

    const handleOblivionChat = async () => {
        if (!chatToOblivion || typeof chatToOblivion.id !== "string") {
            setShowOblivionModal(false);
            setChatToOblivion(null);
            return;
        }
        try {
            const result = await oblivionConversationApi(chatToOblivion.id);
            const shouldRemoveLocally = !result.retainedForPresence;

            if (shouldRemoveLocally) {
                setChats(prev => prev.filter(chat => String(chat.id) !== String(chatToOblivion.id)));
                if (String(selectedChatId) === String(chatToOblivion.id)) {
                    setSelectedChatId(null);
                    setNewMessage("");
                    setReplyDraft(null);
                }
            } else {
                setChats(prev => prev.map(chat => (
                    String(chat.id) === String(chatToOblivion.id)
                        ? { ...chat, isArchived: false, isRecycled: false }
                        : chat
                )));
            }

            showMessageToast(result.retainedForPresence ? "Removed from their side" : "Conversation erased");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not erase conversation."));
        } finally {
            setShowOblivionModal(false);
            setChatToOblivion(null);
            setMenuOpen(false);
        }
    };

    const handleBlockUser = async (chat: Chat) => {
        if (typeof chat.id !== 'string') return;
        if (chat.isPresenceConversation && !currentUserIsPresence) {
            setShowBlockModal(false);
            setChatToBlock(null);
            setMenuOpen(false);
            return;
        }
        try {
            const hadLocalIncognito = incognitoChatIds.has(chat.id);
            const hadActiveIncognito = isChatIncognito(chat.id);
            // Save only the per-chat state; global Settings incognito is restored from profile.
            savePreBlockState(chat.id, hadLocalIncognito);

            // Clear incognito messages from both sides if active
            if (hadActiveIncognito) {
                const incognitoMsgIds = chat.messages
                    .filter(m => m.isIncognito && m.from === "me")
                    .map(m => m.id);
                if (incognitoMsgIds.length > 0) {
                    const socket = getSocket();
                    if (socket) {
                        socket.emit('incognito:clear', {
                            conversationId: chat.id,
                            messageIds: incognitoMsgIds.map(String),
                        });
                    }
                    storeIncognitoClear(chat.id, incognitoMsgIds.map(String)).catch(() => {});
                }
                // Remove incognito state
                setIncognitoChatIds(prev => {
                    const next = new Set(prev);
                    next.delete(chat.id);
                    return next;
                });
            }

            const result = await blockConversationApi(chat.id as string);
            if (result.isBlocked) {
                setChats(prev => prev.map(c =>
                    c.id === chat.id
                        ? { ...c, isBlocked: true, isBlockedByMe: Boolean(result.isBlockedByMe ?? result.isBlocked), messages: c.messages.filter(m => !m.isIncognito) }
                        : c
                ));
                if (chat.requestStatus === "pending") {
                    setRequests(prev => prev.filter(request => String(request.conversationId) !== String(chat.id)));
                    requestConversationByIdRef.current.delete(String(chat.id));
                }
                setBlocked(prev => {
                    const exists = prev.find(b => b.id === chat.id);
                    if (exists) return prev;
                    return [
                        ...prev,
                        {
                            id: chat.id,
                            name: chat.name,
                            avatar: chat.avatar,
                            reason: 'Blocked',
                            conversationId: chat.id as string,
                            requestStatus: chat.requestStatus,
                            requestedBy: chat.requestedBy || null,
                        },
                    ];
                });
                await loadBlocked();
                setActiveTab('blocked');
            } else {
                const [conversations, messageRequests] = await Promise.all([
                    fetchConversations(),
                    fetchMessageRequests(),
                ]);
                const restoredRequest = messageRequests.some(request => String(request.id) === String(chat.id));
                setChats(conversations.map(conversationToChat));
                await Promise.all([loadRequests(), loadBlocked()]);
                if (restoredRequest) {
                    setSelectedChatId(null);
                    setActiveTab("requests");
                } else {
                    setActiveTab("chats");
                }
            }
        } catch (err) {
            console.error('Block failed:', err);
        }
        setMenuOpen(false);
    };

    // ✅ New Feature Functions
    const reportChat = async (chat: Chat, category: "spam" | "harassment" | "other" = "other") => {
        if (typeof chat.id !== "string") return;
        try {
            await createSafetyReport({
                conversationId: chat.id,
                reportedUserId: chat.otherUserId,
                category,
            });
            showMessageToast("Report submitted.");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not submit report."));
        } finally {
            setMenuOpen(false);
            closeMessageActionMenu();
        }
    };

    const reportChatMessage = async (message: ChatMessageItem) => {
        if (!selectedChat || !isPersistedMessageId(message.id)) return;
        try {
            await createSafetyReport({
                conversationId: String(selectedChat.id),
                messageId: message.id,
                category: "other",
            });
            showMessageToast("Report submitted.");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not submit report."));
        } finally {
            closeMessageActionMenu();
        }
    };

    const reportRequest = async (req: FriendRequest) => {
        if (req.kind !== "message" || !req.conversationId) return;
        try {
            await createSafetyReport({
                conversationId: req.conversationId,
                category: "spam",
                note: req.note || "",
            });
            showMessageToast("Report submitted.");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not submit report."));
        }
    };

    const blockRequest = async (req: FriendRequest) => {
        if (req.kind !== "message" || !req.conversationId) return;
        try {
            await blockConversationApi(req.conversationId);
            setRequests((prev) => prev.filter((r) => r.id !== req.id));
            requestConversationByIdRef.current.delete(String(req.conversationId));
            setChats(prev => prev.map(chat => (
                String(chat.id) === String(req.conversationId)
                    ? { ...chat, isBlocked: true, isBlockedByMe: true }
                    : chat
            )));
            await loadBlocked();
            showMessageToast("User blocked.");
            setActiveTab("blocked");
        } catch (err) {
            showMessageToast(betaSafeErrorMessage(err, "Could not block user."));
        }
    };

    const toggleEncryption = async (chatId: string | number) => {
        if (typeof chatId !== 'string') return;
        try {
            const result = await encryptConversationApi(chatId);
            if (result.isEncrypted) {
                addToIconOrder(chatId, 'lock');
            } else {
                removeFromIconOrder(chatId, 'lock');
            }
            setChats(prev => prev.map(chat =>
                chat.id === chatId
                    ? { ...chat, isEncrypted: result.isEncrypted }
                    : chat
            ));
        } catch (err) {
            console.error('Encrypt toggle failed:', err);
        }
    };

    const togglePinMessage = (chatId: string | number, messageId: string | number) => {
        setChats(prev => prev.map(chat => {
            if (chat.id !== chatId) return chat;

            const isPinned = chat.pinnedMessages.includes(messageId);
            return {
                ...chat,
                pinnedMessages: isPinned
                    ? chat.pinnedMessages.filter(id => id !== messageId)
                    : [...chat.pinnedMessages, messageId]
            };
        }));
    };

    const getMyReaction = (messageId: string | number, reactions?: { user: string; reaction: string }[]) => {
        const persisted = reactions?.find(item => String(item.user) === String(currentUser?.id))?.reaction as ReactionId | undefined;
        return persisted || messageReactions[String(messageId)];
    };

    const withMyReaction = (reactions: { user: string; reaction: string; reactedAt?: string }[] = [], nextReaction: ReactionId | null) => {
        const userId = String(currentUser?.id || "me");
        const withoutMine = reactions.filter(item => String(item.user) !== userId);
        if (!nextReaction) return withoutMine;
        return [...withoutMine, { user: userId, reaction: nextReaction, reactedAt: new Date().toISOString() }];
    };

    const getReactionCounts = (messageId: string | number, reactions?: { user: string; reaction: string }[]) => {
        const counts: Partial<Record<ReactionId, number>> = {};
        for (const item of reactions || []) {
            const reaction = item.reaction as ReactionId;
            if (REACTION_OPTIONS.some(option => option.id === reaction)) {
                counts[reaction] = (counts[reaction] || 0) + 1;
            }
        }

        if ((!reactions || reactions.length === 0) && messageReactions[String(messageId)]) {
            const localReaction = messageReactions[String(messageId)];
            counts[localReaction] = 1;
        }

        return counts;
    };

    const toggleReaction = async (messageId: string | number, reaction: ReactionId) => {
        if (!selectedChat) return;

        const message = selectedChat.messages.find(item => String(item.id) === String(messageId));
        const currentReaction = getMyReaction(messageId, message?.reactions);
        const nextReaction = currentReaction === reaction ? null : reaction;

        setMessageReactions(prev => {
            const key = String(messageId);
            const next = { ...prev };
            if (nextReaction) next[key] = nextReaction;
            else delete next[key];
            return next;
        });

        setChats(prev => prev.map(chat =>
            String(chat.id) === String(selectedChat.id)
                ? {
                    ...chat,
                    messages: chat.messages.map(item =>
                        String(item.id) === String(messageId)
                            ? { ...item, reactions: withMyReaction(item.reactions, nextReaction) }
                            : item
                    ),
                }
                : chat
        ));

        const isPersistedMessage = isPersistedMessageId(messageId);
        if (!isPersistedMessage || typeof selectedChat.id !== 'string') return;

        const socket = getSocket();
        if (socket?.connected) {
            socket.emit('message:reaction', {
                conversationId: selectedChat.id,
                messageId,
                reaction: nextReaction,
            });
            return;
        }

        try {
            const result = await setMessageReaction(String(messageId), nextReaction);
            setChats(prev => prev.map(chat =>
                String(chat.id) === String(result.conversationId)
                    ? {
                        ...chat,
                        messages: chat.messages.map(item =>
                            String(item.id) === String(result.messageId)
                                ? { ...item, reactions: result.reactions }
                                : item
                        ),
                    }
                    : chat
            ));
        } catch (err) {
            console.error('Reaction update failed:', err);
            showMessageToast(betaSafeErrorMessage(err, "Could not update reaction."));
        }
    };

    const isTouchLikePointer = (event: React.PointerEvent) =>
        event.pointerType === "touch" || event.pointerType === "pen";

    const hasDesktopReactionPointer = () =>
        Boolean(window.matchMedia?.("(hover: hover) and (pointer: fine)").matches);

    const startMessagePress = (
        event: React.PointerEvent<HTMLDivElement>,
        messageId: string | number,
        align: "left" | "right",
        canManage: boolean
    ) => {
        if (!isTouchLikePointer(event)) return;
        const target = event.target as HTMLElement;
        if (target.closest('button, a, textarea, input, [data-message-actions="true"]')) return;
        const rect = event.currentTarget.getBoundingClientRect();
        pressStartRef.current = { x: event.clientX, y: event.clientY };
        if (reactionPressTimerRef.current) window.clearTimeout(reactionPressTimerRef.current);
        reactionPressTimerRef.current = window.setTimeout(() => {
            event.preventDefault();
            window.getSelection?.()?.removeAllRanges();
            setOpenReactionMessageId(null);
            setReactionPalettePosition(null);
            openMessageActionMenuFromRect(rect, messageId, align, canManage);
            reactionPressTimerRef.current = null;
            pressStartRef.current = null;
        }, 420);
    };

    const cancelReactionPress = () => {
        if (!reactionPressTimerRef.current) return;
        window.clearTimeout(reactionPressTimerRef.current);
        reactionPressTimerRef.current = null;
        pressStartRef.current = null;
    };

    const moveMessagePress = (event: React.PointerEvent) => {
        if (!pressStartRef.current || !isTouchLikePointer(event)) return;
        const dx = Math.abs(event.clientX - pressStartRef.current.x);
        const dy = Math.abs(event.clientY - pressStartRef.current.y);
        if (dx > 10 || dy > 10) cancelReactionPress();
    };

    const finishMessagePress = (event: React.PointerEvent, messageId: string | number, canReact: boolean) => {
        const wasTap = Boolean(reactionPressTimerRef.current);
        cancelReactionPress();
        if (wasTap) toggleReactionPaletteForTap(event, messageId, canReact);
    };

    const toggleReactionPaletteForTap = (event: React.PointerEvent, messageId: string | number, canReact: boolean) => {
        if (!isTouchLikePointer(event)) return;
        if (!canReact) return;
        const target = event.target as HTMLElement;
        if (target.closest('button, a, textarea, input, [data-message-actions="true"]')) return;
        closeMessageActionMenu();
        setActiveMessageControlsId(null);
        const isSamePicker = String(openReactionMessageId) === String(messageId);
        if (isSamePicker) {
            setOpenReactionMessageId(null);
            setReactionPalettePosition(null);
            return;
        }
        const align = event.currentTarget.classList.contains("bs-message-own") ? "right" : "left";
        setReactionPalettePosition(getReactionPalettePosition(event.currentTarget.getBoundingClientRect(), align));
        setOpenReactionMessageId(messageId);
    };

    const openReactionPaletteForDesktopHover = (messageId: string | number, canReact: boolean) => {
        if (!canReact || !hasDesktopReactionPointer()) return;
        closeMessageActionMenu();
        setActiveMessageControlsId(null);
        setReactionPalettePosition(null);
        setOpenReactionMessageId(messageId);
    };

    const closeReactionPaletteForDesktopLeave = (messageId: string | number) => {
        if (!hasDesktopReactionPointer()) return;
        setOpenReactionMessageId(current => {
            if (String(current) !== String(messageId)) return current;
            setReactionPalettePosition(null);
            return null;
        });
    };

    const renderReaction = (reaction?: ReactionId) => {
        const option = REACTION_OPTIONS.find(item => item.id === reaction);
        if (!option) return null;
        if (option.id === "heart") {
            return <Heart size={11} className="fill-red-400 text-red-400" />;
        }
        return <span className="leading-none">{option.value}</span>;
    };

    const insertEmoji = (emoji: string) => {
        const input = messageInputRef.current;
        const start = input?.selectionStart ?? newMessage.length;
        const end = input?.selectionEnd ?? newMessage.length;
        const next = `${newMessage.slice(0, start)}${emoji}${newMessage.slice(end)}`;
        setNewMessage(next);
        window.setTimeout(() => {
            const cursor = start + emoji.length;
            messageInputRef.current?.focus();
            messageInputRef.current?.setSelectionRange(cursor, cursor);
        }, 0);
    };

    const exportChat = (chatId: string | number, format: 'txt' | 'html' | 'md') => {
        const chat = chats.find(c => c.id === chatId);
        if (!chat) return;

        const nonIncognitoMessages = chat.messages.filter(m => !m.isIncognito);
        const exportedAt = formatBlackspaceFullDateTime(new Date());

        if (format === 'txt') {
            const header = `Chat with ${chat.name}\nExported: ${exportedAt}\n${'─'.repeat(40)}\n`;
            const body = nonIncognitoMessages.map(m =>
                `[${m.time}] ${m.from === 'me' ? 'You' : chat.name}: ${m.text}`
            ).join('\n');
            const blob = new Blob([header + body], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `chat-${chat.name}-${Date.now()}.txt`;
            a.click();
            URL.revokeObjectURL(url);
        } else if (format === 'html') {
            const messages = nonIncognitoMessages.map(m => {
                const isMe = m.from === 'me';
                return `<div style="display:flex;justify-content:${isMe ? 'flex-end' : 'flex-start'};margin:6px 0">
  <div style="max-width:70%;padding:10px 14px;border-radius:4px;background:${isMe ? '#7c3aed' : '#262626'};color:#fff;font-size:14px">
    <div style="font-size:11px;opacity:0.6;margin-bottom:4px">${isMe ? 'You' : chat.name} · ${m.time}</div>
    <div>${m.text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
  </div>
</div>`;
            }).join('\n');
            const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Chat with ${chat.name}</title>
<style>body{margin:0;padding:24px;background:#0a0a0a;color:#fff;font-family:-apple-system,system-ui,sans-serif}
.header{text-align:center;padding:16px;border-bottom:1px solid #333;margin-bottom:16px}
.header h1{font-size:20px;margin:0 0 4px}.header p{font-size:12px;color:#888;margin:0}</style></head>
<body><div class="header"><h1>💬 Chat with ${chat.name}</h1><p>Exported ${exportedAt}</p></div>
<div style="max-width:600px;margin:0 auto">${messages}</div></body></html>`;
            const blob = new Blob([html], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `chat-${chat.name}-${Date.now()}.html`;
            a.click();
            URL.revokeObjectURL(url);
        } else if (format === 'md') {
            const header = `# Chat with ${chat.name}\n\n*Exported: ${exportedAt}*\n\n---\n\n`;
            const body = nonIncognitoMessages.map(m =>
                `**${m.from === 'me' ? 'You' : chat.name}** *(${m.time})*: ${m.text}`
            ).join('\n\n');
            const blob = new Blob([header + body], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `chat-${chat.name}-${Date.now()}.md`;
            a.click();
            URL.revokeObjectURL(url);
        }
        setShowExportMenu(false);
    };

    const startVoiceRecording = () => {
        setIsRecording(true);
        // Add actual voice recording logic here
    };

    const stopVoiceRecording = () => {
        setIsRecording(false);
        // Add actual voice recording logic here
    };

    const groupRoleLabel = (role?: string | null) => {
        if (role === "presence") return "Owner";
        if (role === "owner") return "Owner";
        if (role === "admin") return "Admin";
        return "Member";
    };

    const groupRoleClass = (role?: string | null) => {
        if (role === "presence") return "border-fuchsia-300/35 bg-fuchsia-400/10 text-fuchsia-100";
        if (role === "owner") return "border-amber-300/35 bg-amber-400/10 text-amber-200";
        if (role === "admin") return "border-cyan-300/35 bg-cyan-400/10 text-cyan-200";
        return "border-neutral-700 bg-neutral-900 text-neutral-300";
    };

    const groupRoleIcon = (role?: string | null) => {
        if (role === "presence") return <ShieldCheck size={13} />;
        if (role === "owner") return <Crown size={13} />;
        if (role === "admin") return <Shield size={13} />;
        return <UsersRound size={13} />;
    };

    const actionMessage = selectedChat?.messages.find(message => String(message.id) === String(openMessageMenuId)) || null;
    const actionMessageCanReply = Boolean(
        actionMessage &&
        !actionMessage.isIncognito &&
        actionMessage.status !== "failed" &&
        isPersistedMessageId(actionMessage.id)
    );
    const actionMessageCanManage = Boolean(
        actionMessage &&
        selectedChat &&
        (actionMessage.from === "me" || (selectedChat.myGroupRole === "presence" && !selectedChat.presenceOwnedByPeer)) &&
        !actionMessage.isIncognito &&
        actionMessage.status !== "failed" &&
        isPersistedMessageId(actionMessage.id)
    );
    const forwardQuery = forwardSearch.trim().toLowerCase();
    const forwardChatResults = chats
        .filter(chat => !chat.isBlocked && (chat.requestStatus || "accepted") !== "declined")
        .filter(chat => !forwardQuery || chat.name.toLowerCase().includes(forwardQuery))
        .sort((a, b) => getChatActivityTime(b) - getChatActivityTime(a));
    const forwardUserTargets = forwardUserResults
        .map(userToForwardTarget)
        .filter(target => !forwardChatResults.some(chat => forwardTargetKey(chatToForwardTarget(chat)) === forwardTargetKey(target)));

    return (
        <div className={`bs-mobile-messages h-screen flex flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] ${selectedChat ? "mobile-chat-open" : ""}`}>
            {/* Layout */}
            <div className="flex flex-1 overflow-hidden">
                {/* LEFT INBOX */}
                <aside
                    className="bs-message-inbox flex flex-col border-r border-neutral-800 bg-neutral-950/95"
                    style={{ width: sidebarWidth }}
                >
                    <div ref={inboxMenuRef} className="bs-message-inbox-head sticky top-0 z-10 border-b border-neutral-800 bg-neutral-950/90 px-4 pb-3 pt-4 backdrop-blur">
                        {chatSelectionMode ? (
                            <div className="mb-3 rounded-lg border border-cyan-300/15 bg-black/35 p-2.5 shadow-[0_0_28px_rgba(34,211,238,0.06)]">
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={clearChatSelection}
                                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-neutral-800 text-neutral-300 transition hover:bg-neutral-900 hover:text-white"
                                        aria-label="Cancel selection"
                                    >
                                        <X size={17} />
                                    </button>
                                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-white">
                                        {selectedChatIds.size} selected
                                    </span>
                                    <div className="flex shrink-0 items-center gap-1 overflow-x-auto">
                                        {inboxFilter === "recycle" ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => void runBulkRestore()}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/10 px-2.5 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/15"
                                                >
                                                    <RotateCcw size={14} /> Restore
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={beginBulkPermanentDelete}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-400/25 bg-rose-500/10 px-2.5 text-xs font-semibold text-rose-100 transition hover:bg-rose-500/15"
                                                >
                                                    <Trash2 size={14} /> Delete
                                                </button>
                                            </>
                                        ) : inboxFilter === "archive" ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => void runBulkRestore()}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/10 px-2.5 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/15"
                                                >
                                                    <RotateCcw size={14} /> Restore
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void runBulkRecycle()}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-400/25 bg-rose-500/10 px-2.5 text-xs font-semibold text-rose-100 transition hover:bg-rose-500/15"
                                                >
                                                    <Trash2 size={14} /> Recycle Bin
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => void runBulkPin(bulkPinShouldPin)}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-yellow-300/25 bg-yellow-300/10 px-2.5 text-xs font-semibold text-yellow-100 transition hover:bg-yellow-300/15"
                                                >
                                                    <Pin size={14} /> {bulkPinShouldPin ? "Pin" : "Unpin"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void runBulkArchive()}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/10 px-2.5 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-400/15"
                                                >
                                                    <Archive size={14} /> Archive
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => void runBulkRecycle()}
                                                    className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-400/25 bg-rose-500/10 px-2.5 text-xs font-semibold text-rose-100 transition hover:bg-rose-500/15"
                                                >
                                                    <Trash2 size={14} /> Recycle Bin
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                        <div className="bs-message-inbox-title-row mb-4 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h1 className="bs-message-page-title truncate">Messages</h1>
                            </div>
                            <div className="relative flex items-center gap-1">
                                <button
                                    onClick={() => setShowNewChatModal(true)}
                                    className="bs-message-inbox-command inline-flex h-9 w-9 items-center justify-center rounded-md border border-cyan-400/25 bg-cyan-400/10 text-cyan-200 shadow-[0_0_22px_rgba(34,211,238,0.10)] transition hover:border-cyan-300/45 hover:bg-cyan-400/15 hover:text-cyan-100"
                                    title="New conversation"
                                >
                                    <Plus size={17} />
                                </button>
                                <button
                                    onClick={() => {
                                        setGroupActionExpanded(false);
                                        setShowInboxMenu(prev => !prev);
                                    }}
                                    className="bs-message-inbox-command inline-flex h-9 w-9 items-center justify-center rounded-md border border-neutral-800 text-neutral-300 transition hover:bg-neutral-900 hover:text-white"
                                    title="Inbox options"
                                >
                                    <MoreVertical size={17} />
                                </button>
                            </div>
                        </div>

                        {showInboxMenu && (
                            <div className="bs-inbox-menu mb-3 rounded-lg border border-cyan-300/15 bg-neutral-950/95 p-2 shadow-2xl shadow-black/40">
                                {[
                                    { label: "Requests", count: filteredRequests.length, tab: "requests" as const, icon: Inbox, tone: "text-amber-200" },
                                    { label: "Blocked", count: filteredBlocked.length, tab: "blocked" as const, icon: Ban, tone: "text-rose-200" },
                                ].map(item => {
                                    const Icon = item.icon;
                                    return (
                                    <button
                                        key={item.tab}
                                        onClick={() => {
                                            setGroupActionExpanded(false);
                                            setActiveTab(item.tab);
                                            setShowInboxMenu(false);
                                        }}
                                        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-neutral-300 transition hover:bg-neutral-900 hover:text-white"
                                    >
                                        <span className={`grid h-8 w-8 place-items-center rounded-md border border-white/10 bg-white/5 ${item.tone}`}>
                                            <Icon size={15} />
                                        </span>
                                        <span className="min-w-0 flex-1 text-left font-bold">{item.label}</span>
                                        <span className="rounded-md border border-neutral-800 bg-black px-1.5 py-0.5 text-[11px] font-black text-neutral-400">{item.count}</span>
                                    </button>
                                    );
                                })}
                                <button
                                    type="button"
                                    onClick={() => setGroupActionExpanded(prev => !prev)}
                                    className="bs-message-group-action-toggle flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-neutral-300 transition hover:bg-neutral-900 hover:text-white"
                                >
                                    <span className="grid h-8 w-8 place-items-center rounded-md border border-white/10 bg-white/5 text-emerald-200">
                                        <UsersRound size={15} />
                                    </span>
                                    <span className="min-w-0 flex-1 text-left font-bold">Group Action</span>
                                    <span className="rounded-md border border-neutral-800 bg-black px-1.5 py-0.5 text-[11px] font-black text-neutral-400">
                                        Code
                                    </span>
                                </button>
                                {groupActionExpanded && (
                                    <div className="bs-message-group-action-panel mx-1 mb-1 rounded-md border border-neutral-800 bg-black/40 p-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                value={groupCodeInput}
                                                onChange={(event) => setGroupCodeInput(event.target.value.toUpperCase())}
                                                onKeyDown={(event) => { if (event.key === "Enter") handleJoinByCode(); }}
                                                placeholder="Group code"
                                                className="h-9 min-w-0 flex-1 rounded-md border border-neutral-800 bg-black/60 px-3 text-xs font-bold uppercase tracking-[0.08em] text-white outline-none placeholder:normal-case placeholder:tracking-normal placeholder:text-neutral-600 focus:border-cyan-400/40"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleJoinByCode}
                                                disabled={!groupCodeInput.trim() || groupAction === "join-code"}
                                                className="inline-flex h-9 items-center justify-center rounded-md border border-cyan-400/30 bg-cyan-400/10 px-3 text-xs font-black text-cyan-100 transition hover:bg-cyan-400/15 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {groupAction === "join-code" ? <Loader size={13} className="animate-spin" /> : "Enter"}
                                            </button>
                                        </div>
                                        {groupCodeStatus && <p className="mt-2 truncate text-[11px] font-semibold text-neutral-400">{groupCodeStatus}</p>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="bs-message-search-shell relative mb-3">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                            <input
                                value={searchQuery}
                                onChange={(event) => handleSearch(event.target.value)}
                                placeholder="Search inbox..."
                                className="bs-message-search-input h-10 w-full rounded-full border border-neutral-800 bg-black/50 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/10"
                            />
                        </div>

                        <div className="bs-message-filter-strip no-scrollbar mb-3 flex gap-1 overflow-x-auto pb-1">
                            {inboxFilters.map(filter => {
                                const active = activeTab === "chats" && inboxFilter === filter.id;
                                return (
                                    <button
                                        key={filter.id}
                                        onClick={() => { setActiveTab("chats"); setInboxFilter(filter.id); }}
                                        className={`bs-message-filter-pill inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                            active
                                                ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-100"
                                                : "border-neutral-800 text-neutral-400 hover:border-neutral-700 hover:text-white"
                                        }`}
                                    >
                                        {filter.label}
                                        <span className={`${active ? "text-cyan-200" : "text-neutral-600"}`}>{filter.count}</span>
                                    </button>
                                );
                            })}
                        </div>
                            </>
                        )}

                    </div>

                    <div className="flex-1 overflow-y-auto py-2 custom-scrollbar">
                    {activeTab === "chats" && <SupportInbox requestedCaseId={requestedSupportCaseId} filter={inboxFilter} search={searchQuery} onCountsChange={updateSupportCounts} />}
                    {/* Chats List */}
                    {activeTab === "chats" && (
                        <>
                            {visibleChats.length === 0 && (inboxFilter === "all" ? supportCounts.all === 0 : inboxFilter === "unread" ? supportCounts.unread === 0 : true) ? (
                                <div className="bs-message-empty-state px-4 py-10 text-center text-neutral-500">
                                    <MessageCircle size={36} className="mx-auto mb-3 text-neutral-600" />
                                    <p className="text-sm">{searchQuery ? "No chats match your search" : inboxFilter === "all" ? "No conversations yet" : inboxFilter === "archive" ? "Archive is empty" : inboxFilter === "recycle" ? "Recycle Bin is empty" : `No ${inboxFilter} conversations`}</p>
                                    <p className="text-xs text-neutral-600 mt-1">{searchQuery ? "Try a different keyword" : inboxFilter === "archive" ? "Swipe right on a chat to archive it." : inboxFilter === "recycle" ? "Swipe left on a chat to move it here." : "Start a new conversation to begin messaging"}</p>
                                </div>
                            ) : (
                                visibleChats.map((chat) => {
                                    const last = chat.messages[chat.messages.length - 1];
                                    const userStatus = getChatStatus(chat);
                                    return (
                                        <ChatCell
                                            key={chat.id}
                                            avatar={chat.avatar}
                                            title={chat.name}
                                            subtitle={last ? last.text : "No messages yet"}
                                            time={last ? last.time : undefined}
                                            active={selectedChatId === chat.id}
                                            presence={getChatPresence(chat)}
                                            isEncrypted={chat.isEncrypted}
                                            status={userStatus}
                                            unreadCount={inboxFilter === "recycle" ? 0 : chat.unreadCount}
                                            lastMessageAt={chat.lastMessageAt}
                                            isMuted={chat.isMuted}
                                            isPinned={chat.isPinned}
                                            isIncognito={isChatIncognito(chat.id)}
                                            isGroup={chat.isGroup}
                                            isSelected={selectedChatIds.has(String(chat.id))}
                                            selectionMode={chatSelectionMode}
                                            chatId={chat.id}
                                            folder={inboxFilter === "archive" ? "archive" : inboxFilter === "recycle" ? "recycle" : "inbox"}
                                            onArchive={() => void handleArchiveChat(chat)}
                                            onRecycle={() => void handleRecycleChat(chat)}
                                            onRestore={() => void handleRestoreChat(chat)}
                                            onPermanentDelete={() => { setChatToDelete(chat); setShowDeleteModal(true); }}
                                            onLongPress={() => enterChatSelection(chat)}
                                            onToggleEncryption={() => toggleEncryption(chat.id)}
                                            onToggleMute={() => handleMuteChat(chat.id)}
                                            onTogglePin={() => handlePinChat(chat.id)}
                                            onToggleIncognito={() => toggleChatIncognito(chat.id)}
                                            onClick={() => openChatFromList(chat)}
                                        />
                                    );
                                })
                            )}
                        </>
                    )}

                    {activeTab === "requests" && (
                        <div className="bs-message-side-page bs-message-requests-page pb-4">
                            <div className="bs-message-side-page-head px-4 pb-3">
                                <button onClick={() => setActiveTab("chats")} className="bs-message-side-back mb-3 text-xs font-semibold text-cyan-300 hover:text-cyan-200">
                                    <ArrowLeft size={14} /> Inbox
                                </button>
                                <h2 className="text-sm font-semibold text-white">Message Requests</h2>
                                <p className="text-xs text-neutral-500">New conversations waiting for a decision.</p>
                            </div>
                            {filteredRequests.length === 0 ? (
                                <div className="bs-message-side-empty mx-4 rounded-xl border border-neutral-800 bg-black/30 px-4 py-8 text-center text-neutral-500">
                                    <Inbox size={30} className="mx-auto mb-3 text-cyan-300/70" />
                                    <p>{searchQuery ? "No requests found" : "No requests right now."}</p>
                                    <span>{searchQuery ? "Try a different search." : "New first messages and group invitations will appear here."}</span>
                                </div>
                            ) : (
                                filteredRequests.map((req) => (
                                    <div
                                        key={req.id}
                                        className="bs-message-request-card mx-2 mb-2 rounded-xl border border-neutral-800 bg-black/25 px-3 py-3 transition hover:border-cyan-400/25 hover:bg-neutral-950"
                                        onClick={() => openMessageRequest(req)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <img src={req.avatar} alt={req.name} className="bs-message-side-avatar w-9 h-9 rounded-full" />
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <p className="truncate text-sm font-semibold">{req.name}</p>
                                                    <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold ${
                                                        req.kind === "group"
                                                            ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-100"
                                                            : "border-cyan-400/25 bg-cyan-400/10 text-cyan-100"
                                                    }`}>
                                                        {req.kind === "group" ? "Group invite" : "Message"}
                                                    </span>
                                                </div>
                                                {req.note && (
                                                    <p className="text-xs text-neutral-400 truncate">
                                                        {req.note}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-3 flex flex-wrap items-center gap-2">
                                            {req.kind === "message" && (
                                                <button
                                                    onClick={(event) => { event.stopPropagation(); openMessageRequest(req); }}
                                                    className="bs-message-side-action is-soft-cyan flex items-center gap-1 rounded border border-cyan-400/30 bg-cyan-400/10 px-2 py-1 text-xs font-bold text-cyan-100 transition hover:bg-cyan-400/15"
                                                >
                                                    <MessageCircle size={14} /> Open
                                                </button>
                                            )}
                                            <button
                                                onClick={(event) => { event.stopPropagation(); acceptRequest(req); }}
                                                className="bs-message-side-action is-accept flex items-center gap-1 px-2 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-xs transition"
                                            >
                                                <Check size={14} /> Accept
                                            </button>
                                            <button
                                                onClick={(event) => { event.stopPropagation(); declineRequest(req); }}
                                                className="bs-message-side-action flex items-center gap-1 px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-xs transition"
                                            >
                                                <X size={14} /> Reject
                                            </button>
                                            {req.kind === "message" && (
                                                <>
                                                    <button
                                                        onClick={(event) => { event.stopPropagation(); void reportRequest(req); }}
                                                        className="bs-message-side-action is-report flex items-center gap-1 rounded border border-amber-400/25 bg-amber-400/10 px-2 py-1 text-xs font-bold text-amber-100 transition hover:bg-amber-400/15"
                                                    >
                                                        <Shield size={14} /> Report
                                                    </button>
                                                    <button
                                                        onClick={(event) => { event.stopPropagation(); void blockRequest(req); }}
                                                        className="bs-message-side-action is-block flex items-center gap-1 rounded border border-red-400/25 bg-red-500/10 px-2 py-1 text-xs font-bold text-red-100 transition hover:bg-red-500/15"
                                                    >
                                                        <Ban size={14} /> Block
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === "blocked" && (
                        <div className="bs-message-side-page bs-message-blocked-page pb-4">
                            <div className="bs-message-side-page-head px-4 pb-3">
                                <button onClick={() => setActiveTab("chats")} className="bs-message-side-back mb-3 text-xs font-semibold text-cyan-300 hover:text-cyan-200">
                                    <ArrowLeft size={14} /> Inbox
                                </button>
                                <h2 className="text-sm font-semibold text-white">Blocked</h2>
                                <p className="text-xs text-neutral-500">People you have blocked from messaging you.</p>
                            </div>
                            {filteredBlocked.length === 0 ? (
                                <div className="bs-message-side-empty mx-4 rounded-xl border border-neutral-800 bg-black/30 px-4 py-8 text-center text-neutral-500">
                                    <Ban size={30} className="mx-auto mb-3 text-rose-300/75" />
                                    <p>{searchQuery ? "No blocked users found" : "You haven't blocked anyone."}</p>
                                    <span>Blocked users can be unblocked here whenever you decide.</span>
                                </div>
                            ) : (
                                filteredBlocked.map((b) => (
                                    <div
                                        key={b.id}
                                        className="bs-message-blocked-card mx-2 mb-2 rounded-xl border border-neutral-800 bg-black/25 px-3 py-3 hover:bg-neutral-900/70 cursor-pointer transition-colors"
                                        onClick={() => {
                                            // Find the chat and open it in read-only mode
                                            const chat = chats.find(c => c.id === b.id || c.id === b.conversationId);
                                            if (chat) {
                                                setSelectedChatId(chat.id);
                                            }
                                        }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <img src={b.avatar} alt={b.name} className="bs-message-side-avatar w-9 h-9 rounded-full" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold">{b.name}</p>
                                                {b.reason && (
                                                    <p className="text-xs text-neutral-500">Reason: {b.reason}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); unblockUser(b); }}
                                                className="bs-message-side-action is-soft-cyan flex items-center gap-1 px-2 py-1 rounded bg-neutral-800 hover:bg-cyan-700 text-xs transition"
                                            >
                                                <Unlock size={14} /> Unblock
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                    </div>
                </aside>

                {/* RESIZER HANDLE */}
                <div
                    onMouseDown={onResizerMouseDown}
                    onDoubleClick={onResizerDoubleClick}
                    className="bs-message-resizer w-1 cursor-col-resize bg-transparent relative"
                    title="Drag to resize"
                >
                    <div className="absolute inset-y-0 left-0 right-0 mx-auto w-0.5 bg-neutral-800 hover:bg-neutral-600 transition-colors" />
                </div>

                {/* RIGHT CHAT PANEL */}
                <section className="bs-message-panel flex-1 flex flex-col bg-neutral-950">
                    {selectedChat ? (
                        <>
                            {/* Header */}
                            <div className={`bs-chat-header ${isCurrentChatIncognito ? "bs-chat-header-incognito" : ""} relative z-[180] flex items-center justify-between px-6 py-4 border-b bg-neutral-950/95 backdrop-blur ${
                                isCurrentChatIncognito ? "border-cyan-500/30 shadow-[0_1px_0_rgba(34,211,238,0.12)]" : "border-neutral-800"
                            }`}>
                                <div className="bs-chat-identity min-w-0 flex items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedChatId(null)}
                                        className="bs-chat-mobile-back hidden h-9 w-9 shrink-0 items-center justify-center rounded-md border border-neutral-800 bg-black/35 text-neutral-300 transition hover:border-cyan-300/35 hover:text-white"
                                        aria-label="Back to inbox"
                                    >
                                        <ArrowLeft size={17} />
                                    </button>
                                    <div className="bs-chat-avatar-shell relative">
                                        <img
                                            src={selectedChat.avatar}
                                            alt={selectedChat.name}
                                            className="bs-chat-avatar h-11 w-11 rounded-full object-cover ring-1 ring-white/10"
                                        />
                                        {selectedChat.isGroup && (
                                            <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-md border border-cyan-400/30 bg-black text-cyan-300">
                                                <UsersRound size={12} />
                                            </span>
                                        )}
                                        {isCurrentChatIncognito && !selectedChat.isGroup && (
                                            <span className="bs-chat-avatar-incognito-badge absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-400/30 bg-black text-cyan-300">
                                                <EyeOff size={12} />
                                            </span>
                                        )}
                                    </div>
                                    <div className="bs-chat-title-block min-w-0">
                                        <div className="bs-chat-title-row flex min-w-0 items-center gap-2">
                                            <h2
                                                className={`bs-chat-title font-bold text-lg ${selectedChat.isGroup ? "cursor-pointer transition hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/40" : ""}`}
                                                onClick={selectedChat.isGroup ? () => openGroupPanel("chat") : undefined}
                                                onKeyDown={selectedChat.isGroup ? (event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        openGroupPanel("chat");
                                                    }
                                                } : undefined}
                                                role={selectedChat.isGroup ? "button" : undefined}
                                                tabIndex={selectedChat.isGroup ? 0 : undefined}
                                                aria-label={selectedChat.isGroup ? `Open ${selectedChat.name} group info` : undefined}
                                            >{selectedChat.name}</h2>
                                            {ENABLE_E2E && (
                                                <button
                                                    onClick={() => toggleEncryption(selectedChat.id)}
                                                    className="bs-chat-encryption-button p-1 rounded hover:bg-neutral-800 transition"
                                                    title={selectedChat.isEncrypted ? "E2E Encrypted" : "Not Encrypted"}
                                                >
                                                    {selectedChat.isEncrypted ?
                                                        <Lock size={14} className="text-teal-400" /> :
                                                        <UnlockIcon size={14} className="text-neutral-400" />
                                                    }
                                                </button>
                                            )}
                                            <button
                                                onClick={() => toggleChatIncognito(selectedChat.id)}
                                                disabled={globalIncognitoEnabled}
                                                className={`bs-chat-mode-pill inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition ${
                                                    isCurrentChatIncognito
                                                        ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200"
                                                        : "border-neutral-800 text-neutral-400 hover:bg-neutral-800"
                                                } ${globalIncognitoEnabled ? "cursor-default opacity-90" : ""}`}
                                                title={
                                                    globalIncognitoEnabled
                                                        ? "Incognito active"
                                                        : isCurrentChatIncognito
                                                            ? "Disable Incognito"
                                                            : "Enable Incognito"
                                                }
                                            >
                                                <EyeOff size={13} />
                                                <span className="bs-chat-mode-text">{isCurrentChatIncognito ? "Incognito" : "Private"}</span>
                                            </button>
                                        </div>
                                        {selectedChat.isGroup ? (
                                            <p className="mt-0.5 text-xs font-semibold text-neutral-500">{selectedChat.status || `${selectedChat.participantIds?.length || 1} members`}</p>
                                        ) : (
                                            <ChatPresenceBadge
                                                p={selectedChatPresence}
                                                status={selectedChatStatus}
                                                incognito={isCurrentChatIncognito}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="bs-chat-actions flex gap-2 relative" ref={menuRef}>
                                    <button
                                        onClick={() => setShowChatSearch(prev => !prev)}
                                        className={`hidden p-2 rounded-md border transition sm:inline-flex ${
                                            showChatSearch ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-neutral-800 bg-neutral-900 text-neutral-300 hover:text-white"
                                        }`}
                                        title="Search this chat"
                                    >
                                        <Search size={18} />
                                    </button>
                                    {ENABLE_CALLS && (
                                        <button className="group inline-flex h-9 w-9 items-center justify-center rounded-md border border-emerald-400/20 bg-emerald-400/10 text-emerald-300 transition hover:border-emerald-300/40 hover:bg-emerald-400/15 hover:text-emerald-200" title="Voice call">
                                            <PhoneCall size={17} className="transition group-hover:scale-105" />
                                        </button>
                                    )}
                                    {ENABLE_VIDEO_CALLS && (
                                        <button className="group inline-flex h-9 w-9 items-center justify-center rounded-md border border-sky-400/20 bg-sky-400/10 text-sky-300 transition hover:border-sky-300/40 hover:bg-sky-400/15 hover:text-sky-200" title="Video call">
                                            <VideoIcon size={17} className="transition group-hover:scale-105" />
                                        </button>
                                    )}
                                    <div className="relative">
                                        <button
                                            onClick={() => { setMenuOpen(!menuOpen); setShowExportMenu(false); }}
                                            className={`inline-flex h-9 w-9 items-center justify-center rounded-md border transition ${
                                                menuOpen
                                                    ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200"
                                                    : "border-neutral-800 bg-neutral-900 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                                            }`}
                                            title="Chat actions"
                                        >
                                            <MoreVertical size={17} />
                                        </button>
                                        {menuOpen && (
                                            <div className="bs-chat-menu absolute right-0 z-[500] mt-2 w-64 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-2xl shadow-black/50">
                                                <div className="bs-chat-menu-scroll overflow-y-auto overflow-x-hidden">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => { setShowChatSearch(prev => !prev); setMenuOpen(false); }}
                                                        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-neutral-300 transition hover:bg-neutral-900"
                                                    >
                                                        <Search size={16} className="text-cyan-300" /> Search
                                                    </button>
                                                    <button
                                                        onClick={() => handleMuteChat(selectedChat.id)}
                                                        className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-neutral-300 hover:bg-neutral-900 transition"
                                                    >
                                                        <BellOff size={16} className="text-amber-300" /> {selectedChat.isMuted ? "Unmute" : "Mute"}
                                                    </button>
                                                    <button
                                                        onClick={() => handlePinChat(selectedChat.id)}
                                                        className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-neutral-300 hover:bg-neutral-900 transition"
                                                    >
                                                        <Pin size={16} className="text-violet-300" /> {selectedChat.isPinned ? "Unpin" : "Pin"}
                                                    </button>
                                                    {selectedChat.isGroup && (
                                                        <button
                                                            onClick={() => openGroupPanel("menu")}
                                                            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-neutral-300 transition hover:bg-neutral-900"
                                                        >
                                                            <Info size={16} className="text-cyan-300" /> Group info
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="border-y border-neutral-800 py-1">
                                                    {ENABLE_E2E && (
                                                        <button
                                                            onClick={() => toggleEncryption(selectedChat.id)}
                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-neutral-300 hover:bg-neutral-900 transition"
                                                        >
                                                            {selectedChat.isEncrypted ?
                                                                <UnlockIcon size={16} className="text-emerald-300" /> :
                                                                <Lock size={16} className="text-neutral-400" />
                                                            }
                                                            {selectedChat.isEncrypted ? "Disable E2E" : "Enable E2E"}
                                                        </button>
                                                    )}
                                                    {!selectedChat.isGroup && (
                                                        <button
                                                            onClick={() => toggleChatIncognito(selectedChat.id)}
                                                            disabled={globalIncognitoEnabled}
                                                            className={`flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm transition ${
                                                                isCurrentChatIncognito
                                                                    ? "text-cyan-200 hover:bg-cyan-400/10"
                                                                    : "text-neutral-300 hover:bg-neutral-900"
                                                            } ${globalIncognitoEnabled ? "cursor-default opacity-75" : ""}`}
                                                            title={globalIncognitoEnabled ? "Incognito active" : undefined}
                                                        >
                                                            <EyeOff size={16} className="text-cyan-300" />
                                                            <span>{globalIncognitoEnabled ? "Incognito active" : isCurrentChatIncognito ? "Disable Incognito" : "Enable Incognito"}</span>
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="py-1">
                                                    <button
                                                        onClick={() => setShowExportMenu(prev => !prev)}
                                                        className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-neutral-300 hover:bg-neutral-900 transition"
                                                    >
                                                        <Download size={16} className="text-sky-300" /> Export
                                                    </button>
                                                    {showExportMenu && (
                                                        <div className="bg-black/30 pb-1">
                                                            <button
                                                                onClick={() => exportChat(selectedChat.id, 'html')}
                                                                className="flex items-center gap-2 w-full px-8 py-2 text-xs text-neutral-400 hover:text-white hover:bg-neutral-900 transition"
                                                            >
                                                                <FileText size={14} /> HTML
                                                            </button>
                                                            <button
                                                                onClick={() => exportChat(selectedChat.id, 'txt')}
                                                                className="flex items-center gap-2 w-full px-8 py-2 text-xs text-neutral-400 hover:text-white hover:bg-neutral-900 transition"
                                                            >
                                                                <FileText size={14} /> Text
                                                            </button>
                                                            <button
                                                                onClick={() => exportChat(selectedChat.id, 'md')}
                                                                className="flex items-center gap-2 w-full px-8 py-2 text-xs text-neutral-400 hover:text-white hover:bg-neutral-900 transition"
                                                            >
                                                                <FileText size={14} /> Markdown
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="border-t border-neutral-800 py-1">
                                                    {selectedChat.isGroup ? (
                                                        selectedChat.myGroupRole === "presence" && !selectedChat.presenceOwnedByPeer ? (
                                                            <button
                                                                onClick={() => { setChatToDelete(selectedChat); setShowDeleteModal(true); setMenuOpen(false); }}
                                                                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-red-400 transition hover:bg-red-950/20"
                                                            >
                                                                <Trash2 size={16} /> Delete group
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => { openGroupPanel("menu"); setGroupError(""); }}
                                                                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-amber-300 transition hover:bg-amber-950/20"
                                                            >
                                                                <LogOut size={16} /> Leave group
                                                            </button>
                                                        )
                                                    ) : (
                                                        <button
                                                            onClick={() => { void handleRecycleChat(selectedChat); setMenuOpen(false); }}
                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-red-400 hover:bg-red-950/20 transition"
                                                        >
                                                            <Trash2 size={16} /> Recycle Bin
                                                        </button>
                                                    )}
                                                    {canReportSelectedChat && (
                                                        <button
                                                            onClick={() => void reportChat(selectedChat, "other")}
                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-amber-300 hover:bg-amber-950/20 transition"
                                                        >
                                                            <Shield size={16} /> Report
                                                        </button>
                                                    )}
                                                    {canUseOblivionSelectedChat && (
                                                        <button
                                                            onClick={() => { setChatToOblivion(selectedChat); setShowOblivionModal(true); setMenuOpen(false); }}
                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-fuchsia-200 hover:bg-fuchsia-950/20 transition"
                                                        >
                                                            <Skull size={16} className="text-fuchsia-300" /> Oblivion
                                                        </button>
                                                    )}
                                                    {canBlockSelectedChat && (
                                                        <button
                                                            onClick={() => {
                                                                if (selectedChat.isBlockedByMe) {
                                                                    void handleBlockUser(selectedChat);
                                                                    setMenuOpen(false);
                                                                    return;
                                                                }
                                                                setChatToBlock(selectedChat);
                                                                setShowBlockModal(true);
                                                                setMenuOpen(false);
                                                            }}
                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-sm text-red-500 hover:bg-red-950/20 transition"
                                                        >
                                                            {selectedChat.isBlockedByMe ? <Unlock size={16} /> : <Ban size={16} />}
                                                            {selectedChat.isBlockedByMe ? "Unblock" : "Block"}
                                                        </button>
                                                    )}
                                                </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {isCurrentChatIncognito && (
                                <div className="bs-incognito-banner border-b border-cyan-500/20 bg-cyan-950/20 px-6 py-3">
                                    <div className="bs-incognito-card flex items-center justify-between gap-3 rounded-lg border border-cyan-400/15 bg-black/30 px-4 py-2">
                                        <div className="flex items-center gap-3">
                                            <span className="bs-incognito-card-icon inline-flex h-8 w-8 items-center justify-center rounded-full bg-cyan-400/10 text-cyan-300">
                                                <ShieldCheck size={16} />
                                            </span>
                                            <div className="bs-incognito-card-copy">
                                                <p className="text-sm font-semibold text-cyan-100">
                                                    Incognito chat is active
                                                </p>
                                                <p className="text-xs text-cyan-200/60">
                                                    Messages in this mode stay out of saved history and skip typing indicators.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {selectedRequestNeedsReview && (
                                <div className="bs-message-request-review-bar border-b border-amber-300/20 bg-amber-950/10 px-6 py-3">
                                    <div className="bs-message-request-review-card grid gap-3 rounded-lg border border-amber-300/20 bg-black/35 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                                        <div className="min-w-0">
                                            <p className="text-sm font-black text-amber-100">Message request</p>
                                            <p className="mt-0.5 text-xs font-semibold text-amber-100/60">Review the first message before this enters your inbox.</p>
                                        </div>
                                        <div className="bs-message-request-review-actions grid grid-cols-4 gap-2 sm:flex sm:items-center">
                                            <button
                                                type="button"
                                                onClick={() => respondToSelectedMessageRequest("accept")}
                                                className="bs-message-request-review-action is-accept inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-2 text-xs font-black text-white transition hover:bg-emerald-500"
                                            >
                                                <Check size={14} /> Accept
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => respondToSelectedMessageRequest("decline")}
                                                className="bs-message-request-review-action inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-900 px-2 text-xs font-black text-neutral-100 transition hover:bg-neutral-800"
                                            >
                                                <X size={14} /> Reject
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void reportChat(selectedChat, "spam")}
                                                className="bs-message-request-review-action is-report inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md border border-amber-400/25 bg-amber-400/10 px-2 text-xs font-black text-amber-100 transition hover:bg-amber-400/15"
                                            >
                                                <Shield size={14} /> Report
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void handleBlockUser(selectedChat)}
                                                className="bs-message-request-review-action is-block inline-flex h-9 min-w-0 items-center justify-center gap-1.5 rounded-md border border-red-400/25 bg-red-500/10 px-2 text-xs font-black text-red-100 transition hover:bg-red-500/15"
                                            >
                                                {selectedChat.isBlockedByMe ? <Unlock size={14} /> : <Ban size={14} />}
                                                {selectedChat.isBlockedByMe ? "Unblock" : "Block"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Search in Chat */}
                            {showChatSearch && (
                                <div className="bs-chat-search-panel border-b border-neutral-800 bg-black px-6 py-3">
                                    <div className="bs-chat-search-shell relative">
                                        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-cyan-300/80" />
                                        <input
                                            type="text"
                                            value={searchInChat}
                                            onChange={(e) => setSearchInChat(e.target.value)}
                                            placeholder="Search in this chat..."
                                            className="bs-chat-search-input w-full rounded-md border border-neutral-800 bg-neutral-950 py-2 pl-10 pr-9 text-sm text-white outline-none transition placeholder:text-neutral-600 focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/10"
                                        />
                                        {searchInChat && (
                                            <button
                                                type="button"
                                                onClick={() => setSearchInChat("")}
                                                className="absolute right-2.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md text-neutral-500 transition hover:text-white"
                                                aria-label="Clear chat search"
                                                title="Clear"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Pinned Messages */}
                            {selectedChat.pinnedMessages.length > 0 && (
                                <div className="px-6 py-2 bg-neutral-900 border-b border-neutral-700">
                                    <div className="flex items-center gap-2 text-xs text-neutral-400">
                                        <Pin size={12} />
                                        <span>Pinned Messages</span>
                                    </div>
                                    {selectedChat.pinnedMessages.map(pinnedId => {
                                        const pinnedMsg = selectedChat.messages.find(m => m.id === pinnedId);
                                        return pinnedMsg ? (
                                            <div key={pinnedId} className="mt-1 p-2 bg-neutral-800 rounded text-xs">
                                                <div className="flex justify-between">
                                                    <span className="font-semibold">{pinnedMsg.from}</span>
                                                    <button
                                                        onClick={() => togglePinMessage(selectedChat.id, pinnedId)}
                                                        className="text-neutral-400 hover:text-white"
                                                    >
                                                        <X size={10} />
                                                    </button>
                                                </div>
                                                <p className="text-neutral-300 truncate">{pinnedMsg.text}</p>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            )}

                            {/* Messages */}
                            <div
                                className={`bs-chat-scroll flex-1 overflow-y-auto bg-black px-6 py-5 custom-scrollbar ${isCurrentChatIncognito ? "incognito-protected" : ""}`}
                                style={{
                                    ...(isCurrentChatIncognito ? { userSelect: 'none', WebkitUserSelect: 'none' } : {}),
                                    ...(tabBlurred ? { filter: 'blur(12px)', transition: 'filter 0.2s' } : { transition: 'filter 0.2s' }),
                                }}
                                onContextMenu={isCurrentChatIncognito ? (e) => { e.preventDefault(); } : undefined}
                            >
                                {messageActionError && (
                                    <div className="mb-3 rounded-md border border-red-400/25 bg-red-950/25 px-3 py-2 text-xs font-semibold text-red-100">
                                        {messageActionError}
                                    </div>
                                )}
                                {selectedChat && typeof selectedChat.id === "string" && messageHasMoreByChat[String(selectedChat.id)] && (
                                    <div className="mb-4 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={loadOlderMessages}
                                            disabled={loadingOlderChatId === String(selectedChat.id)}
                                            className="inline-flex items-center gap-2 rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black text-cyan-100 transition hover:border-cyan-200/45 hover:bg-cyan-300/15 disabled:cursor-wait disabled:opacity-60"
                                        >
                                            {loadingOlderChatId === String(selectedChat.id) && <Loader size={13} className="animate-spin" />}
                                            {loadingOlderChatId === String(selectedChat.id) ? "Loading older" : "Load older messages"}
                                        </button>
                                    </div>
                                )}
                                {selectedChat.messages.filter(msg =>
                                    searchInChat ?
                                        msg.text.toLowerCase().includes(searchInChat.toLowerCase()) : true
                                ).length === 0 && (
                                    <div className="bs-chat-empty-thread mx-auto flex min-h-[38vh] max-w-sm flex-col items-center justify-center px-5 text-center">
                                        <span className="bs-chat-empty-thread-icon mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/10 text-cyan-200">
                                            {selectedChat.isGroup ? <UsersRound size={26} /> : <MessageCircle size={26} />}
                                        </span>
                                        <p className="text-base font-black text-white">
                                            {searchInChat ? "No messages found" : selectedChat.isGroup ? "Group is ready" : "Chat is ready"}
                                        </p>
                                        <p className="mt-2 text-sm leading-5 text-neutral-500">
                                            {searchInChat
                                                ? "Try another word or clear the chat search."
                                                : selectedChat.isGroup
                                                    ? "Send the first message when you want this group to begin."
                                                    : "Start with a simple message. The conversation will stay clean here."}
                                        </p>
                                    </div>
                                )}
                                {selectedChat.messages
                                    .filter(msg =>
                                        searchInChat ?
                                            msg.text.toLowerCase().includes(searchInChat.toLowerCase()) : true
                                    )
                                    .map((msg, index, visibleMessages) => {
                                        const myReaction = getMyReaction(msg.id, msg.reactions);
                                        const reactionCounts = getReactionCounts(msg.id, msg.reactions);
                                        const hasReactions = Object.values(reactionCounts).some(Boolean);
                                        const reactionPickerOpen = String(openReactionMessageId) === String(msg.id);
                                        const messageControlsActive = String(activeMessageControlsId) === String(msg.id)
                                            || String(openMessageMenuId) === String(msg.id)
                                            || reactionPickerOpen;
                                        const previousMessage = visibleMessages[index - 1];
                                        const isGroupedWithPrevious = Boolean(previousMessage && previousMessage.from === msg.from);
                                        const showGroupSenderName = Boolean(selectedChat.isGroup && !isGroupedWithPrevious);
                                        const groupSenderName = msg.from === "me" ? "You" : msg.from;
                                        const isEditing = String(editingMessageId) === String(msg.id);
                                        const canManageMessage = (msg.from === "me" || (selectedChat.myGroupRole === "presence" && !selectedChat.presenceOwnedByPeer)) && !msg.isIncognito && msg.status !== "failed" && isPersistedMessageId(msg.id);
                                        const canReactToMessage = !msg.isIncognito && msg.status !== "failed" && isPersistedMessageId(msg.id);
                                        const hasMessageActions = canManageMessage || canReactToMessage;
                                        const useFloatingReactionPicker = reactionPickerOpen && Boolean(reactionPalettePosition);
                                        const reactionPaletteStyle = useFloatingReactionPicker
                                            ? ({
                                                "--reaction-top": `${reactionPalettePosition!.top}px`,
                                                "--reaction-left": `${reactionPalettePosition!.left}px`,
                                            } as React.CSSProperties)
                                            : undefined;
                                        const retryKey = msg.clientMessageId || String(msg.id);
                                        const isRetrying = retryingMessageIds.has(retryKey);
                                        return (
                                        <div
                                            key={msg.id}
                                            className={`bs-message-row ${isGroupedWithPrevious ? "bs-message-row-grouped mb-1.5" : "mb-3"} flex ${msg.from === "me" ? "justify-end" : "justify-start"
                                                } ${vanishingMessages.has(msg.id) ? "incognito-vanish" : ""}`}
                                        >
                                            <div
                                                className={`bs-message-bubble-wrap ${String(highlightedMessageId) === String(msg.id) ? "bs-message-highlight" : ""} relative group max-w-[min(70%,680px)] ${msg.from === "me" ? "items-end bs-message-own" : "items-start bs-message-other"}`}
                                                data-message-reactions="true"
                                                data-message-id={String(msg.id)}
                                                id={`message-${msg.id}`}
                                                ref={(node) => {
                                                    const key = String(msg.id);
                                                    if (node) messageRefs.current[key] = node;
                                                    else delete messageRefs.current[key];
                                                }}
                                                onPointerDown={(event) => startMessagePress(event, msg.id, msg.from === "me" ? "right" : "left", canManageMessage)}
                                                onPointerMove={moveMessagePress}
                                                onPointerUp={(event) => finishMessagePress(event, msg.id, canReactToMessage)}
                                                onPointerCancel={cancelReactionPress}
                                                onMouseEnter={() => openReactionPaletteForDesktopHover(msg.id, canReactToMessage)}
                                                onMouseLeave={() => closeReactionPaletteForDesktopLeave(msg.id)}
                                                onContextMenu={(event) => {
                                                    if (window.matchMedia?.("(pointer: coarse)")?.matches) event.preventDefault();
                                                }}
                                            >
                                                {showGroupSenderName && (
                                                    <span className={`bs-message-sender-name ${msg.from === "me" ? "self" : ""}`}>
                                                        {groupSenderName}
                                                    </span>
                                                )}
                                                <div
                                                        className={`bs-message-bubble message-bubble w-fit px-4 py-2.5 text-sm leading-relaxed relative ${
                                                        msg.from === "me" ? "rounded-2xl rounded-br-md" : "rounded-2xl rounded-bl-md"
                                                    } ${msg.from === "me"
                                                            ? bubbleColors.me
                                                            : bubbleColors.them
                                                        } ${msg.isIncognito ? "ring-1 ring-cyan-400/35 shadow-cyan-950/20" : ""}`}
                                                >
                                                    {hasMessageActions && (
                                                        <div
                                                            data-message-actions="true"
                                                            className={`bs-message-action-anchor ${messageControlsActive ? "is-visible" : ""} absolute right-1.5 top-1.5 z-10`}
                                                            onPointerDown={(event) => event.stopPropagation()}
                                                            onPointerUp={(event) => event.stopPropagation()}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={(event) => openMessageActionMenu(event, msg.id, "right", canManageMessage)}
                                                                className="bs-message-action-trigger inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/15 bg-black/30 text-cyan-100/85 shadow-sm shadow-black/20 transition hover:border-cyan-300/45 hover:bg-cyan-300/15 hover:text-white"
                                                                title="Message actions"
                                                                aria-label="Message actions"
                                                            >
                                                                <MoreVertical size={14} />
                                                            </button>
                                                        </div>
                                                    )}
                                                    {isEditing ? (
                                                        <div className="min-w-[min(17rem,68vw)]">
                                                            <textarea
                                                                value={editingMessageText}
                                                                onChange={(event) => setEditingMessageText(event.target.value)}
                                                                autoFocus
                                                                rows={3}
                                                                className="w-full resize-none rounded-md border border-cyan-300/25 bg-black/45 px-3 py-2 text-sm text-white outline-none transition placeholder:text-neutral-500 focus:border-cyan-200/55 focus:ring-2 focus:ring-cyan-300/10"
                                                            />
                                                            <div className="mt-2 flex justify-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={cancelEditMessage}
                                                                    className="inline-flex h-8 items-center gap-1 rounded-md border border-neutral-700 px-2.5 text-xs font-bold text-neutral-200 transition hover:bg-neutral-900"
                                                                >
                                                                    <X size={13} /> Cancel
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => saveEditedMessage(msg)}
                                                                    className="inline-flex h-8 items-center gap-1 rounded-md border border-cyan-300/35 bg-cyan-300/10 px-2.5 text-xs font-black text-cyan-100 transition hover:bg-cyan-300/15"
                                                                >
                                                                    <Check size={13} /> Save
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {msg.replyTo && (
                                                                <button
                                                                    type="button"
                                                                    onPointerDown={(event) => event.stopPropagation()}
                                                                    onPointerUp={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        if (msg.replyTo?.unavailable) {
                                                                            showMessageToast("Original message unavailable");
                                                                            return;
                                                                        }
                                                                        if (msg.replyTo?.id) void scrollToMessage(msg.replyTo.id);
                                                                    }}
                                                                    className="bs-message-reply-quote mb-1.5 block w-full text-left transition"
                                                                >
                                                                    <span className="block truncate text-[11px] font-semibold text-cyan-50/85">
                                                                        {msg.replyTo.unavailable ? "Original message unavailable" : msg.replyTo.senderName || "Unknown"}
                                                                    </span>
                                                                    <span className="mt-0.5 block truncate text-[11px] text-white/55">
                                                                        {msg.replyTo.unavailable ? "Original message unavailable." : shortMessageText(msg.replyTo.content, 80)}
                                                                    </span>
                                                                </button>
                                                            )}
                                                            {msg.forwarded && (
                                                                <span className="bs-forwarded-label mb-1 inline-flex text-[11px] font-medium text-white/50">
                                                                    Forwarded
                                                                </span>
                                                            )}
                                                            <SharedPostMessage text={msg.text} />
                                                        </>
                                                    )}

                                                    {msg.from === "me" && (
                                                            <div className="bs-message-meta mt-1.5 flex items-center justify-end gap-1.5">
                                                            {msg.status !== 'sent' && (
                                                                <span className={`text-[10px] font-medium ${
                                                                    msg.status === 'read' ? 'text-emerald-300' :
                                                                    msg.status === 'failed' ? 'text-red-400' :
                                                                    msg.status === 'sending' ? 'text-cyan-100/70' :
                                                                    'text-white/60'
                                                                }`}>
                                                                    {msg.status === 'sending' && 'Sending'}
                                                                    {msg.status === 'delivered' && 'Delivered'}
                                                                    {msg.status === 'read' && 'Read'}
                                                                    {msg.status === 'failed' && 'Failed'}
                                                                </span>
                                                            )}
                                                            {msg.status === 'failed' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => retryMessage(msg)}
                                                                    disabled={isRetrying}
                                                                    className="rounded-md border border-red-400/30 px-1.5 py-0.5 text-[10px] font-semibold text-red-100 transition hover:bg-red-400/10 disabled:cursor-wait disabled:opacity-60"
                                                                >
                                                                    {isRetrying ? "Retrying" : "Retry"}
                                                                </button>
                                                            )}
                                                            <span className="text-[10px] text-white/55">
                                                                {msg.time}{msg.editedAt && msg.status !== "failed" ? " · edited" : ""}
                                                            </span>
                                                            {msg.isIncognito && <EyeOff size={11} className="shrink-0 text-cyan-300" />}
                                                        </div>
                                                    )}

                                                    {msg.from !== "me" && (
                                                        <div className="bs-message-meta mt-1.5 flex items-center justify-end gap-1.5">
                                                            <span className="text-[10px] opacity-60">
                                                                {msg.time}{msg.editedAt ? " · edited" : ""}
                                                            </span>
                                                            {msg.isIncognito && <EyeOff size={11} className="shrink-0 text-cyan-300" />}
                                                        </div>
                                                    )}
                                                </div>

                                                {canReactToMessage && !(menuOpen || showExportMenu) && (
                                                <div
                                                    style={reactionPaletteStyle}
                                                    className={`bs-message-reaction-palette ${reactionPickerOpen ? "is-open" : ""} ${
                                                        useFloatingReactionPicker
                                                            ? `bs-message-reaction-palette-floating ${reactionPalettePosition!.placement === "above" ? "is-above" : "is-below"} fixed z-[3200]`
                                                            : `absolute -top-8 z-10 ${msg.from === "me" ? "right-0" : "left-0"}`
                                                    } opacity-0 transition-opacity duration-200 flex items-center gap-1 rounded-md border border-neutral-800 bg-neutral-950/95 p-1 shadow-xl shadow-black/40`}
                                                >
                                                    {REACTION_OPTIONS.map((reaction) => {
                                                        const active = myReaction === reaction.id;
                                                        return (
                                                            <button
                                                                key={reaction.id}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    toggleReaction(msg.id, reaction.id);
                                                                    setOpenReactionMessageId(null);
                                                                    setReactionPalettePosition(null);
                                                                }}
                                                                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs transition ${
                                                                    active ? "bg-cyan-400/20 text-cyan-100" : "hover:bg-neutral-800"
                                                                }`}
                                                                title={active ? `Remove ${reaction.label}` : reaction.label}
                                                            >
                                                                {reaction.value}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                )}

                                                {hasReactions && (
                                                    <div className={`bs-message-reaction-strip flex flex-wrap gap-1 ${msg.from === "me" ? "justify-end" : "justify-start"}`}>
                                                        {REACTION_OPTIONS.filter(option => reactionCounts[option.id]).map(option => (
                                                            <button
                                                                type="button"
                                                                key={option.id}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    toggleReaction(msg.id, option.id);
                                                                }}
                                                                className={`inline-flex h-5 min-w-5 items-center gap-1 rounded-full border px-1.5 text-[11px] shadow-sm shadow-black/30 ${
                                                                    myReaction === option.id
                                                                        ? "border-cyan-400/30 bg-cyan-400/10 text-cyan-100"
                                                                        : "border-neutral-800 bg-neutral-900 text-neutral-300"
                                                                }`}
                                                                >
                                                                    {renderReaction(option.id)}
                                                                {selectedChat.isGroup && (reactionCounts[option.id] || 0) > 1 && (
                                                                    <span className="text-[10px] text-neutral-400">{reactionCounts[option.id]}</span>
                                                                )}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                <div ref={messagesEndRef} />


                            </div>

                            {/* Typing Indicator */}
                            {typingUsers.has(selectedChat.id) && (
                                <div className="px-6 py-2">
                                    <div className="flex items-center gap-2 text-sm text-neutral-400">
                                        <div className="flex gap-1">
                                            <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                            <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                        </div>
                                        <span>is typing...</span>
                                    </div>
                                </div>
                            )}

                            {/* Input */}
                            {selectedChat.isBlocked ? (
                                <div className="bs-chat-blocked-state flex items-center justify-center px-6 py-4 border-t border-neutral-800 bg-neutral-950 gap-3">
                                    <span className="text-neutral-400 text-sm">You can not message this user.</span>
                                    {canUnblockSelectedChat && (
                                        <button
                                            onClick={() => {
                                                const entry = blocked.find(b => b.conversationId === String(selectedChat.id) || b.id === selectedChat.id);
                                                if (entry) {
                                                    unblockUser(entry);
                                                } else {
                                                    void handleBlockUser(selectedChat);
                                                }
                                            }}
                                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-600 text-white text-sm font-medium transition-colors"
                                        >
                                            <Unlock size={14} /> Unblock
                                        </button>
                                    )}
                                </div>
                            ) : selectedMessagingDisabled ? (
                                <div className="bs-chat-disabled-state border-t border-cyan-400/15 bg-black px-6 py-4">
                                    <div className="rounded-md border border-cyan-300/20 bg-cyan-300/10 px-4 py-3">
                                        <p className="text-sm font-black text-cyan-100">
                                            {selectedChat.hasLeftGroup
                                                ? "You left this group"
                                                : selectedRequestPendingByMe
                                                ? "Message request pending"
                                                : selectedChat.requestStatus === "declined"
                                                    ? "Message request declined"
                                                    : "Message request needs review"}
                                        </p>
                                        <p className="mt-1 text-xs font-semibold text-cyan-100/60">
                                            {selectedChat.hasLeftGroup
                                                ? "The chat history stays here. You will not receive new group messages unless you are added again."
                                                : selectedRequestPendingByMe
                                                ? "You can continue after they accept the request."
                                                : selectedChat.requestStatus === "declined"
                                                    ? "This conversation is closed."
                                                    : "Accept the request from Requests before replying."}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                            <div className={`bs-chat-composer-shell border-t px-6 py-3 ${isCurrentChatIncognito ? "border-cyan-500/20 bg-gradient-to-r from-black via-cyan-950/10 to-black" : "border-neutral-800 bg-black"}`}>
                                {replyDraft && (
                                    <div className="bs-reply-preview mb-2 flex items-center gap-2 rounded-md border border-cyan-300/20 bg-cyan-300/10 px-3 py-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs font-semibold text-cyan-50/85">
                                                Replying to {replyDraft.from === "me" ? "you" : replyDraft.from}
                                            </p>
                                            <p className="mt-0.5 truncate text-xs text-cyan-50/70">{shortMessageText(replyDraft.text, 110)}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setReplyDraft(null)}
                                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-cyan-200/20 text-cyan-100/70 transition hover:bg-cyan-300/10 hover:text-white"
                                            aria-label="Cancel reply"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                )}
                            <div className="bs-chat-composer flex items-center gap-3">
                                <div className="bs-chat-emoji-wrap relative" ref={emojiPickerRef}>
                                    <button
                                        type="button"
                                        onClick={() => setShowEmojiPicker(prev => !prev)}
                                        className={`bs-chat-tool-button group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all ${
                                            showEmojiPicker
                                                ? "border-cyan-400/40 bg-cyan-400/15 text-cyan-200 shadow-cyan-500/10"
                                                : isCurrentChatIncognito
                                                    ? "border-cyan-500/20 bg-cyan-950/10 text-cyan-200/70 hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:text-cyan-100"
                                                    : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-purple-500/40 hover:bg-purple-500/10 hover:text-purple-200"
                                        }`}
                                        title="Emoji"
                                        aria-label="Emoji"
                                    >
                                        <Smile size={18} className="transition group-hover:scale-105" />
                                    </button>
                                    {showEmojiPicker && (
                                        <div className="absolute bottom-12 left-0 z-40 w-64 rounded-lg border border-neutral-800 bg-neutral-950 p-3 shadow-2xl shadow-black/60">
                                            <div className="mb-2 flex items-center justify-between">
                                                <span className="text-xs font-semibold text-neutral-300">Emoji</span>
                                                <span className="text-[11px] text-neutral-600">Click to insert</span>
                                            </div>
                                            <div className="grid grid-cols-6 gap-1">
                                                {EMOJI_OPTIONS.map((emoji) => (
                                                    <button
                                                        key={emoji}
                                                        type="button"
                                                        onClick={() => insertEmoji(emoji)}
                                                        className="flex h-8 w-8 items-center justify-center rounded-md text-lg transition hover:bg-neutral-800"
                                                    >
                                                        {emoji}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {ENABLE_ATTACHMENTS && (
                                    <button
                                        type="button"
                                        title="Attach file"
                                        aria-label="Attach file"
                                        className={`bs-chat-tool-button group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all ${
                                            isCurrentChatIncognito
                                                ? "border-cyan-500/20 bg-cyan-950/10 text-cyan-200/70 hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:text-cyan-100"
                                                : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-sky-500/40 hover:bg-sky-500/10 hover:text-sky-200"
                                        }`}
                                    >
                                        <Paperclip size={18} className="transition group-hover:-rotate-6 group-hover:scale-105" />
                                    </button>
                                )}
                                {ENABLE_VOICE_NOTES && (
                                    <button
                                        type="button"
                                        onMouseDown={startVoiceRecording}
                                        onMouseUp={stopVoiceRecording}
                                        onTouchStart={startVoiceRecording}
                                        onTouchEnd={stopVoiceRecording}
                                        className={`bs-chat-tool-button bs-chat-mic-button group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all ${isRecording
                                                ? 'border-red-400/40 bg-red-500/15 text-red-100 shadow-red-500/20'
                                                : isCurrentChatIncognito
                                                    ? 'border-cyan-500/20 bg-cyan-950/10 text-cyan-200/70 hover:border-cyan-400/40 hover:bg-cyan-400/10 hover:text-cyan-100'
                                                    : 'border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-rose-500/40 hover:bg-rose-500/10 hover:text-rose-200'
                                            }`}
                                        title="Hold to record"
                                        aria-label="Hold to record"
                                    >
                                        <Mic size={18} className="transition group-hover:scale-105" />
                                    </button>
                                )}
                                <input
                                    ref={messageInputRef}
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => {
                                        setNewMessage(e.target.value);
                                        if (!isCurrentChatIncognito && canUseLiveChatSignals(selectedChat)) {
                                            const socket = getSocket();
                                            if (socket && selectedChat && typeof selectedChat.id === 'string') {
                                                const now = Date.now();
                                                if (now - typingLastEmitRef.current > 1200) {
                                                    socket.emit('typing:start', { conversationId: selectedChat.id });
                                                    typingLastEmitRef.current = now;
                                                }
                                                if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
                                                typingTimerRef.current = setTimeout(() => {
                                                    socket.emit('typing:stop', { conversationId: selectedChat.id });
                                                    typingLastEmitRef.current = 0;
                                                }, 2000);
                                            }
                                        }
                                    }}
                                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                    placeholder={isCurrentChatIncognito ? "Incognito message..." : "Message..."}
                                    className={`bs-chat-input min-w-0 flex-1 rounded-md border px-4 py-2.5 text-sm text-white transition-all focus:outline-none focus:ring-2 ${
                                        isCurrentChatIncognito
                                            ? "border-cyan-500/30 bg-cyan-950/20 placeholder:text-cyan-200/35 focus:border-cyan-400/50 focus:ring-cyan-500/30"
                                            : "border-neutral-800 bg-neutral-950 placeholder:text-neutral-600 focus:border-purple-500/50 focus:ring-purple-500/25"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={handleSend}
                                    disabled={!newMessage.trim()}
                                    className={`bs-chat-send-button group inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border transition-all ${newMessage.trim()
                                            ? isCurrentChatIncognito
                                                ? 'border-cyan-300/30 bg-cyan-400 text-black shadow-lg shadow-cyan-500/20 hover:bg-cyan-300 hover:scale-[1.03]'
                                                : 'border-purple-400/30 bg-purple-600 text-white shadow-lg shadow-purple-700/20 hover:bg-purple-500 hover:scale-[1.03]'
                                            : 'cursor-not-allowed border-neutral-800 bg-neutral-950 text-neutral-600'
                                        }`}
                                    title="Send message"
                                    aria-label="Send message"
                                >
                                    <Send size={18} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </button>
                            </div>
                            </div>
                            )}
                        </>
                    ) : (
                        <div className="flex-1 bg-black px-8 py-10">
                            <div className="flex h-full items-center justify-center rounded-2xl border border-neutral-900 bg-neutral-950/60">
                                <div className="max-w-sm text-center">
                                    <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-neutral-800 bg-black text-cyan-300">
                                        <MessageCircle size={28} />
                                    </span>
                                    <p className="mb-2 text-lg font-semibold text-white">Create a conversation</p>
                                    <p className="text-sm text-neutral-500">Search, filter, or start a new conversation from the inbox on the left.</p>
                                    <button
                                        onClick={() => setShowNewChatModal(true)}
                                        className="mt-5 inline-flex items-center gap-2 rounded-md bg-cyan-400 px-4 py-2 text-sm font-semibold text-black transition hover:bg-cyan-300"
                                    >
                                        <Plus size={16} /> New conversation
                                    </button>
                                    {(globalIncognitoEnabled || incognitoChatIds.size > 0) && (
                                        <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-200">
                                            <EyeOff size={13} />
                                            {globalIncognitoEnabled ? "Incognito active" : `Incognito active on ${incognitoChatIds.size} chat${incognitoChatIds.size > 1 ? 's' : ''}`}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {actionMessage && selectedChat && messageActionMenuPosition && createPortal(
                <div
                    data-message-actions="true"
                    className={`bs-message-action-menu bs-message-action-menu-floating ${messageActionMenuPosition.placement === "above" ? "is-above" : "is-below"} fixed z-[3200] overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-2xl shadow-black/50`}
                    style={{
                        top: messageActionMenuPosition.top,
                        left: messageActionMenuPosition.left,
                        width: 168,
                    }}
                >
                    {actionMessageCanManage && (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    beginEditMessage(actionMessage);
                                    closeMessageActionMenu();
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-cyan-100 transition hover:bg-cyan-400/10"
                            >
                                <Pencil size={14} /> Edit
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    void deleteChatMessage(actionMessage);
                                    closeMessageActionMenu();
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-red-200 transition hover:bg-red-500/10"
                            >
                                <Trash2 size={14} /> Delete
                            </button>
                        </>
                    )}
                    {actionMessageCanReply && (
                        <button
                            type="button"
                            onClick={() => beginReplyToMessage(actionMessage)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                        >
                            <MessageCircle size={14} className="text-cyan-300" /> Reply
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => copyChatMessage(actionMessage)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                    >
                        <Copy size={14} className="text-neutral-300" /> {String(copiedMessageId) === String(actionMessage.id) ? "Copied" : "Copy"}
                    </button>
                    <button
                        type="button"
                        onClick={() => beginForwardMessage(actionMessage)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-neutral-200 transition hover:bg-neutral-900"
                    >
                        <Share2 size={14} className="text-violet-300" /> Forward
                    </button>
                    {actionMessage.from !== "me" && (
                        <button
                            type="button"
                            onClick={() => void reportChatMessage(actionMessage)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-amber-100 transition hover:bg-amber-500/10"
                        >
                            <Shield size={14} className="text-amber-300" /> Report
                        </button>
                    )}
                </div>,
                document.body
            )}

            {forwardMessage && createPortal(
                <div
                    className="fixed inset-0 z-[3300] flex items-end justify-center bg-black/55 px-3 pb-3 backdrop-blur-sm sm:items-center sm:pb-0"
                    onClick={closeForwardPicker}
                >
                    <div
                        className="bs-forward-picker flex max-h-[min(42rem,calc(100svh-1.5rem))] w-full max-w-md flex-col overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-2xl shadow-black/60"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="border-b border-neutral-900 px-4 py-3">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-black text-white">Forward message</p>
                                    <p className="mt-0.5 truncate text-xs text-neutral-500">{shortMessageText(forwardMessage.text, 90)}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeForwardPicker}
                                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-neutral-800 text-neutral-300 transition hover:bg-neutral-900 hover:text-white"
                                    aria-label="Close forward picker"
                                >
                                    <X size={15} />
                                </button>
                            </div>
                        </div>
                        <div className="border-b border-neutral-900 px-4 py-3">
                            <div className="flex items-center gap-2 rounded-md border border-neutral-800 bg-black/45 px-3 py-2">
                                <Search size={15} className="shrink-0 text-neutral-500" />
                                <input
                                    type="text"
                                    value={forwardSearch}
                                    onChange={(event) => setForwardSearch(event.target.value)}
                                    placeholder="Search chats, users, or groups"
                                    className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-neutral-600"
                                    autoFocus
                                />
                                {forwardSearching && <Loader size={14} className="shrink-0 animate-spin text-cyan-200" />}
                            </div>
                            {forwardTargets.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {forwardTargets.map(target => (
                                        <button
                                            type="button"
                                            key={forwardTargetKey(target)}
                                            onClick={() => removeForwardTarget(target)}
                                            className="inline-flex max-w-[11rem] items-center gap-1.5 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-2 py-1 text-xs font-semibold text-cyan-50 transition hover:bg-cyan-300/15"
                                            title={`Remove ${target.name}`}
                                        >
                                            <span className="truncate">{target.name}</span>
                                            <X size={12} className="shrink-0 text-cyan-100/70" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="hidden-scrollbar flex-1 overflow-y-auto p-2">
                            {forwardChatResults.length > 0 && (
                                <div className="px-2 pb-1 pt-1 text-[11px] font-semibold text-neutral-500">
                                    Chats and groups
                                </div>
                            )}
                            {forwardChatResults.map(chat => {
                                const target = chatToForwardTarget(chat);
                                const selected = isForwardTargetSelected(target);
                                const disabled = Boolean(target.disabledReason);
                                return (
                                    <button
                                        type="button"
                                        key={chat.id}
                                        onClick={() => toggleForwardTarget(target)}
                                        disabled={disabled}
                                        className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition ${
                                            selected
                                                ? "border-cyan-300/35 bg-cyan-300/10"
                                                : disabled
                                                    ? "cursor-not-allowed border-transparent opacity-55"
                                                    : "border-transparent hover:border-neutral-800 hover:bg-neutral-900/70"
                                        }`}
                                    >
                                        <img src={chat.avatar} alt={chat.name} className="h-9 w-9 rounded-md object-cover" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-semibold text-white">{chat.name}</span>
                                            <span className="block truncate text-[11px] text-neutral-500">{target.disabledReason || (chat.isGroup ? "Group" : "Chat")}</span>
                                        </span>
                                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                                            selected
                                                ? "border-cyan-300 bg-cyan-300 text-black"
                                                : "border-neutral-700"
                                        }`}>
                                            {selected && <Check size={13} />}
                                        </span>
                                    </button>
                                );
                            })}
                            {forwardUserTargets.length > 0 && (
                                <div className="px-2 pb-1 pt-3 text-[11px] font-semibold text-neutral-500">
                                    People
                                </div>
                            )}
                            {forwardUserTargets.map(target => {
                                const selected = isForwardTargetSelected(target);
                                const disabled = Boolean(target.disabledReason);
                                return (
                                    <button
                                        type="button"
                                        key={forwardTargetKey(target)}
                                        onClick={() => toggleForwardTarget(target)}
                                        disabled={disabled}
                                        className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition ${
                                            selected
                                                ? "border-cyan-300/35 bg-cyan-300/10"
                                                : disabled
                                                    ? "cursor-not-allowed border-transparent opacity-55"
                                                    : "border-transparent hover:border-neutral-800 hover:bg-neutral-900/70"
                                        }`}
                                    >
                                        <img src={target.avatar} alt={target.name} className="h-9 w-9 rounded-md object-cover" />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm font-semibold text-white">{target.name}</span>
                                            <span className="block truncate text-[11px] text-neutral-500">{target.disabledReason || "Chat"}</span>
                                        </span>
                                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border ${
                                            selected
                                                ? "border-cyan-300 bg-cyan-300 text-black"
                                                : "border-neutral-700"
                                        }`}>
                                            {selected && <Check size={13} />}
                                        </span>
                                    </button>
                                );
                            })}
                            {!forwardChatResults.length && !forwardUserTargets.length && !forwardSearching && (
                                <div className="px-4 py-8 text-center text-sm text-neutral-500">
                                    {forwardSearch.trim().length >= 2 ? "No matches found." : "Search or choose a recent chat."}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-neutral-900 px-4 py-3">
                            <button
                                type="button"
                                onClick={closeForwardPicker}
                                className="inline-flex h-9 items-center rounded-md border border-neutral-800 px-3 text-xs font-bold text-neutral-300 transition hover:bg-neutral-900"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => void sendForwardedMessage()}
                                disabled={!forwardTargets.length || forwardSending}
                                className="inline-flex h-9 items-center gap-2 rounded-md border border-cyan-300/30 bg-cyan-300/10 px-3 text-xs font-black text-cyan-100 transition hover:bg-cyan-300/15 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {forwardSending ? <Loader size={14} className="animate-spin" /> : <Share2 size={14} />}
                                {forwardSending ? "Sending" : "Send"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {messageToast && createPortal(
                <div className="bs-copy-toast fixed bottom-[calc(5.25rem+env(safe-area-inset-bottom))] left-1/2 z-[3400] -translate-x-1/2 rounded-full border border-emerald-300/25 bg-neutral-950/95 px-3 py-1.5 text-xs font-semibold text-emerald-100 shadow-2xl shadow-black/45 backdrop-blur">
                    {messageToast}
                </div>,
                document.body
            )}

            <FooterNav />

            {showGroupPanel && selectedChat?.isGroup && (
                <div
                    className="bs-group-panel-overlay fixed inset-0 z-[2300] flex items-center justify-center bg-black/55 px-3 backdrop-blur-md"
                    onClick={closeGroupPanel}
                >
                    <div
                        className="bs-group-panel-modal hidden-scrollbar flex max-h-[min(44rem,calc(100svh-2rem))] w-full max-w-3xl flex-col overflow-y-auto rounded-lg border border-cyan-900/45 bg-gradient-to-br from-neutral-950/88 via-black/90 to-cyan-950/18 shadow-[0_0_100px_rgba(8,145,178,0.24)] backdrop-blur-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="bs-group-panel-head sticky top-0 z-10 border-b border-neutral-800 bg-black/55 px-5 py-4 backdrop-blur-xl">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <span className="bs-group-panel-icon inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-cyan-400/25 bg-cyan-400/10 text-cyan-200">
                                        <UsersRound size={20} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h2 className="truncate text-lg font-black text-white">{groupDetails?.groupName || selectedChat.name}</h2>
                                            <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-black ${groupRoleClass(groupDetails?.myGroupRole)}`}>
                                                {selectedChat.hasLeftGroup ? <LogOut size={13} /> : groupRoleIcon(groupDetails?.myGroupRole)}
                                                {selectedChat.hasLeftGroup ? "Left" : groupRoleLabel(groupDetails?.myGroupRole)}
                                            </span>
                                        </div>
                                        <p className="mt-1 text-xs text-neutral-500">
                                            Group info - {visibleGroupMemberCount || 0} {(visibleGroupMemberCount || 0) === 1 ? "member" : "members"}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeGroupPanel}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-neutral-800 bg-black/60 text-neutral-300 transition hover:border-cyan-400/40 hover:text-white"
                                    aria-label="Close group panel"
                                >
                                    <X size={17} />
                                </button>
                            </div>
                            {(groupToast || groupError) && (
                                <div className={`mt-3 rounded-md border px-3 py-2 text-xs font-semibold ${
                                    groupError
                                        ? "border-rose-400/25 bg-rose-500/10 text-rose-200"
                                        : "border-emerald-400/25 bg-emerald-400/10 text-emerald-200"
                                }`}>
                                    {groupError || groupToast}
                                </div>
                            )}
                        </div>

                        {groupLoading ? (
                            <div className="grid gap-3 p-5 sm:grid-cols-2">
                                {[0, 1, 2, 3].map(item => (
                                    <div key={item} className="h-28 animate-pulse rounded-lg border border-neutral-900 bg-neutral-900/50" />
                                ))}
                            </div>
                        ) : (
                            <div className="bs-group-panel-body grid gap-4 p-5 lg:grid-cols-[0.9fr_1.1fr]">
                                <section className="bs-group-panel-main space-y-4">
                                    <div className="bs-group-info-card bs-group-identity-card rounded-lg border border-neutral-800 bg-black/40 p-4">
                                        <div className="mb-3 flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-300/80">Identity</p>
                                                <p className="mt-1 text-xs text-neutral-500">Name and purpose visible to members.</p>
                                            </div>
                                        </div>
                                        <label className="bs-group-field-label text-xs font-semibold text-neutral-400">Group name</label>
                                        <div className="bs-group-field-shell mt-2">
                                            <Pencil size={14} className="shrink-0 text-cyan-200/70" />
                                            <input
                                                value={groupNameDraft}
                                                onChange={(event) => setGroupNameDraft(event.target.value)}
                                                disabled={!isGroupManager}
                                                className="bs-group-field-input min-w-0 flex-1"
                                                maxLength={60}
                                                placeholder="Name this group"
                                            />
                                        </div>
                                        <label className="bs-group-field-label mt-4 block text-xs font-semibold text-neutral-400">Description</label>
                                        <div className="bs-group-textarea-shell mt-2">
                                            <Pencil size={14} className="mt-1 shrink-0 text-cyan-200/70" />
                                            <textarea
                                                value={groupDescriptionDraft}
                                                onChange={(event) => setGroupDescriptionDraft(event.target.value)}
                                                disabled={!isGroupManager}
                                                className="bs-group-field-input hidden-scrollbar min-h-20 min-w-0 flex-1 resize-none"
                                                maxLength={240}
                                                placeholder="What is this group for?"
                                            />
                                        </div>
                                        {isGroupManager && (
                                            <button
                                                type="button"
                                                onClick={handleSaveGroupInfo}
                                                disabled={groupAction === "save-info"}
                                                className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-cyan-300/35 bg-cyan-400 px-3 text-sm font-black text-black transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                {groupAction === "save-info" ? <Loader size={15} className="animate-spin" /> : <Save size={15} />}
                                                Save group
                                            </button>
                                        )}
                                    </div>

                                    <div className="bs-group-info-card bs-group-invite-card rounded-lg border border-neutral-800 bg-black/40 p-4">
                                        <div className="mb-3 flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300/80">Invite members</p>
                                                <p className="mt-1 text-xs text-neutral-500">
                                                    {isGroupManager ? "Search live users and send a group invite." : "Only group admins can invite members."}
                                                </p>
                                            </div>
                                            <UserPlus size={16} className="text-emerald-300" />
                                        </div>
                                        <div className="bs-group-field-shell bs-group-invite-search-shell">
                                            <Search size={14} className="shrink-0 text-emerald-200/75" />
                                            <input
                                                value={groupMemberSearch}
                                                onChange={(event) => setGroupMemberSearch(event.target.value)}
                                                disabled={!isGroupManager}
                                                className="bs-group-field-input min-w-0 flex-1"
                                                placeholder="Search people to invite"
                                            />
                                        </div>
                                        <div className="mt-3 space-y-2">
                                            {groupMemberSearching && (
                                                <p className="rounded-md border border-neutral-900 bg-neutral-950 px-3 py-2 text-xs text-neutral-500">Searching...</p>
                                            )}
                                            {!groupMemberSearching && groupMemberSearch.trim().length >= 2 && groupMemberResults.length === 0 && (
                                                <p className="rounded-md border border-neutral-900 bg-neutral-950 px-3 py-2 text-xs text-neutral-500">No addable users found.</p>
                                            )}
                                            {groupMemberResults.slice(0, 5).map(user => {
                                                const label = user.displayName || user.username;
                                                const busy = groupAction === `add-${user.id}`;
                                                return (
                                                    <div key={user.id} className="flex items-center gap-3 rounded-md border border-neutral-900 bg-neutral-950/70 p-2">
                                                        <img
                                                            src={user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=111&color=67e8f9`}
                                                            alt={label}
                                                            className="h-9 w-9 rounded-md object-cover"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate text-sm font-semibold text-white">{label}</p>
                                                            <p className="truncate text-xs text-neutral-500">@{user.username}</p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAddMemberToGroup(user)}
                                                            disabled={busy}
                                                            className="inline-flex h-8 items-center justify-center gap-1 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-3 text-xs font-black text-emerald-200 transition hover:bg-emerald-400/15 disabled:opacity-60"
                                                        >
                                                            {busy ? <Loader size={13} className="animate-spin" /> : <Plus size={13} />}
                                                            Invite
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {isGroupManager && (
                                        <div className="bs-group-info-card bs-group-code-card rounded-lg border border-neutral-800 bg-black/40 p-4">
                                            <div className="mb-3 flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-sky-300/80">Invite code</p>
                                                    <p className="mt-1 text-xs text-neutral-500">Share this code with people who should request entry.</p>
                                                </div>
                                                <Share2 size={16} className="text-sky-300" />
                                            </div>
                                            <div className="bs-group-code-shell flex items-center gap-2 rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2">
                                                <code className="min-w-0 flex-1 truncate text-sm font-black text-cyan-100">
                                                    {groupDetails?.invite?.enabled === false
                                                        ? "Invite Disabled"
                                                        : groupDetails?.invite?.code || "No code yet"}
                                                </code>
                                                <button
                                                    type="button"
                                                    onClick={handleToggleGroupCode}
                                                    disabled={groupAction === "invite-code-toggle"}
                                                    className="inline-flex h-8 items-center justify-center rounded-md border border-neutral-700 bg-neutral-900 px-2 text-xs font-black text-neutral-200 transition hover:bg-neutral-800 disabled:opacity-60"
                                                >
                                                    {groupAction === "invite-code-toggle"
                                                        ? <Loader size={13} className="animate-spin" />
                                                        : groupDetails?.invite?.enabled === false ? "Enable" : "Disable"}
                                                </button>
                                                {groupDetails?.invite?.enabled !== false && (
                                                    <button
                                                        type="button"
                                                        onClick={handleRegenerateGroupCode}
                                                        disabled={groupAction === "invite-code"}
                                                        className="bs-group-code-refresh inline-flex h-8 w-8 items-center justify-center rounded-md border border-sky-400/30 bg-sky-400/10 text-sky-200 transition hover:bg-sky-400/15 disabled:opacity-60"
                                                        title="Refresh invite code"
                                                        aria-label="Refresh invite code"
                                                    >
                                                        {groupAction === "invite-code" ? <Loader size={13} className="animate-spin" /> : <RotateCcw size={14} />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {isGroupOwner && groupDetails?.permissions && (
                                        <div className="bs-group-info-card bs-group-settings-card rounded-lg border border-neutral-800 bg-black/40 p-4 lg:col-span-2">
                                            <div className="mb-3 flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-[10px] font-black tracking-[0.18em] text-violet-300/80">Group Settings</p>
                                                    <p className="mt-1 text-xs text-neutral-500">Organized Controls</p>
                                                </div>
                                                <Shield size={16} className="text-violet-300" />
                                            </div>
                                            <div className="space-y-3">
                                                <details className={groupRulesSectionClass}>
                                                    <summary className={groupRulesSummaryClass}>
                                                        <span>Access</span>
                                                        <span className="text-[11px] font-semibold text-neutral-500">Visibility and roles</span>
                                                    </summary>
                                                    <div className="grid gap-3 border-t border-neutral-900 p-3 sm:grid-cols-2">
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Invite power</span>
                                                            <span className={groupSettingHintClass}>Who can invite new people.</span>
                                                            {renderGroupChoiceControl(groupDetails.permissions.addMembers, [
                                                                { value: "owner", label: "Owner only" },
                                                                { value: "admins", label: "Admins" },
                                                                { value: "members", label: "Everyone" },
                                                            ], (value) => handleGroupPermissionChange("addMembers", value), "Invite power")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Visibility</span>
                                                            <span className={groupSettingHintClass}>How the group can be discovered.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.visibility || "invite_only", [
                                                                { value: "private", label: "Private" },
                                                                { value: "invite_only", label: "Invite only" },
                                                                { value: "code_request", label: "Code request" },
                                                            ], (value) => handleGroupSettingChange("visibility", value), "Visibility")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Group mode</span>
                                                            <span className={groupSettingHintClass}>How strictly this group is moderated.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.groupType || "normal", [
                                                                { value: "normal", label: "Standard" },
                                                                { value: "protected", label: "Protected" },
                                                                { value: "authority_controlled", label: "Guided" },
                                                            ], (value) => handleGroupSettingChange("groupType", value), "Group mode")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Edit info</span>
                                                            <span className={groupSettingHintClass}>Who can change name and details.</span>
                                                            {renderGroupChoiceControl(groupDetails.permissions.editInfo, [
                                                                { value: "owner", label: "Owner only" },
                                                                { value: "admins", label: "Admins" },
                                                            ], (value) => handleGroupPermissionChange("editInfo", value), "Edit info")}
                                                        </label>
                                                    </div>
                                                </details>

                                                <details className={groupRulesSectionClass}>
                                                    <summary className={groupRulesSummaryClass}>
                                                        <span>Invites</span>
                                                        <span className="text-[11px] font-semibold text-neutral-500">Trust, approval, limits</span>
                                                    </summary>
                                                    <div className="grid gap-3 border-t border-neutral-900 p-3 sm:grid-cols-2">
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Invite trust</span>
                                                            <span className={groupSettingHintClass}>Relationship required before inviting.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.inviteRelationship || "accepted", [
                                                                { value: "accepted", label: "Accepted chats" },
                                                                { value: "friends", label: "Followers" },
                                                                { value: "shared_space", label: "Shared spaces" },
                                                                { value: "anyone", label: "Anyone searchable" },
                                                            ], (value) => handleGroupSettingChange("inviteRelationship", value), "Invite trust")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Invite expiry</span>
                                                            <span className={groupSettingHintClass}>Hours before an invite expires.</span>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={720}
                                                                value={groupDetails.settings?.inviteExpiryHours ?? 168}
                                                                onChange={(event) => handleGroupSettingChange("inviteExpiryHours", Number(event.target.value))}
                                                                className={groupControlClass}
                                                            />
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Code uses</span>
                                                            <span className={groupSettingHintClass}>0 means no usage limit.</span>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={100000}
                                                                value={groupDetails.settings?.inviteCodeMaxUses ?? 0}
                                                                onChange={(event) => handleGroupSettingChange("inviteCodeMaxUses", Number(event.target.value))}
                                                                className={groupControlClass}
                                                            />
                                                        </label>
                                                        <div className="grid gap-2">
                                                            <label className="flex items-center justify-between gap-3 rounded-md border border-neutral-900 bg-neutral-950/75 px-3 py-2 text-xs font-semibold text-neutral-300">
                                                                <span>
                                                                    <span className="block text-xs font-bold text-neutral-200">Invite approval</span>
                                                                    <span className="block text-[11px] font-medium text-neutral-500">Review invites before they land.</span>
                                                                </span>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={groupDetails.settings?.inviteRequiresApproval !== false}
                                                                    onChange={(event) => handleGroupSettingChange("inviteRequiresApproval", event.target.checked)}
                                                                />
                                                            </label>
                                                            <label className="flex items-center justify-between gap-3 rounded-md border border-neutral-900 bg-neutral-950/75 px-3 py-2 text-xs font-semibold text-neutral-300">
                                                                <span>
                                                                    <span className="block text-xs font-bold text-neutral-200">Join approval</span>
                                                                    <span className="block text-[11px] font-medium text-neutral-500">Approve code requests manually.</span>
                                                                </span>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={groupDetails.settings?.joinApprovalRequired !== false}
                                                                    onChange={(event) => handleGroupSettingChange("joinApprovalRequired", event.target.checked)}
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </details>

                                                <details className={groupRulesSectionClass}>
                                                    <summary className={groupRulesSummaryClass}>
                                                        <span>Messaging</span>
                                                        <span className="text-[11px] font-semibold text-neutral-500">Send power and pacing</span>
                                                    </summary>
                                                    <div className="grid gap-3 border-t border-neutral-900 p-3 sm:grid-cols-2">
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Messaging</span>
                                                            <span className={groupSettingHintClass}>Who can send messages here.</span>
                                                            {renderGroupChoiceControl(groupDetails.permissions.sendMessages, [
                                                                { value: "everyone", label: "Everyone" },
                                                                { value: "admins", label: "Admins only" },
                                                                { value: "owner", label: "Owner only" },
                                                                { value: "locked", label: "Locked" },
                                                            ], (value) => handleGroupPermissionChange("sendMessages", value), "Messaging")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Slow mode</span>
                                                            <span className={groupSettingHintClass}>Seconds between member messages.</span>
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={3600}
                                                                value={groupDetails.settings?.slowModeSeconds ?? 0}
                                                                onChange={(event) => handleGroupSettingChange("slowModeSeconds", Number(event.target.value))}
                                                                className={groupControlClass}
                                                            />
                                                        </label>
                                                    </div>
                                                </details>

                                                <details className={groupRulesSectionClass}>
                                                    <summary className={groupRulesSummaryClass}>
                                                        <span>Privacy</span>
                                                        <span className="text-[11px] font-semibold text-neutral-500">Signals and counts</span>
                                                    </summary>
                                                    <div className="grid gap-3 border-t border-neutral-900 p-3 sm:grid-cols-2">
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Member count</span>
                                                            <span className={groupSettingHintClass}>Who can see the total.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.memberCountVisibility || "members", [
                                                                { value: "members", label: "Members" },
                                                                { value: "admins", label: "Admins" },
                                                                { value: "hidden", label: "Hidden" },
                                                            ], (value) => handleGroupSettingChange("memberCountVisibility", value), "Member count")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Activity status</span>
                                                            <span className={groupSettingHintClass}>Who can see live status.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.memberPresenceVisibility || "normal", [
                                                                { value: "normal", label: "Normal" },
                                                                { value: "admins", label: "Admins" },
                                                                { value: "hidden", label: "Hidden" },
                                                            ], (value) => handleGroupSettingChange("memberPresenceVisibility", value), "Activity status")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Typing</span>
                                                            <span className={groupSettingHintClass}>Who can see typing signals.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.typingVisibility || "normal", [
                                                                { value: "normal", label: "Normal" },
                                                                { value: "admins", label: "Admins" },
                                                                { value: "hidden", label: "Hidden" },
                                                            ], (value) => handleGroupSettingChange("typingVisibility", value), "Typing")}
                                                        </label>
                                                        <label className={groupSettingRowClass}>
                                                            <span className={groupSettingTitleClass}>Read receipts</span>
                                                            <span className={groupSettingHintClass}>Who can see read state.</span>
                                                            {renderGroupChoiceControl(groupDetails.settings?.readReceiptVisibility || "normal", [
                                                                { value: "normal", label: "Normal" },
                                                                { value: "admins", label: "Admins" },
                                                                { value: "hidden", label: "Hidden" },
                                                            ], (value) => handleGroupSettingChange("readReceiptVisibility", value), "Read receipts")}
                                                        </label>
                                                    </div>
                                                </details>
                                            </div>
                                        </div>
                                    )}

                                    {isGroupManager && Boolean(groupDetails?.joinRequests?.length) && (
                                        <div className="rounded-lg border border-neutral-800 bg-black/40 p-4">
                                            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200/80">Join requests</p>
                                            <div className="mt-3 space-y-2">
                                                {(groupDetails?.joinRequests || []).map(request => {
                                                    const label = request.user.displayName || request.user.username || "Unknown";
                                                    return (
                                                        <div key={request.id} className="flex items-center gap-2 rounded-md border border-neutral-900 bg-neutral-950/75 p-2">
                                                            <img
                                                                src={request.user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=111&color=67e8f9`}
                                                                alt={label}
                                                                className="h-9 w-9 rounded-md object-cover"
                                                            />
                                                            <div className="min-w-0 flex-1">
                                                                <p className="truncate text-sm font-semibold text-white">{label}</p>
                                                                <p className="truncate text-xs text-neutral-500">@{request.user.username}</p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleReviewJoinRequest(request.user, "approve")}
                                                                className="inline-flex h-8 items-center rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2 text-xs font-black text-emerald-200"
                                                            >
                                                                Accept
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleReviewJoinRequest(request.user, "decline")}
                                                                className="inline-flex h-8 items-center rounded-md border border-neutral-700 bg-neutral-900 px-2 text-xs font-black text-neutral-300"
                                                            >
                                                                Decline
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {isGroupManager && Boolean(groupDetails?.bans?.length) && (
                                        <div className="rounded-lg border border-neutral-800 bg-black/40 p-4">
                                            <div className="mb-3 flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-rose-200/80">Banned users</p>
                                                    <p className="mt-1 text-xs text-neutral-500">People blocked from joining or being invited back.</p>
                                                </div>
                                                <Ban size={16} className="text-rose-300" />
                                            </div>
                                            <div className="space-y-2">
                                                {(groupDetails?.bans || []).map(ban => {
                                                    const bannedId = String(ban.user);
                                                    const shortId = bannedId.length > 10 ? `${bannedId.slice(0, 6)}...${bannedId.slice(-4)}` : bannedId;
                                                    return (
                                                        <div key={bannedId} className="flex items-center gap-3 rounded-md border border-neutral-900 bg-neutral-950/75 p-2">
                                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-rose-300/15 bg-rose-500/10 text-xs font-black text-rose-100">
                                                                <Ban size={15} />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="truncate text-sm font-semibold text-white">User {shortId}</p>
                                                                <p className="truncate text-xs text-neutral-500">
                                                                    {ban.reason || "No reason added"}
                                                                    {ban.bannedAt ? ` · ${formatBlackspaceFullDateTime(ban.bannedAt)}` : ""}
                                                                </p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUnbanGroupUser(bannedId)}
                                                                disabled={groupAction === `unban-${bannedId}`}
                                                                className="inline-flex h-8 items-center gap-1 rounded-md border border-neutral-700 bg-neutral-900 px-2.5 text-xs font-bold text-neutral-200 transition hover:bg-neutral-800 disabled:opacity-60"
                                                            >
                                                                {groupAction === `unban-${bannedId}` ? <Loader size={13} className="animate-spin" /> : <Unlock size={13} />}
                                                                Unban
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {isPresenceAuthority && (
                                        <div className="rounded-lg border border-fuchsia-400/20 bg-fuchsia-950/10 p-4">
                                            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-fuchsia-200/80">Moderation</p>
                                            <p className="mt-1 text-xs text-fuchsia-100/60">Lock or reopen messaging for this group.</p>
                                            <div className="mt-3 grid grid-cols-2 gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleAuthorityControl(true, "locked")}
                                                    disabled={groupAction === "authority"}
                                                    className="inline-flex h-9 items-center justify-center rounded-md border border-fuchsia-300/30 bg-fuchsia-400/10 px-2 text-xs font-black text-fuchsia-100"
                                                >
                                                    Lock
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleAuthorityControl(false, "everyone")}
                                                    disabled={groupAction === "authority"}
                                                    className="inline-flex h-9 items-center justify-center rounded-md border border-neutral-700 bg-neutral-900 px-2 text-xs font-black text-neutral-200"
                                                >
                                                    Release
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {selectedChat.hasLeftGroup ? (
                                        <div className="rounded-lg border border-neutral-800 bg-neutral-950/70 p-4">
                                            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-neutral-300/80">Left group</p>
                                            <p className="mt-1 text-xs text-neutral-500">
                                                This history remains in Messages. You will not receive new group messages unless you are added again.
                                            </p>
                                        </div>
                                    ) : isPresenceAuthority ? (
                                        <div className="rounded-lg border border-rose-500/20 bg-rose-950/10 p-4">
                                            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-rose-200/80">Delete group</p>
                                            <p className="mt-1 text-xs text-rose-100/60">
                                                Remove this group without requiring an owner transfer.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => { if (selectedChat) { setChatToDelete(selectedChat); setShowDeleteModal(true); } }}
                                                className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-rose-300/30 bg-rose-400/10 px-3 text-sm font-black text-rose-200 transition hover:bg-rose-400/15"
                                            >
                                                <Trash2 size={15} />
                                                Delete group
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="rounded-lg border border-amber-500/20 bg-amber-950/10 p-4">
                                            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200/80">Exit</p>
                                            <p className="bs-group-leave-guidance mt-1 text-xs text-amber-100/60">
                                                Transfer the Ownership before exiting.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleLeaveGroup}
                                                disabled={groupAction === "leave"}
                                                className="mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border border-amber-300/30 bg-amber-400/10 px-3 text-sm font-black text-amber-200 transition hover:bg-amber-400/15 disabled:opacity-60"
                                            >
                                                {groupAction === "leave" ? <Loader size={15} className="animate-spin" /> : <LogOut size={15} />}
                                                Leave group
                                            </button>
                                        </div>
                                    )}
                                </section>

                                <section className="bs-group-members-panel rounded-lg border border-neutral-800 bg-black/40">
                                    <div className="border-b border-neutral-900 px-4 py-3">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-[10px] font-black tracking-[0.18em] text-cyan-300/80">Members</p>
                                            </div>
                                            <div className="flex shrink-0 items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setGroupMemberListSearchOpen(prev => !prev)}
                                                    className="bs-group-member-search-toggle inline-flex h-8 w-8 items-center justify-center rounded-md border border-neutral-800 bg-neutral-950 text-neutral-300 transition hover:border-cyan-400/35 hover:text-cyan-100"
                                                    title="Search members"
                                                    aria-label="Search members"
                                                >
                                                    <Search size={15} />
                                                </button>
                                                <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-neutral-800 bg-neutral-950 px-2 text-xs font-black text-white">
                                                    {groupDetails?.memberCountHidden ? "Hidden" : visibleGroupMemberCount || 0}
                                                </span>
                                            </div>
                                        </div>
                                        {groupMemberListSearchOpen && (
                                            <div className="bs-group-member-search mt-3 flex items-center gap-2 rounded-md border border-neutral-800 bg-black/55 px-3 py-2">
                                                <Search size={14} className="shrink-0 text-cyan-200/80" />
                                                <input
                                                    value={groupMemberListSearch}
                                                    onChange={(event) => setGroupMemberListSearch(event.target.value)}
                                                    className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-white outline-none placeholder:text-neutral-600"
                                                    placeholder="Search members..."
                                                />
                                                {groupMemberListSearch && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setGroupMemberListSearch("")}
                                                        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-neutral-500 transition hover:text-white"
                                                        title="Clear search"
                                                        aria-label="Clear member search"
                                                    >
                                                        <X size={13} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="hidden-scrollbar max-h-[32rem] space-y-2 overflow-y-auto p-3">
                                        {filteredGroupMembers.length === 0 && (
                                            <div className="bs-group-member-empty rounded-lg border border-neutral-900 bg-neutral-950/55 px-4 py-8 text-center text-xs text-neutral-500">
                                                No members match this search.
                                            </div>
                                        )}
                                        {filteredGroupMembers.map(member => {
                                            const label = member.displayName || member.username;
                                            const self = String(member.id) === String(currentUser?.id);
                                            const isOwnerMember = member.role === "owner";
                                            const isAdminMember = member.role === "admin";
                                            const restriction = groupDetails?.restrictions?.find(item => String(item.user) === String(member.id));
                                            const muted = Boolean(restriction?.muted || restriction?.restricted);
                                            return (
                                                <div key={member.id} className="rounded-lg border border-neutral-900 bg-neutral-950/75 p-3">
                                                    <div className="flex items-start gap-3">
                                                        <img
                                                            src={member.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(label)}&background=111&color=67e8f9`}
                                                            alt={label}
                                                            className="h-11 w-11 rounded-md object-cover ring-1 ring-white/10"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <p className="truncate text-sm font-black text-white">{label}</p>
                                                                {self && <span className="rounded-md border border-neutral-700 px-1.5 py-0.5 text-[10px] font-bold text-neutral-400">You</span>}
                                                            </div>
                                                            <p className="truncate text-xs text-neutral-500">@{member.username}</p>
                                                        </div>
                                                        <span
                                                            className={`bs-group-member-role inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-[11px] font-black ${groupRoleClass(member.role)}`}
                                                            title={groupRoleLabel(member.role)}
                                                            aria-label={groupRoleLabel(member.role)}
                                                        >
                                                            {groupRoleIcon(member.role)}
                                                        </span>
                                                    </div>

                                                    {(isGroupOwner || (isGroupManager && !isOwnerMember)) && (
                                                        <div className="bs-group-member-actions mt-3 flex flex-wrap gap-2 pl-14">
                                                            {isGroupOwner && !isOwnerMember && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleGroupRoleChange(member, isAdminMember ? "member" : "admin")}
                                                                        disabled={groupAction === `role-${member.id}`}
                                                                        className="bs-group-member-action is-admin inline-flex h-8 w-8 items-center justify-center rounded-md border border-cyan-400/25 bg-cyan-400/10 text-xs font-bold text-cyan-200 transition hover:bg-cyan-400/15 disabled:opacity-60"
                                                                        title={isAdminMember ? "Make member" : "Make admin"}
                                                                        aria-label={isAdminMember ? "Make member" : "Make admin"}
                                                                        data-label={isAdminMember ? "Make member" : "Make admin"}
                                                                    >
                                                                        {groupAction === `role-${member.id}` ? <Loader size={13} className="animate-spin" /> : <UserCog size={13} />}
                                                                    </button>
                                                                    {isNormalGroupOwner && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleTransferGroupOwner(member)}
                                                                            disabled={groupAction === `owner-${member.id}`}
                                                                            className="bs-group-member-action is-owner inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-300/25 bg-amber-400/10 text-xs font-bold text-amber-200 transition hover:bg-amber-400/15 disabled:opacity-60"
                                                                            title="Make owner"
                                                                            aria-label="Make owner"
                                                                            data-label="Make owner"
                                                                        >
                                                                            {groupAction === `owner-${member.id}` ? <Loader size={13} className="animate-spin" /> : <Crown size={13} />}
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                            {isGroupManager && !isOwnerMember && !self && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleToggleRestriction(member, !muted)}
                                                                        disabled={groupAction === `mute-${member.id}`}
                                                                        className="bs-group-member-action is-mute inline-flex h-8 w-8 items-center justify-center rounded-md border border-amber-400/25 bg-amber-500/10 text-xs font-bold text-amber-200 transition hover:bg-amber-500/15 disabled:opacity-60"
                                                                        title={muted ? "Unmute" : "Mute"}
                                                                        aria-label={muted ? "Unmute" : "Mute"}
                                                                        data-label={muted ? "Unmute" : "Mute"}
                                                                    >
                                                                        {groupAction === `mute-${member.id}` ? <Loader size={13} className="animate-spin" /> : <BellOff size={13} />}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveGroupMember(member)}
                                                                        disabled={groupAction === `remove-${member.id}`}
                                                                        className="bs-group-member-action is-remove inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-400/25 bg-rose-500/10 text-xs font-bold text-rose-200 transition hover:bg-rose-500/15 disabled:opacity-60"
                                                                        title="Remove"
                                                                        aria-label="Remove"
                                                                        data-label="Remove"
                                                                    >
                                                                        {groupAction === `remove-${member.id}` ? <Loader size={13} className="animate-spin" /> : <UserMinus size={13} />}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleBanGroupMember(member)}
                                                                        disabled={groupAction === `ban-${member.id}`}
                                                                        className="bs-group-member-action is-ban inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-400/25 bg-red-500/10 text-xs font-bold text-red-200 transition hover:bg-red-500/15 disabled:opacity-60"
                                                                        title="Ban"
                                                                        aria-label="Ban"
                                                                        data-label="Ban"
                                                                    >
                                                                        {groupAction === `ban-${member.id}` ? <Loader size={13} className="animate-spin" /> : <Ban size={13} />}
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Oblivion Confirmation Modal */}
            {showOblivionModal && chatToOblivion && (!chatToOblivion.isPresenceConversation || currentUserIsPresence) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm">
                    <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-fuchsia-300/20 bg-neutral-950 shadow-2xl shadow-fuchsia-950/30">
                        <div className="px-6 py-5">
                            <div className="mb-4 flex items-center gap-3">
                                <div className="rounded-full border border-fuchsia-300/25 bg-fuchsia-500/10 p-2">
                                    <Skull size={20} className="text-fuchsia-200" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-white">Oblivion</h2>
                                    <p className="text-xs font-semibold text-fuchsia-100/55">Permanent conversation erasure</p>
                                </div>
                            </div>
                            <p className="mb-2 text-sm text-neutral-200">
                                Erase <span className="font-semibold text-white">{chatToOblivion.name}</span>?
                            </p>
                            <p className="mb-5 text-xs leading-5 text-neutral-500">
                                {currentUserIsPresence && chatToOblivion.isPresenceConversation
                                    ? "This will permanently remove this conversation from the other user's side."
                                    : "This will permanently delete this conversation for both sides. This cannot be undone."}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowOblivionModal(false); setChatToOblivion(null); }}
                                    className="flex-1 rounded-lg bg-neutral-900 px-4 py-2 text-sm text-neutral-300 transition hover:bg-neutral-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => void handleOblivionChat()}
                                    className="flex-1 rounded-lg bg-fuchsia-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-fuchsia-600"
                                >
                                    Erase
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Block Confirmation Modal */}
            {showBlockModal && chatToBlock && (!chatToBlock.isPresenceConversation || currentUserIsPresence) && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="bg-neutral-900 border border-neutral-700 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                        <div className="px-6 py-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-red-900/50 rounded-full">
                                    <Ban size={20} className="text-red-400" />
                                </div>
                                <h2 className="text-lg font-bold text-white">Block User</h2>
                            </div>
                            <p className="text-sm text-neutral-300 mb-1">
                                Are you sure you want to block <span className="font-semibold text-white">{chatToBlock.name}</span>?
                            </p>
                            <p className="text-xs text-neutral-500 mb-5">You won't be able to send or receive messages from this user until you unblock them.</p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowBlockModal(false); setChatToBlock(null); }}
                                    className="flex-1 px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-sm text-neutral-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { handleBlockUser(chatToBlock); setShowBlockModal(false); setChatToBlock(null); }}
                                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-sm text-white font-semibold transition"
                                >
                                    Block
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && chatToDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="bg-neutral-900 border border-neutral-700 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                        <div className="px-6 py-5">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-red-900/50 rounded-full">
                                    <Trash2 size={20} className="text-red-400" />
                                </div>
                                <h2 className="text-lg font-bold text-white">{chatToDelete.isGroup ? "Delete Group" : "Delete Permanently"}</h2>
                            </div>
                            <p className="text-sm text-neutral-300 mb-1">
                                {chatToDelete.isGroup ? "Are you sure you want to delete " : "Permanently delete your conversation with "}
                                <span className="font-semibold text-white">{chatToDelete.name}</span>?
                            </p>
                            <p className="text-xs text-neutral-500 mb-5">
                                {chatToDelete.isGroup ? "This removes the group for every member and cannot be undone." : "This removes it from Recycle Bin and cannot be undone."}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => { setShowDeleteModal(false); setChatToDelete(null); }}
                                    className="flex-1 px-4 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-sm text-neutral-300 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteChat}
                                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-sm text-white font-semibold transition"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* New Chat Modal */}
            {showNewChatModal && (
                <div
                    className="bs-new-chat-overlay fixed inset-0 z-[2200] flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm"
                    onClick={closeNewChatModal}
                >
                    <div
                        className="bs-new-chat-modal hidden-scrollbar w-full max-w-xl overflow-hidden rounded-lg border border-cyan-900/50 bg-gradient-to-br from-neutral-950/72 via-black/68 to-cyan-950/20 shadow-[0_0_90px_rgba(8,145,178,0.20)] backdrop-blur-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="bs-new-chat-head border-b border-neutral-800 bg-black/30 px-5 py-4 backdrop-blur">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <span className="bs-new-chat-head-icon inline-flex h-10 w-10 items-center justify-center rounded-md border border-cyan-400/20 bg-cyan-400/10 text-cyan-300">
                                        {newChatMode === "group" ? <UsersRound size={18} /> : <MessageCircle size={18} />}
                                    </span>
                                    <div>
                                        <h2 className="text-lg font-bold text-white">New conversation</h2>
                                        <p className="text-xs text-neutral-500">Start a direct chat or gather a small group.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeNewChatModal}
                                    className="bs-new-chat-close inline-flex h-9 w-9 items-center justify-center rounded-md border border-neutral-800 text-neutral-400 transition hover:bg-neutral-900 hover:text-white"
                                    title="Close"
                                >
                                    <X size={17} />
                                </button>
                            </div>
                        </div>

                        <div className="bs-new-chat-controls border-b border-neutral-900 px-5 py-4">
                            <div className="bs-new-chat-mode-tabs mb-3 grid grid-cols-2 gap-2 rounded-md border border-neutral-800 bg-black/35 p-1">
                                {([
                                    ["direct", "Direct"],
                                    ["group", "Group"],
                                ] as const).map(([mode, label]) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => {
                                            setNewChatMode(mode);
                                            setNewGroupError("");
                                        }}
                                        className={`bs-new-chat-mode-tab rounded px-3 py-2 text-xs font-black transition ${
                                            newChatMode === mode
                                                ? "bg-cyan-400 text-black shadow-[0_0_18px_rgba(34,211,238,0.18)]"
                                                : "text-neutral-400 hover:bg-neutral-900 hover:text-neutral-100"
                                        }`}
                                    >
                                        {mode === "group" ? <UsersRound size={14} /> : <MessageCircle size={14} />}
                                        <span>{label}</span>
                                    </button>
                                ))}
                            </div>
                            {newChatMode === "group" && (
                                <div className="bs-new-chat-group-fields mb-3 space-y-2">
                                    <div className="bs-new-chat-control-shell relative">
                                        <UsersRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-200/80" />
                                        <input
                                            type="text"
                                            value={newGroupName}
                                            onChange={(event) => setNewGroupName(event.target.value.slice(0, 60))}
                                            placeholder="Give the group a name"
                                            className="bs-new-chat-control-input min-w-0 flex-1 pl-9 pr-3 text-sm text-white outline-none placeholder:text-neutral-500"
                                        />
                                    </div>
                                    {newGroupMembers.length > 0 && (
                                        <div className="bs-new-chat-chip-row hidden-scrollbar flex gap-2 overflow-x-auto pb-1">
                                            {newGroupMembers.map((member) => (
                                                <button
                                                    key={member.id}
                                                    type="button"
                                                    onClick={() => removeGroupMember(member.id)}
                                                    className="bs-new-chat-chip inline-flex shrink-0 items-center gap-2 rounded-md border border-cyan-400/25 bg-cyan-400/10 px-2 py-1 text-xs font-bold text-cyan-100"
                                                    title="Remove"
                                                >
                                                    @{member.username}
                                                    <X size={12} />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="bs-new-chat-control-shell relative">
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                                <input
                                    type="text"
                                    value={newChatSearch}
                                    onChange={(e) => setNewChatSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        const searching = newChatSearch.trim().length >= 2;
                                        const count = searching ? newChatResults.length : newChatRecentPeople.length;
                                        if (e.key === "ArrowDown") {
                                            e.preventDefault();
                                            setNewChatActiveIndex(prev => count ? (prev + 1) % count : 0);
                                        } else if (e.key === "ArrowUp") {
                                            e.preventDefault();
                                            setNewChatActiveIndex(prev => count ? (prev - 1 + count) % count : 0);
                                        } else if (e.key === "Enter") {
                                            e.preventDefault();
                                            if (searching) {
                                                const user = newChatResults[Math.min(newChatActiveIndex, Math.max(newChatResults.length - 1, 0))];
                                                if (user) newChatMode === "group" ? addGroupMember(user) : handleStartChat(user);
                                            } else {
                                                const chat = newChatRecentPeople[Math.min(newChatActiveIndex, Math.max(newChatRecentPeople.length - 1, 0))];
                                                if (chat && newChatMode === "direct") openExistingChat(chat.id);
                                            }
                                        }
                                    }}
                                    placeholder={newChatMode === "group" ? "Search people by name or username" : "Search username or name"}
                                    className="bs-new-chat-control-input min-w-0 flex-1 pl-10 pr-4 text-sm text-white outline-none placeholder:text-neutral-500"
                                />
                            </div>
                            {newGroupError && <p className="mt-2 text-xs font-semibold text-rose-300">{newGroupError}</p>}
                        </div>

                        <div className="bs-new-chat-results max-h-[28rem] overflow-y-auto p-3 custom-scrollbar">
                            {newChatLoading && (
                                <div className="space-y-2">
                                    {[0, 1, 2].map(item => (
                                        <div key={item} className="bs-new-chat-result-row flex items-center gap-3 rounded-lg border border-neutral-900 bg-black/30 px-3 py-3">
                                            <div className="h-11 w-11 animate-pulse rounded-md bg-neutral-800" />
                                            <div className="min-w-0 flex-1 space-y-2">
                                                <div className="h-3 w-36 animate-pulse rounded bg-neutral-800" />
                                                <div className="h-3 w-24 animate-pulse rounded bg-neutral-900" />
                                            </div>
                                            <div className="h-8 w-20 animate-pulse rounded-md bg-neutral-800" />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!newChatLoading && newChatSearch.trim().length < 2 && (
                                <div>
                                    {newChatMode === "direct" && newChatRecentPeople.length > 0 ? (
                                        <>
                                            <div className="mb-2 flex items-center justify-between px-1">
                                                <p className="text-xs font-semibold text-neutral-300">Recent people</p>
                                                <p className="text-[11px] text-neutral-600">Press Enter to open highlighted</p>
                                            </div>
                                            <div className="space-y-2">
                                                {newChatRecentPeople.map((chat, index) => {
                                                    const last = chat.messages[chat.messages.length - 1];
                                                    const active = index === newChatActiveIndex;
                                                    return (
                                                        <button
                                                            key={chat.id}
                                                            type="button"
                                                            onClick={() => openExistingChat(chat.id)}
                                                            className={`bs-new-chat-result-row flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition ${
                                                                active
                                                                    ? "border-cyan-400/45 bg-cyan-400/10"
                                                                    : "border-neutral-900 bg-black/25 hover:border-neutral-800 hover:bg-neutral-900/70"
                                                            }`}
                                                        >
                                                            <img src={chat.avatar} alt={chat.name} className="bs-new-chat-result-avatar h-11 w-11 rounded-md object-cover ring-1 ring-white/10" />
                                                            <div className="min-w-0 flex-1">
                                                                <p className="truncate text-sm font-semibold text-white">{chat.name}</p>
                                                                <p className="truncate text-xs text-neutral-500">{last ? last.text : "Open conversation"}</p>
                                                            </div>
                                                            <span className="bs-new-chat-result-action inline-flex h-8 items-center justify-center rounded-md border border-cyan-400/25 px-3 text-xs font-semibold text-cyan-200">
                                                                Open
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="bs-new-chat-empty px-5 py-9 text-center">
                                            <span className="bs-new-chat-empty-icon mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-md border border-neutral-800 bg-black text-cyan-300">
                                                <Search size={22} />
                                            </span>
                                            <p className="text-sm font-medium text-neutral-300">{newChatMode === "group" ? "Search people to add" : "Search by username or name"}</p>
                                            <p className="mt-1 text-xs text-neutral-600">{newChatMode === "group" ? "Pick at least two members, then create the group." : "Type at least 2 characters to find people."}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!newChatLoading && newChatSearch.trim().length >= 2 && newChatResults.length === 0 && (
                                <div className="bs-new-chat-empty px-5 py-9 text-center">
                                    <span className="bs-new-chat-empty-icon mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-md border border-neutral-800 bg-black text-neutral-500">
                                        <Search size={22} />
                                    </span>
                                    <p className="text-sm font-medium text-neutral-300">No matching people</p>
                                    <p className="mt-1 text-xs text-neutral-600">No username or name matched "{newChatSearch.trim()}".</p>
                                </div>
                            )}

                            {!newChatLoading && newChatSearch.trim().length >= 2 && newChatResults.length > 0 && (
                                <div className="space-y-2">
                                    {newChatResults.map((user, index) => {
                                        const displayName = user.displayName || user.username;
                                        const avatar = user.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=222&color=fff`;
                                        const existingChat = findExistingChatForUser(user.id);
                                        const isCreating = newChatCreating === user.id;
                                        const active = index === newChatActiveIndex;
                                        const exactMatch = user.username.toLowerCase() === newChatSearch.trim().toLowerCase();

                                        return (
                                            <button
                                                key={user.id}
                                                type="button"
                                                onClick={() => !isCreating && (newChatMode === "group" ? addGroupMember(user) : handleStartChat(user))}
                                                className={`bs-new-chat-result-row flex w-full items-center gap-3 rounded-lg border px-3 py-3 text-left transition ${
                                                    active
                                                        ? "border-cyan-400/45 bg-cyan-400/10"
                                                        : "border-neutral-900 bg-black/25 hover:border-neutral-800 hover:bg-neutral-900/70"
                                                } ${isCreating ? 'pointer-events-none opacity-70' : ''}`}
                                            >
                                                <img
                                                    src={avatar}
                                                    alt={displayName}
                                                    className="bs-new-chat-result-avatar h-11 w-11 rounded-md object-cover ring-1 ring-white/10"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <p className="truncate text-sm font-semibold text-white">{displayName}</p>
                                                        {exactMatch && (
                                                            <span className="shrink-0 rounded-md border border-cyan-400/25 bg-cyan-400/10 px-1.5 py-0.5 text-[10px] font-semibold text-cyan-200">
                                                                Exact
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="truncate text-xs text-neutral-500">@{user.username}</p>
                                                    <p className="mt-1 truncate text-[11px] text-neutral-600">
                                                        {newChatMode === "group"
                                                            ? (newGroupMembers.some((member) => String(member.id) === String(user.id)) ? "Added to group" : "Add to group")
                                                            : existingChat ? "Conversation already exists" : "Available on Blackspace"}
                                                    </p>
                                                </div>
                                                <span className={`bs-new-chat-result-action inline-flex h-8 min-w-20 items-center justify-center rounded-md border px-3 text-xs font-semibold transition ${
                                                    existingChat
                                                        ? "border-cyan-400/25 text-cyan-200"
                                                        : "border-purple-400/30 bg-purple-600 text-white"
                                                }`}>
                                                    {newChatMode === "group" ? (
                                                        newGroupMembers.some((member) => String(member.id) === String(user.id)) ? "Added" : "Add"
                                                    ) : isCreating ? (
                                                        <Loader size={14} className="animate-spin" />
                                                    ) : existingChat ? (
                                                        "Open"
                                                    ) : (
                                                        "Message"
                                                    )}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {newChatMode === "group" && (
                            <div className="bs-new-chat-footer flex flex-col gap-3 border-t border-neutral-900 px-5 py-4 sm:flex-row sm:items-center">
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs font-black uppercase tracking-[0.16em] text-neutral-500">Group ready</p>
                                    <p className="mt-1 truncate text-sm text-neutral-300">
                                        {newGroupMembers.length} selected {newGroupName.trim() ? `for ${newGroupName.trim()}` : ""}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCreateGroup}
                                    disabled={newGroupCreating || newGroupMembers.length < 2}
                                    className="bs-new-chat-create-button inline-flex items-center justify-center gap-2 rounded-md border border-cyan-300/35 bg-cyan-400 px-4 py-2 text-sm font-black text-black shadow-[0_0_22px_rgba(34,211,238,0.18)] transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {newGroupCreating ? <Loader size={15} className="animate-spin" /> : <UsersRound size={15} />}
                                    Create group
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {bulkDeleteChats.length > 0 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-neutral-700 bg-neutral-900 shadow-2xl mx-4">
                        <div className="px-6 py-5">
                            <div className="mb-4 flex items-center gap-3">
                                <div className="rounded-full bg-red-900/50 p-2">
                                    <Trash2 size={20} className="text-red-400" />
                                </div>
                                <h2 className="text-lg font-bold text-white">Delete Permanently</h2>
                            </div>
                            <p className="mb-1 text-sm text-neutral-300">
                                Permanently delete {bulkDeleteChats.length} selected {bulkDeleteChats.length === 1 ? "chat" : "chats"}?
                            </p>
                            <p className="mb-5 text-xs text-neutral-500">This removes them from Recycle Bin and cannot be undone.</p>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setBulkDeleteChats([])}
                                    className="flex-1 rounded-lg bg-neutral-800 px-4 py-2 text-sm text-neutral-300 transition hover:bg-neutral-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleBulkPermanentDelete()}
                                    className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Styles */}
            <style>{`
    .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
        background: #1f2937;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #4f46e5;
        border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #6366f1;
    }

    .no-scrollbar {
        scrollbar-width: none;
        -ms-overflow-style: none;
    }

    .no-scrollbar::-webkit-scrollbar {
        display: none;
    }

    .message-enter {
        opacity: 0;
        transform: translateY(10px);
    }
    .message-enter-active {
        opacity: 1;
        transform: translateY(0);
        transition: all 0.3s ease;
    }

    /* Prevent text selection for better UX */
    .no-select {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    /* ✅ NEW: Better hover effects */
    .chat-cell-hover:hover {
        background: #1f2937;
        transform: scale(1.02);
        transition: all 0.2s ease;
    }
    
    /* ✅ NEW: Smooth button animations */
    .btn-smooth {
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .btn-smooth:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }
    
    /* ✅ NEW: Message bubble shadows */
    .message-bubble {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
    
    .message-bubble:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }
    
    /* ✅ NEW: Online status pulse animation */
    @keyframes pulse-online {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
    }
    
    .status-online {
        animation: pulse-online 2s infinite;
    }
    
    /* ✅ NEW: Typing indicator animation */
    @keyframes typing-bounce {
        0%, 80%, 100% { transform: scale(0.8); }
        40% { transform: scale(1.2); }
    }
    
    .typing-dot {
        animation: typing-bounce 1.4s infinite ease-in-out;
    }
    
    .typing-dot:nth-child(1) { animation-delay: -0.32s; }
    .typing-dot:nth-child(2) { animation-delay: -0.16s; }
    
    /* ✅ NEW: Input focus glow */
    .input-glow:focus {
        box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.5);
    }
    
    /* ✅ NEW: Quick replies animation */
    @keyframes slideUp {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    
    .quick-reply-enter {
        animation: slideUp 0.3s ease-out;
    }
    
    /* ✅ NEW: Mobile responsive improvements */
    @media (max-width: 768px) {
        .sidebar-mobile {
            width: 100% !important;
        }
        
        .resizer-mobile {
            display: none;
        }
    }
    
    /* ✅ NEW: Dark theme enhancements */
    .bg-deep-dark {
        background: #0a0a0a;
    }
    
    .border-deep-dark {
        border-color: #262626;
    }
    
    /* ✅ NEW: Smooth transitions for all interactive elements */
    * {
        transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
    }

    /* ✅ Incognito vanish animation — messages float away like souls */
    @keyframes incognitoVanish {
        0% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        40% { opacity: 0.6; transform: translateY(-20px) scale(0.95); filter: blur(1px); }
        100% { opacity: 0; transform: translateY(-60px) scale(0.8); filter: blur(4px); }
    }

    .incognito-vanish {
        animation: incognitoVanish 0.8s ease-out forwards;
        pointer-events: none;
    }

    /* ✅ Screenshot deterrence for incognito chats */
    @media print {
        .incognito-protected {
            filter: blur(20px) !important;
        }
        .incognito-protected::after {
            content: "Incognito content hidden";
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            color: #999;
        }
    }
`}</style>
        </div>
    );
}
